import React, { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { API_BASE_URL } from "../config/api";
import { appendAuthToken } from "../utils/auth";

const GENERAL_CATEGORIES = [
  "Food",
  "Travel",
  "Petrol",
  "Hotel",
  "Parking",
  "Local transportation",
  "Stationery",
  "Other general expenses"
];

const PROJECT_CATEGORIES = [
  "Food",
  "Travel",
  "Petrol",
  "Hotel",
  "Material Purchase",
  "Local transportation",
  "Other project expense"
];

const COMPLAINT_CATEGORIES = [
  "Travel",
  "Petrol",
  "Spare / Material",
  "Food",
  "Hotel",
  "Other service expense"
];

const ABB_WARRANTY_CATEGORIES = [
  "Travel",
  "Petrol",
  "Spare / Material",
  "Food",
  "Hotel",
  "Local transportation",
  "Other service expense"
];

const isAbbWarrantyComplaint = (cmp) => {
  if (!cmp) return false;
  const pType = String(cmp.productType || "").trim().toLowerCase();
  const advice = String(cmp.siteAdvice || "").trim().toLowerCase();
  const issue = String(cmp.issue || "").trim().toLowerCase();
  return (
    pType.includes("abb") ||
    pType.includes("warranty") ||
    advice.includes("abb warranty") ||
    issue.includes("abb warranty")
  );
};

const todayDateString = () => new Date().toISOString().slice(0, 10);

const formatCurrency = (val) => {
  const parsed = Number(val);
  const num = Number.isFinite(parsed) ? parsed : 0;
  const sign = num < 0 ? "-" : "";
  return `₹${sign}${Math.abs(num).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })}`;
};

const formatDateDisplay = (dateVal) => {
  if (!dateVal) return "-";
  try {
    const d = new Date(dateVal);
    if (isNaN(d.getTime())) return dateVal;
    return d.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric"
    });
  } catch {
    return dateVal;
  }
};

const formatDateTimeDisplay = (dateVal) => {
  if (!dateVal) return "-";
  try {
    const d = new Date(dateVal);
    if (isNaN(d.getTime())) return dateVal;
    return d.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  } catch {
    return dateVal;
  }
};

export default function VoucherSimpleManager() {
  // 1. Role & User Context
  const userRole = useMemo(() => {
    if (typeof window !== "undefined" && window.location.pathname.startsWith("/admin")) {
      return "admin";
    }
    const stored =
      localStorage.getItem("role") ||
      localStorage.getItem("userRole") ||
      (localStorage.getItem("adminToken") ? "admin" : "") ||
      (localStorage.getItem("accountsToken") ? "accounts" : "") ||
      (localStorage.getItem("engineerToken") ? "engineer" : "") ||
      "engineer";
    return stored.toLowerCase();
  }, []);

  const currentUserName = useMemo(() => {
    if (userRole === "admin") return localStorage.getItem("adminName") || "Admin";
    if (userRole === "accounts") return localStorage.getItem("accountsName") || "Accounts";
    return localStorage.getItem("engineerName") || "Engineer";
  }, [userRole]);

  const currentUserId = useMemo(() => {
    return (
      localStorage.getItem("userId") ||
      localStorage.getItem("engineerId") ||
      localStorage.getItem("accountId") ||
      ""
    );
  }, []);

  const canManageStatus = useMemo(() => {
    return userRole === "admin" || userRole === "accounts";
  }, [userRole]);

  // 2. Main Navigation Tabs
  // 'entry' | 'expenses' | 'projects' | 'reports'
  const [activeMainTab, setActiveMainTab] = useState(
    (typeof window !== "undefined" && window.location.pathname.startsWith("/admin")) ||
    localStorage.getItem("role")?.toLowerCase() === "admin" ||
    localStorage.getItem("adminToken")
      ? "expenses"
      : "entry"
  );

  // 3. Two Main Entries Toggle: 'CREDIT' | 'DEBIT'
  const [entryType, setEntryType] = useState("CREDIT");

  // 4. Debit Category: 'general' | 'project' | 'complaint'
  const [debitCategory, setDebitCategory] = useState("general");

  // 5. Balance Summary State
  const [balanceSummary, setBalanceSummary] = useState({
    totalCredit: 0,
    totalExpense: 0,
    availableBalance: 0,
    totalGeneralExpense: 0,
    totalProjectExpense: 0,
    totalComplaintExpense: 0,
    totalAbbWarrantyExpense: 0,
    accountsBalance: 0
  });

  // 6. Master Data Lists
  const [engineers, setEngineers] = useState([]);
  const [accountsUsers, setAccountsUsers] = useState([]);
  const [complaints, setComplaints] = useState([]);
  const [projects, setProjects] = useState([]);

  // Filtered master lists for Complaint vs ABB Warranty Complaint
  const regularComplaints = useMemo(() => {
    return complaints.filter((c) => !isAbbWarrantyComplaint(c));
  }, [complaints]);

  const abbWarrantyComplaints = useMemo(() => {
    return complaints.filter((c) => isAbbWarrantyComplaint(c));
  }, [complaints]);

  // 7. Form States
  // Credit Form
  const [creditForm, setCreditForm] = useState({
    date: todayDateString(),
    amount: "",
    creditType: "self", // 'self' | 'transfer'
    receivedFrom: userRole === "admin" ? "Customer / Bank" : "Admin", // For Admin: source; For Engineer: 'Admin' | 'Accounts'; For Accounts: 'Admin'
    // Admin "Give Amount" fields
    givenTo: "engineer", // 'engineer' | 'accounts'
    recipientId: "",
    recipientName: "",
    description: "",
    attachment: null
  });

  // Debit Form (supports multiple entries/rows per debit voucher)
  const [debitForm, setDebitForm] = useState({
    date: todayDateString(),
    projectId: "",
    projectName: "",
    complaintId: "",
    complaintRefId: "",
    abbWarrantyRefId: "",
    abbWarrantyComplaintId: "",
    customerName: "",
    place: "",
    details: "",
    attachment: null,
    entries: [
      { id: "entry-1", expenseCategory: "Food", amount: "", description: "" }
    ]
  });

  // Helper to calculate total amount of all debit entries
  const debitTotalAmount = useMemo(() => {
    if (!debitForm.entries || debitForm.entries.length === 0) return 0;
    return debitForm.entries.reduce((sum, item) => {
      const val = parseFloat(item.amount);
      return sum + (isNaN(val) || val < 0 ? 0 : val);
    }, 0);
  }, [debitForm.entries]);

  // Helpers to add, remove, and update debit entries
  const addDebitEntry = () => {
    const defaultCat =
      debitCategory === "general"
        ? GENERAL_CATEGORIES[0]
        : debitCategory === "project"
        ? PROJECT_CATEGORIES[0]
        : debitCategory === "abb_warranty"
        ? ABB_WARRANTY_CATEGORIES[0]
        : COMPLAINT_CATEGORIES[0];

    setDebitForm((prev) => ({
      ...prev,
      entries: [
        ...prev.entries,
        {
          id: `entry-${Date.now()}-${Math.random().toString(36).substr(2, 4)}`,
          expenseCategory: defaultCat,
          amount: "",
          description: ""
        }
      ]
    }));
  };

  const removeDebitEntry = (indexToRemove) => {
    setDebitForm((prev) => {
      if (prev.entries.length <= 1) return prev; // keep at least one row
      return {
        ...prev,
        entries: prev.entries.filter((_, idx) => idx !== indexToRemove)
      };
    });
  };

  const updateDebitEntry = (index, field, value) => {
    setDebitForm((prev) => {
      const newEntries = [...prev.entries];
      newEntries[index] = {
        ...newEntries[index],
        [field]: value
      };
      return {
        ...prev,
        entries: newEntries
      };
    });
  };

  // UI status
  const [submitting, setSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [updatingStatusId, setUpdatingStatusId] = useState(null);

  // 8. Expense List State
  const [vouchers, setVouchers] = useState([]);
  const [listLoading, setListLoading] = useState(false);
  const [expenseTab, setExpenseTab] = useState("ALL"); // 'ALL' | 'ENGINEERS' | 'ACCOUNTS' | 'COMPLAINTS' | 'PROJECT' | 'GENERAL'
  const [selectedEngineerFilter, setSelectedEngineerFilter] = useState("ALL");
  const [selectedAccountFilter, setSelectedAccountFilter] = useState("ALL");
  const [sortBy, setSortBy] = useState("date_desc"); // 'date_desc' | 'date_asc' | 'engineers' | 'accounts' | 'complaints' | 'project' | 'amount_desc' | 'amount_asc'
  const [searchTerm, setSearchTerm] = useState("");
  const [quickDatePreset, setQuickDatePreset] = useState("all"); // 'today' | 'week' | 'month' | 'year' | 'custom' | 'all'
  const [dateFilter, setDateFilter] = useState({ from: "", to: "" });
  const [selectedComplaintFilter, setSelectedComplaintFilter] = useState("ALL");
  const [selectedAbbWarrantyFilter, setSelectedAbbWarrantyFilter] = useState("ALL");
  const [selectedProjectFilter, setSelectedProjectFilter] = useState("ALL");
  const [expenseViewMode, setExpenseViewMode] = useState("table"); // 'table' | 'grouped'
  const [expandedGroups, setExpandedGroups] = useState({});
  const [voucherLimit, setVoucherLimit] = useState(20);

  // Checkbox selection & bulk approve state
  const [selectedVoucherIds, setSelectedVoucherIds] = useState([]);
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportFilterState, setExportFilterState] = useState({
    scope: "all",
    status: "all",
    engineer: "ALL",
    referenceType: "ALL",
    complaintId: "",
    projectId: "",
    fromDate: "",
    toDate: "",
    sortBy: "date_desc"
  });
  const [bulkApproving, setBulkApproving] = useState(false);

  // 9. Modals State
  const [viewVoucher, setViewVoucher] = useState(null);
  const [editVoucher, setEditVoucher] = useState(null);
  const [editFormData, setEditFormData] = useState({
    date: "",
    amount: "",
    expenseCategory: "",
    receivedFrom: "",
    referenceType: "general",
    referenceId: "",
    referenceName: "",
    status: "pending_approval",
    description: "",
    attachment: null
  });
  const [editSubmitting, setEditSubmitting] = useState(false);

  // Void / Cancel Modal
  const [voidVoucher, setVoidVoucher] = useState(null);
  const [voidReason, setVoidReason] = useState("");
  const [voidSubmitting, setVoidSubmitting] = useState(false);

  // Project Creation Modal
  const [showProjectModal, setShowProjectModal] = useState(false);
  const [newProjectData, setNewProjectData] = useState({
    projectName: "",
    customerName: "",
    location: "",
    projectType: "panel_building",
    estimateDate: todayDateString(),
    notes: ""
  });
  const [projectSubmitting, setProjectSubmitting] = useState(false);

  // ABB Warranty Complaint Creation Modal
  const [showWarrantyModal, setShowWarrantyModal] = useState(false);
  const [newWarrantyData, setNewWarrantyData] = useState({
    customerName: "",
    place: "",
    details: "",
    visitDate: todayDateString(),
    contactPerson: "",
    contactPhone: ""
  });
  const [warrantySubmitting, setWarrantySubmitting] = useState(false);

  // Selected Project Summary View
  const [selectedProjectForSummary, setSelectedProjectForSummary] = useState(null);

  // 10. Reports State
  const [reportSubTab, setReportSubTab] = useState("dashboard"); // 'dashboard' | 'engineer' | 'project' | 'complaint' | 'abb_warranty' | 'general'
  const [selectedEngineerForReport, setSelectedEngineerForReport] = useState("");
  const [selectedProjectForReport, setSelectedProjectForReport] = useState("");
  const [selectedComplaintForReport, setSelectedComplaintForReport] = useState("");
  const [selectedAbbWarrantyForReport, setSelectedAbbWarrantyForReport] = useState("");
  const [generalReportFilter, setGeneralReportFilter] = useState({ category: "", user: "", from: "", to: "" });

  // Clear notifications
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(""), 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  useEffect(() => {
    if (errorMessage) {
      const timer = setTimeout(() => setErrorMessage(""), 6000);
      return () => clearTimeout(timer);
    }
  }, [errorMessage]);

  // For engineers, ensure tab and sort don't default to admin/accounts options
  useEffect(() => {
    if (userRole === "engineer") {
      if (expenseTab === "ENGINEERS" || expenseTab === "ACCOUNTS") {
        setExpenseTab("ALL");
      }
      if (sortBy === "engineers" || sortBy === "accounts") {
        setSortBy("date_desc");
      }
    }
  }, [userRole, expenseTab, sortBy]);

  // Auth Header helper
  const getAuthHeaders = useCallback(() => {
    const token =
      localStorage.getItem("token") ||
      localStorage.getItem("adminToken") ||
      localStorage.getItem("accountsToken") ||
      localStorage.getItem("engineerToken");
    return token ? { Authorization: `Bearer ${token}` } : {};
  }, []);

  // Fetch Master Data
  const fetchMasterData = useCallback(async () => {
    try {
      const [engRes, prjRes, cmpRes, accRes] = await Promise.allSettled([
        axios.get(`${API_BASE_URL}/api/engineers`, { headers: getAuthHeaders() }),
        axios.get(`${API_BASE_URL}/api/project-expenses`, { headers: getAuthHeaders() }),
        axios.get(`${API_BASE_URL}/api/complaints`, { headers: getAuthHeaders() }),
        axios.get(`${API_BASE_URL}/api/accounts`, { headers: getAuthHeaders() })
      ]);

      if (engRes.status === "fulfilled" && Array.isArray(engRes.value.data)) {
        setEngineers(engRes.value.data);
      }
      if (prjRes.status === "fulfilled" && Array.isArray(prjRes.value.data)) {
        setProjects(prjRes.value.data);
      }
      if (cmpRes.status === "fulfilled" && Array.isArray(cmpRes.value.data)) {
        setComplaints(cmpRes.value.data);
      }
      if (accRes.status === "fulfilled" && Array.isArray(accRes.value.data)) {
        setAccountsUsers(accRes.value.data);
      }
    } catch (err) {
      console.error("Error fetching master data:", err);
    }
  }, [getAuthHeaders]);

  // Fetch Dashboard & Balance Summary
  const fetchSummary = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/api/vouchers/dashboard-summary`, {
        headers: getAuthHeaders()
      });
      if (res.data?.success && res.data?.summary) {
        const s = res.data.summary;
        setBalanceSummary({
          totalCredit: s.totalCredit ?? s.totalAdvanceReceived ?? s.totalMoneyReceived ?? s.totalReceivedFromAdmin ?? 0,
          totalExpense: s.totalExpenses ?? s.totalExpense ?? s.totalApprovedExpense ?? 0,
          availableBalance: s.availableBalance ?? s.accountsAvailableBalance ?? s.totalBalance ?? 0,
          totalGeneralExpense: s.totalGeneralExpense ?? s.generalExpense ?? 0,
          totalProjectExpense: s.totalProjectExpense ?? s.projectExpense ?? 0,
          totalComplaintExpense: s.totalComplaintExpense ?? s.complaintExpense ?? 0,
          totalAbbWarrantyExpense: s.totalAbbWarrantyExpense ?? s.abbWarrantyExpense ?? 0,
          accountsBalance: s.accountsAvailableBalance ?? 0
        });
      }
    } catch (err) {
      console.error("Error fetching balance summary:", err);
    }
  }, [getAuthHeaders]);

  // Fetch Vouchers List
  const fetchVouchers = useCallback(async () => {
    try {
      setListLoading(true);
      const params = { limit: 1000 };
      if (userRole === "engineer" && currentUserId) {
        params.userId = currentUserId;
      }
      const res = await axios.get(`${API_BASE_URL}/api/vouchers/transactions`, {
        headers: getAuthHeaders(),
        params
      });
      const list = res.data?.transactions || (Array.isArray(res.data) ? res.data : []);
      setVouchers(list);
    } catch (err) {
      console.error("Error fetching vouchers:", err);
    } finally {
      setListLoading(false);
    }
  }, [getAuthHeaders, userRole, currentUserId]);

  useEffect(() => {
    fetchMasterData();
    fetchSummary();
    fetchVouchers();
  }, [fetchMasterData, fetchSummary, fetchVouchers]);

  // Reset category options when debit category switches
  useEffect(() => {
    if (debitCategory === "general") {
      setDebitForm((prev) => ({ ...prev, expenseCategory: GENERAL_CATEGORIES[0] }));
    } else if (debitCategory === "project") {
      setDebitForm((prev) => ({ ...prev, expenseCategory: PROJECT_CATEGORIES[0] }));
    } else if (debitCategory === "complaint") {
      setDebitForm((prev) => ({ ...prev, expenseCategory: COMPLAINT_CATEGORIES[0] }));
    } else if (debitCategory === "abb_warranty") {
      setDebitForm((prev) => ({ ...prev, expenseCategory: ABB_WARRANTY_CATEGORIES[0] }));
    }
  }, [debitCategory]);

  // Quick date presets handler
  const handleQuickDate = (preset) => {
    setQuickDatePreset(preset);
    const now = new Date();
    if (preset === "all") {
      setDateFilter({ from: "", to: "" });
    } else if (preset === "today") {
      const today = todayDateString();
      setDateFilter({ from: today, to: today });
    } else if (preset === "week") {
      const first = new Date(now.setDate(now.getDate() - now.getDay() + (now.getDay() === 0 ? -6 : 1)));
      const last = new Date(first);
      last.setDate(last.getDate() + 6);
      setDateFilter({
        from: first.toISOString().slice(0, 10),
        to: last.toISOString().slice(0, 10)
      });
    } else if (preset === "month") {
      const y = now.getFullYear();
      const m = now.getMonth();
      const first = new Date(y, m, 1).toISOString().slice(0, 10);
      const last = new Date(y, m + 1, 0).toISOString().slice(0, 10);
      setDateFilter({ from: first, to: last });
    } else if (preset === "year") {
      const y = now.getFullYear();
      setDateFilter({ from: `${y}-01-01`, to: `${y}-12-31` });
    }
  };

  // Reset pagination limit on filter changes
  useEffect(() => {
    setVoucherLimit(20);
  }, [
    expenseTab,
    selectedEngineerFilter,
    selectedAccountFilter,
    selectedComplaintFilter,
    selectedAbbWarrantyFilter,
    selectedProjectFilter,
    searchTerm,
    dateFilter,
    sortBy
  ]);

  // -------------------------------------------------------------
  // VOUCHER & STATEMENT PDF GENERATION HANDLERS
  // -------------------------------------------------------------
  const handleDownloadVoucherSlip = (voucherOrId) => {
    const id = typeof voucherOrId === "object" ? (voucherOrId.transactionId || voucherOrId._id) : voucherOrId;
    if (!id) return;
    const url = appendAuthToken(`${API_BASE_URL}/api/vouchers/${id}/pdf?download=1`);
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const handleDownloadSelectedPdf = () => {
    if (selectedVoucherIds.length === 0) return;
    const params = new URLSearchParams({
      download: "1",
      ids: selectedVoucherIds.join(",")
    });
    const url = appendAuthToken(`${API_BASE_URL}/api/vouchers/statement/pdf?${params.toString()}`);
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const handleOpenExportModal = () => {
    setExportFilterState({
      scope: selectedVoucherIds.length > 0 ? "selected" : "all",
      status: "all",
      engineer: selectedEngineerFilter !== "ALL" ? selectedEngineerFilter : (userRole === "engineer" ? currentUserId : "ALL"),
      referenceType: expenseTab !== "ALL" ? expenseTab : "ALL",
      complaintId: selectedComplaintFilter !== "ALL" ? selectedComplaintFilter : "",
      projectId: selectedProjectFilter !== "ALL" ? selectedProjectFilter : "",
      fromDate: dateFilter.from || "",
      toDate: dateFilter.to || "",
      sortBy: sortBy || "date_desc"
    });
    setShowExportModal(true);
  };

  const handleExecuteCustomExport = (customState = exportFilterState) => {
    const params = new URLSearchParams({ download: "1" });
    if (customState.scope === "selected" && selectedVoucherIds.length > 0) {
      params.set("ids", selectedVoucherIds.join(","));
    } else {
      if (customState.status && customState.status !== "all") {
        params.set("status", customState.status);
      }
      if (customState.engineer && customState.engineer !== "ALL") {
        params.set("engineer", customState.engineer);
      }
      if (customState.referenceType && customState.referenceType !== "ALL") {
        params.set("referenceType", customState.referenceType.toLowerCase());
      }
      if (customState.complaintId) {
        params.set("complaintId", customState.complaintId);
      }
      if (customState.projectId) {
        params.set("projectId", customState.projectId);
      }
      if (customState.fromDate) params.set("fromDate", customState.fromDate);
      if (customState.toDate) params.set("toDate", customState.toDate);
      if (customState.sortBy) params.set("sortBy", customState.sortBy);
    }
    const url = appendAuthToken(`${API_BASE_URL}/api/vouchers/statement/pdf?${params.toString()}`);
    window.open(url, "_blank", "noopener,noreferrer");
    setShowExportModal(false);
  };


  // -------------------------------------------------------------
  // CREDIT SUBMISSION (Money Received / Admin Give Amount)
  // -------------------------------------------------------------
  const handleCreditSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage("");
    setSuccessMessage("");

    const numAmount = parseFloat(creditForm.amount);
    if (!numAmount || numAmount <= 0) {
      setErrorMessage("Please enter a valid amount greater than ₹0.");
      return;
    }

    try {
      setSubmitting(true);
      const hasAttachment = Boolean(creditForm.attachment);
      const isTransfer = (userRole === "admin" || userRole === "accounts") && creditForm.creditType === "transfer";
      const recName = isTransfer
        ? creditForm.givenTo === "admin"
          ? "Admin"
          : creditForm.recipientName
        : "";

      if (isTransfer) {
        if (creditForm.givenTo === "engineer" && !creditForm.recipientId) {
          setErrorMessage("Please select an Engineer to transfer money to.");
          setSubmitting(false);
          return;
        }
        if (creditForm.givenTo === "accounts" && !creditForm.recipientId) {
          setErrorMessage("Please select an Accounts user to transfer money to.");
          setSubmitting(false);
          return;
        }
      }

      if (hasAttachment) {
        const postData = new FormData();
        postData.append("transactionType", isTransfer ? "TRANSFER" : "CREDIT");
        postData.append("date", creditForm.date);
        postData.append("amount", String(numAmount));

        if (isTransfer) {
          postData.append("creditType", "transfer");
          postData.append("givenTo", creditForm.givenTo);
          postData.append("recipientId", creditForm.givenTo === "admin" ? "" : creditForm.recipientId);
          postData.append("recipientName", recName);
          postData.append(
            "description",
            creditForm.description || `Funds transferred by ${userRole === "admin" ? "Admin" : "Accounts"} to ${recName}`
          );
        } else if (userRole === "admin") {
          const source = creditForm.receivedFrom || "Customer / Bank";
          postData.append("receivedFrom", source);
          postData.append(
            "description",
            creditForm.description || `Credit inflow received from ${source}`
          );
        } else {
          // Engineer or Accounts recording received money
          const recv = userRole === "accounts" ? "Admin" : creditForm.receivedFrom || "Admin";
          postData.append("receivedFrom", recv);
          postData.append(
            "description",
            creditForm.description || (userRole === "accounts" ? "Received funds from Admin" : "")
          );
        }

        postData.append("attachments", creditForm.attachment);

        await axios.post(`${API_BASE_URL}/api/vouchers/transactions`, postData, {
          headers: getAuthHeaders()
        });
      } else {
        const sourceDesc = isTransfer
          ? creditForm.description || `Funds transferred by ${userRole === "admin" ? "Admin" : "Accounts"} to ${recName}`
          : userRole === "admin"
          ? creditForm.description || `Credit inflow received from ${creditForm.receivedFrom || "Customer / Bank"}`
          : userRole === "accounts"
          ? creditForm.description || "Received funds from Admin"
          : creditForm.description || "";

        const payload = {
          transactionType: isTransfer ? "TRANSFER" : "CREDIT",
          date: creditForm.date,
          amount: numAmount,
          description: sourceDesc,
          receivedFrom: !isTransfer
            ? userRole === "admin"
              ? creditForm.receivedFrom || "Customer / Bank"
              : userRole === "accounts"
              ? "Admin"
              : creditForm.receivedFrom || "Admin"
            : undefined,
          ...(isTransfer
            ? {
                creditType: "transfer",
                givenTo: creditForm.givenTo,
                recipientId: creditForm.givenTo === "admin" ? "" : creditForm.recipientId,
                recipientName: recName
              }
            : {})
        };

        await axios.post(`${API_BASE_URL}/api/vouchers/transactions`, payload, {
          headers: {
            ...getAuthHeaders(),
            "Content-Type": "application/json"
          }
        });
      }

      const targetMsg = isTransfer
        ? `Transferred ₹${numAmount.toLocaleString("en-IN")} to ${recName || "Recipient"}. Record updated in both ledgers!`
        : `Credit entry of ₹${numAmount.toLocaleString("en-IN")} recorded successfully! Available balance updated.`;

      setSuccessMessage(targetMsg);

      // Reset
      setCreditForm({
        date: todayDateString(),
        amount: "",
        creditType: "self",
        receivedFrom: userRole === "admin" ? "Customer / Bank" : "Admin",
        givenTo: "engineer",
        recipientId: "",
        recipientName: "",
        description: "",
        attachment: null
      });

      const fileInput = document.getElementById("credit-file-input");
      if (fileInput) fileInput.value = "";

      fetchSummary();
      fetchVouchers();
    } catch (err) {
      console.error("Error creating credit:", err);
      setErrorMessage(
        err.response?.data?.error || err.response?.data?.message || "Failed to record credit transaction."
      );
    } finally {
      setSubmitting(false);
    }
  };

  // -------------------------------------------------------------
  // DEBIT SUBMISSION (General, Project, Complaint Expense) - MULTI-ENTRY
  // -------------------------------------------------------------
  const handleDebitSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage("");
    setSuccessMessage("");

    // Validate project or complaint requirements if applicable
    if (debitCategory === "project" && !debitForm.projectName) {
      setErrorMessage("Please select an existing project or click [+ Create Project].");
      return;
    }

    if (debitCategory === "complaint" && !debitForm.complaintRefId) {
      setErrorMessage("Please select a complaint from Complaint Management.");
      return;
    }

    if (debitCategory === "abb_warranty" && !debitForm.abbWarrantyRefId) {
      setErrorMessage("Please select or click [+ Create Complaint] to add an ABB Warranty Complaint.");
      return;
    }

    // Validate entries
    if (!debitForm.entries || debitForm.entries.length === 0) {
      setErrorMessage("Please add at least one expense entry row.");
      return;
    }

    let calculatedTotal = 0;
    const preparedEntries = [];
    const cmpCompany = debitCategory === "complaint"
      ? (debitForm.customerName || debitForm.complaintId || "")
      : debitCategory === "abb_warranty"
      ? (debitForm.customerName || "")
      : "";

    const activeRefId = debitCategory === "project"
      ? (debitForm.projectId || "")
      : debitCategory === "complaint"
      ? (debitForm.complaintRefId || "")
      : debitCategory === "abb_warranty"
      ? (debitForm.abbWarrantyRefId || "")
      : "";

    const activeRefName = debitCategory === "project"
      ? (debitForm.projectName || "")
      : (debitCategory === "complaint" || debitCategory === "abb_warranty")
      ? cmpCompany
      : "";

    for (let i = 0; i < debitForm.entries.length; i++) {
      const row = debitForm.entries[i];
      const numAmt = parseFloat(row.amount);
      if (!numAmt || isNaN(numAmt) || numAmt <= 0) {
        setErrorMessage(`Entry row #${i + 1}: Please enter a valid amount greater than ₹0.`);
        return;
      }
      calculatedTotal += numAmt;

      preparedEntries.push({
        date: debitForm.date,
        amount: numAmt,
        expenseCategory: row.expenseCategory || (
          debitCategory === "general"
            ? GENERAL_CATEGORIES[0]
            : debitCategory === "project"
            ? PROJECT_CATEGORIES[0]
            : debitCategory === "abb_warranty"
            ? ABB_WARRANTY_CATEGORIES[0]
            : COMPLAINT_CATEGORIES[0]
        ),
        description: row.description || "",
        referenceType: debitCategory,
        referenceId: activeRefId,
        referenceName: activeRefName,
        customerName: (debitCategory === "complaint" || debitCategory === "abb_warranty") ? cmpCompany : ""
      });
    }

    // Negative balance is allowed - proceed directly with submission

    try {
      setSubmitting(true);
      const hasAttachment = Boolean(debitForm.attachment);

      if (hasAttachment) {
        const postData = new FormData();
        postData.append("transactionType", "DEBIT");
        postData.append("date", debitForm.date);
        postData.append("amount", String(calculatedTotal));
        postData.append("referenceType", debitCategory);
        postData.append("entries", JSON.stringify(preparedEntries));

        if (debitCategory === "project") {
          postData.append("referenceId", debitForm.projectId || "");
          postData.append("referenceName", debitForm.projectName || "");
        } else if (debitCategory === "complaint") {
          postData.append("referenceId", debitForm.complaintRefId || "");
          postData.append("referenceName", cmpCompany);
          postData.append("customerName", cmpCompany);
        } else if (debitCategory === "abb_warranty") {
          postData.append("referenceId", debitForm.abbWarrantyRefId || "");
          postData.append("referenceName", cmpCompany);
          postData.append("customerName", cmpCompany);
        }

        postData.append("attachments", debitForm.attachment);

        await axios.post(`${API_BASE_URL}/api/vouchers/transactions`, postData, {
          headers: getAuthHeaders()
        });
      } else {
        const payload = {
          transactionType: "DEBIT",
          date: debitForm.date,
          amount: calculatedTotal,
          referenceType: debitCategory,
          referenceId: activeRefId,
          referenceName: activeRefName,
          customerName: (debitCategory === "complaint" || debitCategory === "abb_warranty") ? cmpCompany : "",
          entries: preparedEntries
        };

        await axios.post(`${API_BASE_URL}/api/vouchers/transactions`, payload, {
          headers: {
            ...getAuthHeaders(),
            "Content-Type": "application/json"
          }
        });
      }

      setSuccessMessage(
        `Successfully recorded ${preparedEntries.length} debit ${preparedEntries.length === 1 ? "entry" : "entries"} totalling ₹${calculatedTotal.toLocaleString("en-IN")} (${debitCategory === "abb_warranty" ? "ABB WARRENTY COMPLAINT" : debitCategory.toUpperCase() + " EXPENSE"}). Balance updated.`
      );

      // Reset
      const defaultCategory =
        debitCategory === "general"
          ? GENERAL_CATEGORIES[0]
          : debitCategory === "project"
          ? PROJECT_CATEGORIES[0]
          : debitCategory === "abb_warranty"
          ? ABB_WARRANTY_CATEGORIES[0]
          : COMPLAINT_CATEGORIES[0];

      setDebitForm({
        date: todayDateString(),
        projectId: "",
        projectName: "",
        complaintId: "",
        complaintRefId: "",
        abbWarrantyRefId: "",
        abbWarrantyComplaintId: "",
        customerName: "",
        place: "",
        details: "",
        attachment: null,
        entries: [
          { id: `entry-${Date.now()}`, expenseCategory: defaultCategory, amount: "", description: "" }
        ]
      });

      const fileInput = document.getElementById("debit-file-input");
      if (fileInput) fileInput.value = "";

      fetchSummary();
      fetchVouchers();
    } catch (err) {
      console.error("Error creating debit:", err);
      setErrorMessage(
        err.response?.data?.error || err.response?.data?.message || "Failed to save debit voucher."
      );
    } finally {
      setSubmitting(false);
    }
  };

  // -------------------------------------------------------------
  // QUICK CREATE PROJECT
  // -------------------------------------------------------------
  const handleQuickCreateProject = async (e) => {
    e.preventDefault();
    if (!newProjectData.projectName.trim()) {
      alert("Please enter a project name.");
      return;
    }

    try {
      setProjectSubmitting(true);
      const res = await axios.post(
        `${API_BASE_URL}/api/project-expenses`,
        {
          projectName: newProjectData.projectName.trim(),
          customerName: newProjectData.customerName.trim(),
          location: newProjectData.location.trim(),
          projectType: newProjectData.projectType,
          estimateDate: newProjectData.estimateDate,
          notes: newProjectData.notes.trim()
        },
        { headers: getAuthHeaders() }
      );

      const created = res.data;
      if (created) {
        setProjects((prev) => [created, ...prev]);
        // Auto-select in Debit form
        setDebitForm((prev) => ({
          ...prev,
          projectId: created.estimateNo || created._id,
          projectName: created.projectName
        }));
        setSuccessMessage(`Project "${created.projectName}" created and selected!`);
        setShowProjectModal(false);
        setNewProjectData({
          projectName: "",
          customerName: "",
          location: "",
          projectType: "panel_building",
          estimateDate: todayDateString(),
          notes: ""
        });
      }
    } catch (err) {
      console.error("Error creating project:", err);
      alert(err.response?.data?.message || "Failed to create project.");
    } finally {
      setProjectSubmitting(false);
    }
  };

  // -------------------------------------------------------------
  // QUICK CREATE ABB WARRANTY COMPLAINT
  // -------------------------------------------------------------
  const handleQuickCreateWarrantyComplaint = async (e) => {
    e.preventDefault();
    if (!newWarrantyData.customerName.trim()) {
      alert("Please enter customer name.");
      return;
    }
    if (!newWarrantyData.place.trim()) {
      alert("Please enter place / location.");
      return;
    }
    if (!newWarrantyData.details.trim()) {
      alert("Please enter complaint details.");
      return;
    }

    try {
      setWarrantySubmitting(true);
      const res = await axios.post(
        `${API_BASE_URL}/api/complaints/quick-warranty`,
        {
          customerName: newWarrantyData.customerName.trim(),
          place: newWarrantyData.place.trim(),
          details: newWarrantyData.details.trim(),
          visitDate: newWarrantyData.visitDate || todayDateString(),
          contactPerson: newWarrantyData.contactPerson?.trim() || "",
          contactPhone: newWarrantyData.contactPhone?.trim() || ""
        },
        { headers: getAuthHeaders() }
      );

      const created = res.data;
      if (created) {
        setComplaints((prev) => [created, ...prev]);
        // Auto-select in Debit form
        setDebitForm((prev) => ({
          ...prev,
          abbWarrantyRefId: created.complaintId || created._id,
          abbWarrantyComplaintId: created.complaintId || created._id,
          customerName: created.company || newWarrantyData.customerName.trim(),
          place: created.address || newWarrantyData.place.trim(),
          details: created.issue || newWarrantyData.details.trim()
        }));
        setSuccessMessage(`ABB Warranty Complaint "${created.complaintId}" created and selected!`);
        setShowWarrantyModal(false);
        setNewWarrantyData({
          customerName: "",
          place: "",
          details: "",
          visitDate: todayDateString(),
          contactPerson: "",
          contactPhone: ""
        });
      }
    } catch (err) {
      console.error("Error creating ABB warranty complaint:", err);
      alert(err.response?.data?.message || "Failed to create ABB warranty complaint.");
    } finally {
      setWarrantySubmitting(false);
    }
  };

  // Helper to resolve ONLY the company name for a complaint transaction
  const getComplaintCompanyName = useCallback(
    (v) => {
      if (!v) return "Complaint";

      // 1. Check customerName (if valid and not an issue text)
      if (v.customerName && typeof v.customerName === "string" && v.customerName.trim()) {
        const lower = v.customerName.toLowerCase();
        const isIssueLike =
          lower.startsWith("acb") ||
          lower.includes("problem") ||
          lower.includes("spring charging") ||
          lower.includes("facing");
        if (!isIssueLike) {
          return v.customerName;
        }
      }

      // 2. Try resolving from master complaints list by referenceId
      const targetId = v.referenceId || v.complaintRefId || v.complaintId;
      if (targetId && complaints.length > 0) {
        const found = complaints.find(
          (c) =>
            c.complaintId === targetId ||
            c._id === targetId ||
            String(c.complaintId).toLowerCase() === String(targetId).toLowerCase()
        );
        if (found?.company) return found.company;
        if (found?.customerName) return found.customerName;
      }

      // 3. Try matching complaint by issue if referenceName contains the issue description
      if (v.referenceName && complaints.length > 0) {
        const refLower = v.referenceName.trim().toLowerCase();
        const foundByIssue = complaints.find(
          (c) => c.issue && c.issue.trim().toLowerCase() === refLower
        );
        if (foundByIssue?.company) return foundByIssue.company;
      }

      // 4. Check referenceName if not an issue
      if (v.referenceName && typeof v.referenceName === "string" && v.referenceName.trim()) {
        const lower = v.referenceName.toLowerCase();
        const isIssueLike =
          lower.startsWith("acb") ||
          lower.includes("problem") ||
          lower.includes("spring charging") ||
          lower.includes("facing");
        if (!isIssueLike) {
          return v.referenceName;
        }
      }

      // Fallback
      return v.customerName || "Complaint";
    },
    [complaints]
  );

  // Helper to resolve Complaint info (both Company Name and Complaint ID)
  const getComplaintInfo = useCallback(
    (v) => {
      if (!v) return { company: "Complaint", id: "" };

      let id = "";
      let company = "";

      const targetId = v.referenceId || v.complaintRefId || v.complaintId;
      let found = null;
      if (targetId && complaints.length > 0) {
        found = complaints.find(
          (c) =>
            c.complaintId === targetId ||
            c._id === targetId ||
            String(c.complaintId).toLowerCase() === String(targetId).toLowerCase()
        );
      }

      if (found) {
        id = found.complaintId || targetId || "";
        company = found.company || found.customerName || "";
      } else {
        id = targetId || "";
      }

      if (!company) {
        company = getComplaintCompanyName(v);
      }

      return { company: company || "Complaint", id: id || "" };
    },
    [complaints, getComplaintCompanyName]
  );

  // Helper to resolve Project info (Title, Company/Customer Name, and Estimate/Project ID)
  const getProjectInfo = useCallback(
    (v) => {
      if (!v) return { title: "Project", company: "", id: "" };

      const targetId = v.referenceId || v.projectId;
      let found = null;
      if (targetId && projects.length > 0) {
        found = projects.find(
          (p) =>
            p.estimateNo === targetId ||
            p._id === targetId ||
            (v.referenceName && p.projectName && p.projectName.toLowerCase() === v.referenceName.toLowerCase())
        );
      }

      const title = found?.projectName || v.referenceName || "Project";
      const company = found?.customerName || v.customerName || "";
      const id = found?.estimateNo || targetId || "";

      return { title, company, id };
    },
    [projects]
  );

  // Helper to extract reliable user info and role from any voucher/transaction
  const getVoucherUserInfo = useCallback(
    (v) => {
      if (!v) return { userType: "other", isEngineer: false, isAccount: false, displayName: "User", userId: "" };
      const role = (v.createdBy?.userRole || v.fromUser?.role || v.ownerRole || v.role || "").toLowerCase();
      const rawId = String(v.createdBy?.userId || v.fromUser?.userId || v.userId || v.engineer || v.engineerId || "");
      const rawName = String(v.createdBy?.userName || v.fromUser?.userName || v.userName || v.engineerName || "").trim();

      let isEngineer = role === "engineer";
      let isAccount = role === "accounts" || role === "account";

      if (!isEngineer && !isAccount) {
        if (
          engineers.some(
            (e) =>
              String(e._id) === rawId ||
              String(e.engineerId) === rawId ||
              (e.name && rawName && e.name.toLowerCase() === rawName.toLowerCase())
          )
        ) {
          isEngineer = true;
        } else if (
          accountsUsers.some(
            (a) =>
              String(a._id) === rawId ||
              (a.username && rawName && a.username.toLowerCase() === rawName.toLowerCase()) ||
              (a.name && rawName && a.name.toLowerCase() === rawName.toLowerCase())
          )
        ) {
          isAccount = true;
        }
      }

      const userType = isEngineer ? "engineer" : isAccount ? "accounts" : role || "other";
      const displayName = rawName || (isEngineer ? "Engineer" : isAccount ? "Accounts Staff" : "User");

      return {
        userType,
        isEngineer,
        isAccount,
        displayName,
        userId: rawId
      };
    },
    [engineers, accountsUsers]
  );

  // Status management permission per voucher:
  // Admin can manage status for all vouchers.
  // Accounts can ONLY manage status (approve/reject/pending) for Engineers' vouchers.
  const canManageVoucherStatus = useCallback(
    (v) => {
      if (!v || v.isCancelled) return false;
      if (userRole === "admin") return true;
      if (userRole === "accounts") {
        const uInfo = getVoucherUserInfo(v);
        return Boolean(uInfo.isEngineer);
      }
      return false;
    },
    [userRole, getVoucherUserInfo]
  );

  // Status update handler (only Admin or Accounts for Engineers)
  const handleStatusUpdate = async (voucherId, newStatus) => {
    if (!canManageStatus) return;
    const targetVoucher = vouchers.find(
      (item) => (item._id || item.transactionId) === voucherId
    );
    if (targetVoucher && !canManageVoucherStatus(targetVoucher)) {
      setErrorMessage("In accounts profile, you can only change status for Engineers' vouchers.");
      return;
    }

    try {
      setUpdatingStatusId(voucherId);
      const res = await axios.put(
        `${API_BASE_URL}/api/vouchers/transactions/${voucherId}/status`,
        { status: newStatus },
        { headers: getAuthHeaders() }
      );
      if (res.data?.success || res.status === 200) {
        setSuccessMessage(`Status updated to ${newStatus.replace("_", " ").toUpperCase()}.`);
        setVouchers((prev) =>
          prev.map((item) =>
            item._id === voucherId || item.transactionId === voucherId
              ? { ...item, status: newStatus }
              : item
          )
        );
        fetchSummary();
      }
    } catch (err) {
      console.error("Failed to update status:", err);
      setErrorMessage(err.response?.data?.error || "Failed to update status.");
    } finally {
      setUpdatingStatusId(null);
    }
  };

  // -------------------------------------------------------------
  // CHECKBOX SELECTION & BULK ACTIONS
  // -------------------------------------------------------------
  const handleToggleSelect = (id) => {
    setSelectedVoucherIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleToggleSelectAllVisible = () => {
    if (isAllVisibleSelected) {
      setSelectedVoucherIds((prev) =>
        prev.filter((id) => !visibleVoucherIds.includes(id))
      );
    } else {
      setSelectedVoucherIds((prev) => {
        const combined = new Set([...prev, ...visibleVoucherIds]);
        return Array.from(combined);
      });
    }
  };

  const handleSelectPendingOnly = () => {
    setSelectedVoucherIds(pendingVisibleVoucherIds);
  };

  const handleClearSelection = () => {
    setSelectedVoucherIds([]);
  };

  const handleBulkApprove = async (targetIds = null, targetStatus = "approved") => {
    if (!canManageStatus) return;
    let idsToUpdate = targetIds || selectedVoucherIds;
    if (!idsToUpdate || idsToUpdate.length === 0) return;

    if (userRole === "accounts") {
      idsToUpdate = idsToUpdate.filter((id) => {
        const v = vouchers.find((item) => (item._id || item.transactionId) === id);
        return v ? canManageVoucherStatus(v) : true;
      });
      if (idsToUpdate.length === 0) {
        setErrorMessage("In accounts profile, you can only approve vouchers for Engineers.");
        return;
      }
    }

    try {
      setBulkApproving(true);
      const res = await axios.put(
        `${API_BASE_URL}/api/vouchers/transactions/batch-status`,
        { ids: idsToUpdate, status: targetStatus },
        { headers: getAuthHeaders() }
      );

      if (res.data?.success || res.status === 200) {
        setSuccessMessage(
          `Successfully approved ${idsToUpdate.length} voucher${idsToUpdate.length > 1 ? "s" : ""}.`
        );
        setVouchers((prev) =>
          prev.map((item) => {
            const id = item._id || item.transactionId;
            if (idsToUpdate.includes(id)) {
              return { ...item, status: targetStatus };
            }
            return item;
          })
        );
        setSelectedVoucherIds((prev) => prev.filter((id) => !idsToUpdate.includes(id)));
        fetchSummary();
      }
    } catch (err) {
      console.error("Batch status update error, attempting fallback:", err);
      try {
        await Promise.all(
          idsToUpdate.map((id) =>
            axios.put(
              `${API_BASE_URL}/api/vouchers/transactions/${id}/status`,
              { status: targetStatus },
              { headers: getAuthHeaders() }
            )
          )
        );
        setSuccessMessage(
          `Successfully approved ${idsToUpdate.length} voucher${idsToUpdate.length > 1 ? "s" : ""}.`
        );
        setVouchers((prev) =>
          prev.map((item) => {
            const id = item._id || item.transactionId;
            if (idsToUpdate.includes(id)) {
              return { ...item, status: targetStatus };
            }
            return item;
          })
        );
        setSelectedVoucherIds((prev) => prev.filter((id) => !idsToUpdate.includes(id)));
        fetchSummary();
      } catch (fallbackErr) {
        setErrorMessage(
          err.response?.data?.error ||
            fallbackErr.response?.data?.error ||
            "Failed to approve selected vouchers."
        );
      }
    } finally {
      setBulkApproving(false);
    }
  };


  // -------------------------------------------------------------
  // EDIT VOUCHER
  // -------------------------------------------------------------
  const openEditModal = (v) => {
    setEditVoucher(v);
    setEditFormData({
      date: v.date ? new Date(v.date).toISOString().slice(0, 10) : todayDateString(),
      amount: v.amount || "",
      expenseCategory: v.expenseCategory || "General Expense",
      receivedFrom: v.fromUser?.userName || "",
      referenceType: v.referenceType || "general",
      referenceId: v.referenceId || "",
      referenceName: v.referenceType === "complaint" ? getComplaintCompanyName(v) : (v.referenceName || ""),
      status: v.status || "pending_approval",
      description: v.description || "",
      attachment: null
    });
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!editVoucher) return;

    const numAmount = parseFloat(editFormData.amount);
    if (!numAmount || numAmount <= 0) {
      alert("Please enter a valid amount greater than ₹0.");
      return;
    }

    try {
      setEditSubmitting(true);
      const hasAttachment = Boolean(editFormData.attachment);

      if (hasAttachment) {
        const postData = new FormData();
        postData.append("date", editFormData.date);
        postData.append("amount", String(numAmount));
        postData.append("description", editFormData.description || "");

        if (editVoucher.transactionType === "CREDIT") {
          postData.append("receivedFrom", editFormData.receivedFrom);
        } else {
          postData.append("expenseCategory", editFormData.expenseCategory);
          postData.append("referenceType", editFormData.referenceType);
          postData.append("referenceId", editFormData.referenceId);
          postData.append("referenceName", editFormData.referenceName);
          if (editFormData.referenceType === "complaint") {
            postData.append("customerName", editFormData.referenceName);
          }
        }

        if (canManageVoucherStatus(editVoucher) && editFormData.status) {
          postData.append("status", editFormData.status);
        }

        postData.append("attachments", editFormData.attachment);

        await axios.put(
          `${API_BASE_URL}/api/vouchers/transactions/${editVoucher._id || editVoucher.transactionId}`,
          postData,
          {
            headers: getAuthHeaders()
          }
        );
      } else {
        const payload = {
          date: editFormData.date,
          amount: numAmount,
          description: editFormData.description || "",
          ...(editVoucher.transactionType === "CREDIT"
            ? { receivedFrom: editFormData.receivedFrom }
            : {
                expenseCategory: editFormData.expenseCategory,
                referenceType: editFormData.referenceType,
                referenceId: editFormData.referenceId,
                referenceName: editFormData.referenceName,
                ...(editFormData.referenceType === "complaint" ? { customerName: editFormData.referenceName } : {})
              }),
          ...(canManageVoucherStatus(editVoucher) && editFormData.status ? { status: editFormData.status } : {})
        };

        await axios.put(
          `${API_BASE_URL}/api/vouchers/transactions/${editVoucher._id || editVoucher.transactionId}`,
          payload,
          {
            headers: {
              ...getAuthHeaders(),
              "Content-Type": "application/json"
            }
          }
        );
      }

      setSuccessMessage("Voucher updated successfully.");
      setEditVoucher(null);
      fetchSummary();
      fetchVouchers();
    } catch (err) {
      console.error("Error editing voucher:", err);
      alert(err.response?.data?.error || err.response?.data?.message || "Failed to update voucher.");
    } finally {
      setEditSubmitting(false);
    }
  };

  // -------------------------------------------------------------
  // VOID / CANCEL VOUCHER (Section 19: Non-destructive audit trail)
  // -------------------------------------------------------------
  const openVoidModal = (v) => {
    setVoidVoucher(v);
    setVoidReason("");
  };

  const handleVoidSubmit = async (e) => {
    e.preventDefault();
    if (!voidVoucher) return;
    if (!voidReason.trim()) {
      alert("Please specify a reason for voiding/cancelling this transaction.");
      return;
    }

    try {
      setVoidSubmitting(true);
      await axios.put(
        `${API_BASE_URL}/api/vouchers/transactions/${voidVoucher._id || voidVoucher.transactionId}/status`,
        {
          status: "cancelled",
          cancellationReason: voidReason.trim()
        },
        { headers: getAuthHeaders() }
      );

      setSuccessMessage("Voucher cancelled and archived with audit trail.");
      setVoidVoucher(null);
      fetchSummary();
      fetchVouchers();
    } catch (err) {
      console.error("Error voiding voucher:", err);
      alert(err.response?.data?.error || err.response?.data?.message || "Failed to cancel voucher.");
    } finally {
      setVoidSubmitting(false);
    }
  };

  // Grouped options for engineer dropdown filter
  const engineerOptions = useMemo(() => {
    const map = new Map();
    vouchers.forEach((v) => {
      if (v.isCancelled) return;
      const uInfo = getVoucherUserInfo(v);
      if (!uInfo.isEngineer) return;
      const key = uInfo.displayName || uInfo.userId || "Unknown Engineer";
      if (!map.has(key)) {
        map.set(key, { key, name: key, count: 0, totalAmount: 0 });
      }
      const item = map.get(key);
      item.count += 1;
      item.totalAmount += Number(v.amount || 0);
    });
    engineers.forEach((eng) => {
      const name = eng.name || eng.username;
      if (name && !map.has(name)) {
        map.set(name, { key: name, name, count: 0, totalAmount: 0 });
      }
    });
    return Array.from(map.values()).sort((a, b) => b.totalAmount - a.totalAmount || a.name.localeCompare(b.name));
  }, [vouchers, engineers, getVoucherUserInfo]);

  // Grouped options for accounts dropdown filter
  const accountOptions = useMemo(() => {
    const map = new Map();
    vouchers.forEach((v) => {
      if (v.isCancelled) return;
      const uInfo = getVoucherUserInfo(v);
      if (!uInfo.isAccount) return;
      const key = uInfo.displayName || uInfo.userId || "Unknown Staff";
      if (!map.has(key)) {
        map.set(key, { key, name: key, count: 0, totalAmount: 0 });
      }
      const item = map.get(key);
      item.count += 1;
      item.totalAmount += Number(v.amount || 0);
    });
    accountsUsers.forEach((acc) => {
      const name = acc.name || acc.username;
      if (name && !map.has(name)) {
        map.set(name, { key: name, name, count: 0, totalAmount: 0 });
      }
    });
    return Array.from(map.values()).sort((a, b) => b.totalAmount - a.totalAmount || a.name.localeCompare(b.name));
  }, [vouchers, accountsUsers, getVoucherUserInfo]);

  // Grouped options for complaint dropdown filter
  const complaintOptions = useMemo(() => {
    const map = new Map();
    vouchers.forEach((v) => {
      if (v.isCancelled || v.referenceType !== "complaint") return;
      const { company, id } = getComplaintInfo(v);
      const key = id || company;
      if (!map.has(key)) {
        map.set(key, { key, id, company, count: 0, totalAmount: 0 });
      }
      const item = map.get(key);
      item.count += 1;
      item.totalAmount += Number(v.amount || 0);
    });
    return Array.from(map.values()).sort((a, b) => b.totalAmount - a.totalAmount);
  }, [vouchers, getComplaintInfo]);

  // Grouped options for ABB warranty dropdown filter
  const abbWarrantyOptions = useMemo(() => {
    const map = new Map();
    vouchers.forEach((v) => {
      if (v.isCancelled || v.referenceType !== "abb_warranty") return;
      const { company, id } = getComplaintInfo(v);
      const key = id || company;
      if (!map.has(key)) {
        map.set(key, { key, id, company, count: 0, totalAmount: 0 });
      }
      const item = map.get(key);
      item.count += 1;
      item.totalAmount += Number(v.amount || 0);
    });
    return Array.from(map.values()).sort((a, b) => b.totalAmount - a.totalAmount);
  }, [vouchers, getComplaintInfo]);

  // Grouped options for project dropdown filter
  const projectOptions = useMemo(() => {
    const map = new Map();
    vouchers.forEach((v) => {
      if (v.isCancelled || v.referenceType !== "project") return;
      const { title, company, id } = getProjectInfo(v);
      const key = id || title;
      if (!map.has(key)) {
        map.set(key, { key, id, title, company, count: 0, totalAmount: 0 });
      }
      const item = map.get(key);
      item.count += 1;
      item.totalAmount += Number(v.amount || 0);
    });
    return Array.from(map.values()).sort((a, b) => b.totalAmount - a.totalAmount);
  }, [vouchers, getProjectInfo]);

  // Live admin stats across engineers and accounts
  const adminExpenseStats = useMemo(() => {
    let engTotal = 0, engCount = 0;
    let accTotal = 0, accCount = 0;
    let cmpTotal = 0, cmpCount = 0;
    let abbTotal = 0, abbCount = 0;
    let prjTotal = 0, prjCount = 0;
    let genTotal = 0, genCount = 0;
    let allTotal = 0, allCount = 0;

    vouchers.forEach((v) => {
      if (v.isCancelled) return;
      if (v.transactionType !== "DEBIT" && v.transactionType !== "EXPENSE") return;
      const amt = Number(v.amount || 0);
      allTotal += amt;
      allCount += 1;

      const uInfo = getVoucherUserInfo(v);
      if (uInfo.isEngineer) {
        engTotal += amt;
        engCount += 1;
      } else if (uInfo.isAccount) {
        accTotal += amt;
        accCount += 1;
      }

      if (v.referenceType === "abb_warranty") {
        abbTotal += amt;
        abbCount += 1;
      } else if (v.referenceType === "complaint" || v.expenseMode === "complaint" || v.entryType === "complaint") {
        cmpTotal += amt;
        cmpCount += 1;
      } else if (v.referenceType === "project" || v.expenseMode === "project" || v.entryType === "project") {
        prjTotal += amt;
        prjCount += 1;
      } else {
        genTotal += amt;
        genCount += 1;
      }
    });

    return {
      allTotal,
      allCount,
      engTotal,
      engCount,
      accTotal,
      accCount,
      cmpTotal,
      cmpCount,
      abbTotal,
      abbCount,
      prjTotal,
      prjCount,
      genTotal,
      genCount
    };
  }, [vouchers, getVoucherUserInfo]);

  // -------------------------------------------------------------
  // FILTERED EXPENSE LIST & SORTING
  // -------------------------------------------------------------
  const filteredVouchers = useMemo(() => {
    const list = vouchers.filter((v) => {
      // Exclude cancelled unless searched
      if (v.isCancelled && !searchTerm.trim()) {
        return false;
      }

      // Tab filter: 'ALL' | 'ENGINEERS' | 'ACCOUNTS' | 'COMPLAINTS' | 'ABB_WARRANTY' | 'PROJECT' | 'GENERAL'
      if (expenseTab === "ENGINEERS") {
        const uInfo = getVoucherUserInfo(v);
        if (!uInfo.isEngineer) return false;
        if (selectedEngineerFilter !== "ALL") {
          if (
            uInfo.displayName !== selectedEngineerFilter &&
            uInfo.userId !== selectedEngineerFilter
          ) {
            return false;
          }
        }
      } else if (expenseTab === "ACCOUNTS") {
        const uInfo = getVoucherUserInfo(v);
        if (!uInfo.isAccount) return false;
        if (selectedAccountFilter !== "ALL") {
          if (
            uInfo.displayName !== selectedAccountFilter &&
            uInfo.userId !== selectedAccountFilter
          ) {
            return false;
          }
        }
      } else if (expenseTab === "GENERAL") {
        if (v.transactionType !== "DEBIT" && v.transactionType !== "EXPENSE") return false;
        if (v.referenceType === "project" || v.referenceType === "complaint" || v.referenceType === "abb_warranty") return false;
      } else if (expenseTab === "PROJECT") {
        if (v.referenceType !== "project") return false;
        if (selectedProjectFilter !== "ALL") {
          const prj = getProjectInfo(v);
          const key = prj.id || prj.title;
          if (
            key !== selectedProjectFilter &&
            prj.id !== selectedProjectFilter &&
            prj.title !== selectedProjectFilter
          ) {
            return false;
          }
        }
      } else if (expenseTab === "COMPLAINTS" || expenseTab === "COMPLAINT") {
        if (v.referenceType === "abb_warranty") return false;
        if (v.referenceType !== "complaint" && v.expenseMode !== "complaint" && v.entryType !== "complaint") return false;
        if (selectedComplaintFilter !== "ALL") {
          const cmp = getComplaintInfo(v);
          const key = cmp.id || cmp.company;
          if (
            key !== selectedComplaintFilter &&
            cmp.id !== selectedComplaintFilter &&
            cmp.company !== selectedComplaintFilter
          ) {
            return false;
          }
        }
      } else if (expenseTab === "ABB_WARRANTY" || expenseTab === "ABB_WARRANTY_COMPLAINT") {
        if (v.referenceType !== "abb_warranty") return false;
        if (selectedAbbWarrantyFilter !== "ALL") {
          const cmp = getComplaintInfo(v);
          const key = cmp.id || cmp.company;
          if (
            key !== selectedAbbWarrantyFilter &&
            cmp.id !== selectedAbbWarrantyFilter &&
            cmp.company !== selectedAbbWarrantyFilter
          ) {
            return false;
          }
        }
      }

      // Date Range Filter
      const vDate = v.date ? new Date(v.date).toISOString().slice(0, 10) : "";
      if (dateFilter.from && vDate < dateFilter.from) return false;
      if (dateFilter.to && vDate > dateFilter.to) return false;

      // Text Search
      if (searchTerm.trim()) {
        const q = searchTerm.toLowerCase();
        const descMatch = String(v.description || "").toLowerCase().includes(q);
        const fromMatch = String(v.fromUser?.userName || "").toLowerCase().includes(q);
        const toMatch = String(v.toUser?.userName || "").toLowerCase().includes(q);
        const catMatch = String(v.expenseCategory || "").toLowerCase().includes(q);
        const cmpInfo = v.referenceType === "complaint" ? getComplaintInfo(v) : null;
        const prjInfo = v.referenceType === "project" ? getProjectInfo(v) : null;
        const uInfo = getVoucherUserInfo(v);
        const userMatch = uInfo.displayName.toLowerCase().includes(q);
        const refMatch =
          String(v.referenceName || v.referenceId || "").toLowerCase().includes(q) ||
          (cmpInfo && (cmpInfo.company.toLowerCase().includes(q) || cmpInfo.id.toLowerCase().includes(q))) ||
          (prjInfo && (prjInfo.title.toLowerCase().includes(q) || prjInfo.company.toLowerCase().includes(q) || prjInfo.id.toLowerCase().includes(q)));
        return descMatch || fromMatch || toMatch || catMatch || refMatch || userMatch;
      }

      return true;
    });

    // Sort by Date, Engineers, Accounts, Complaints, Projects, or Amount
    return list.sort((a, b) => {
      if (sortBy === "date_desc") {
        return new Date(b.date || 0) - new Date(a.date || 0);
      }
      if (sortBy === "date_asc") {
        return new Date(a.date || 0) - new Date(b.date || 0);
      }
      if (sortBy === "amount_desc") {
        return Number(b.amount || 0) - Number(a.amount || 0);
      }
      if (sortBy === "amount_asc") {
        return Number(a.amount || 0) - Number(b.amount || 0);
      }
      if (sortBy === "engineers") {
        const infoA = getVoucherUserInfo(a);
        const infoB = getVoucherUserInfo(b);
        if (infoA.isEngineer && !infoB.isEngineer) return -1;
        if (!infoA.isEngineer && infoB.isEngineer) return 1;
        return infoA.displayName.localeCompare(infoB.displayName);
      }
      if (sortBy === "accounts") {
        const infoA = getVoucherUserInfo(a);
        const infoB = getVoucherUserInfo(b);
        if (infoA.isAccount && !infoB.isAccount) return -1;
        if (!infoA.isAccount && infoB.isAccount) return 1;
        return infoA.displayName.localeCompare(infoB.displayName);
      }
      if (sortBy === "complaints") {
        const isCmpA = a.referenceType === "complaint";
        const isCmpB = b.referenceType === "complaint";
        if (isCmpA && !isCmpB) return -1;
        if (!isCmpA && isCmpB) return 1;
        const cmpA = (getComplaintInfo(a).company || getComplaintInfo(a).id || "").toLowerCase();
        const cmpB = (getComplaintInfo(b).company || getComplaintInfo(b).id || "").toLowerCase();
        return cmpA.localeCompare(cmpB);
      }
      if (sortBy === "project") {
        const isPrjA = a.referenceType === "project";
        const isPrjB = b.referenceType === "project";
        if (isPrjA && !isPrjB) return -1;
        if (!isPrjA && isPrjB) return 1;
        const prjA = (getProjectInfo(a).title || getProjectInfo(a).company || "").toLowerCase();
        const prjB = (getProjectInfo(b).title || getProjectInfo(b).company || "").toLowerCase();
        return prjA.localeCompare(prjB);
      }
      return new Date(b.date || 0) - new Date(a.date || 0);
    });
  }, [
    vouchers,
    expenseTab,
    dateFilter,
    searchTerm,
    sortBy,
    selectedComplaintFilter,
    selectedProjectFilter,
    selectedEngineerFilter,
    selectedAccountFilter,
    selectedAbbWarrantyFilter,
    getComplaintInfo,
    getProjectInfo,
    getVoucherUserInfo
  ]);

  // Checkbox selection computations
  const selectedVouchers = useMemo(() => {
    if (!selectedVoucherIds || selectedVoucherIds.length === 0) return [];
    const set = new Set(selectedVoucherIds);
    return vouchers.filter((v) => set.has(v._id || v.transactionId));
  }, [selectedVoucherIds, vouchers]);

  const selectedTotalAmount = useMemo(() => {
    return selectedVouchers.reduce((sum, v) => sum + (Number(v.amount) || 0), 0);
  }, [selectedVouchers]);

  const displayedVouchers = useMemo(() => {
    return filteredVouchers.slice(0, voucherLimit);
  }, [filteredVouchers, voucherLimit]);

  const visibleVoucherIds = useMemo(() => {
    return displayedVouchers
      .filter((v) => !v.isCancelled && (userRole === "admin" || canManageVoucherStatus(v)))
      .map((v) => v._id || v.transactionId);
  }, [displayedVouchers, userRole, canManageVoucherStatus]);

  const pendingVisibleVoucherIds = useMemo(() => {
    return displayedVouchers
      .filter(
        (v) =>
          !v.isCancelled &&
          (v.status === "pending" || v.status === "pending_approval" || !v.status) &&
          (userRole === "admin" || canManageVoucherStatus(v))
      )
      .map((v) => v._id || v.transactionId);
  }, [displayedVouchers, userRole, canManageVoucherStatus]);

  const isAllVisibleSelected = useMemo(() => {
    if (visibleVoucherIds.length === 0) return false;
    return visibleVoucherIds.every((id) => selectedVoucherIds.includes(id));
  }, [visibleVoucherIds, selectedVoucherIds]);

  const isSomeVisibleSelected = useMemo(() => {
    return visibleVoucherIds.some((id) => selectedVoucherIds.includes(id)) && !isAllVisibleSelected;
  }, [visibleVoucherIds, selectedVoucherIds, isAllVisibleSelected]);


  // Grouped filtered engineers for accordion view
  const groupedFilteredEngineers = useMemo(() => {
    if (expenseTab !== "ENGINEERS") return [];
    const map = new Map();
    filteredVouchers.forEach((v) => {
      const uInfo = getVoucherUserInfo(v);
      const key = uInfo.displayName || uInfo.userId || "Unknown Engineer";
      if (!map.has(key)) {
        map.set(key, {
          key,
          name: key,
          userId: uInfo.userId,
          entries: [],
          totalAmount: 0
        });
      }
      const item = map.get(key);
      item.entries.push(v);
      if (!v.isCancelled) {
        item.totalAmount += Number(v.amount || 0);
      }
    });
    return Array.from(map.values()).sort((a, b) => b.totalAmount - a.totalAmount);
  }, [filteredVouchers, expenseTab, getVoucherUserInfo]);

  // Grouped filtered accounts for accordion view
  const groupedFilteredAccounts = useMemo(() => {
    if (expenseTab !== "ACCOUNTS") return [];
    const map = new Map();
    filteredVouchers.forEach((v) => {
      const uInfo = getVoucherUserInfo(v);
      const key = uInfo.displayName || uInfo.userId || "Unknown Staff";
      if (!map.has(key)) {
        map.set(key, {
          key,
          name: key,
          userId: uInfo.userId,
          entries: [],
          totalAmount: 0
        });
      }
      const item = map.get(key);
      item.entries.push(v);
      if (!v.isCancelled) {
        item.totalAmount += Number(v.amount || 0);
      }
    });
    return Array.from(map.values()).sort((a, b) => b.totalAmount - a.totalAmount);
  }, [filteredVouchers, expenseTab, getVoucherUserInfo]);

  // Grouped filtered complaints for accordion view
  const groupedFilteredComplaints = useMemo(() => {
    if (expenseTab !== "COMPLAINTS" && expenseTab !== "COMPLAINT") return [];
    const map = new Map();
    filteredVouchers.forEach((v) => {
      const cmp = getComplaintInfo(v);
      const key = cmp.id || cmp.company || "unknown";
      if (!map.has(key)) {
        map.set(key, {
          key,
          id: cmp.id,
          company: cmp.company,
          entries: [],
          totalAmount: 0
        });
      }
      const item = map.get(key);
      item.entries.push(v);
      if (!v.isCancelled) {
        item.totalAmount += Number(v.amount || 0);
      }
    });
    return Array.from(map.values()).sort((a, b) => b.totalAmount - a.totalAmount);
  }, [filteredVouchers, expenseTab, getComplaintInfo]);

  // Grouped filtered ABB warranties for accordion view
  const groupedFilteredAbbWarranties = useMemo(() => {
    if (expenseTab !== "ABB_WARRANTY" && expenseTab !== "ABB_WARRANTY_COMPLAINT") return [];
    const map = new Map();
    filteredVouchers.forEach((v) => {
      const cmp = getComplaintInfo(v);
      const key = cmp.id || cmp.company || "unknown";
      if (!map.has(key)) {
        map.set(key, {
          key,
          id: cmp.id,
          company: cmp.company,
          entries: [],
          totalAmount: 0
        });
      }
      const item = map.get(key);
      item.entries.push(v);
      if (!v.isCancelled) {
        item.totalAmount += Number(v.amount || 0);
      }
    });
    return Array.from(map.values()).sort((a, b) => b.totalAmount - a.totalAmount);
  }, [filteredVouchers, expenseTab, getComplaintInfo]);

  // Grouped filtered projects for accordion view
  const groupedFilteredProjects = useMemo(() => {
    if (expenseTab !== "PROJECT") return [];
    const map = new Map();
    filteredVouchers.forEach((v) => {
      const prj = getProjectInfo(v);
      const key = prj.id || prj.title || "unknown";
      if (!map.has(key)) {
        map.set(key, {
          key,
          id: prj.id,
          title: prj.title,
          company: prj.company,
          entries: [],
          totalAmount: 0
        });
      }
      const item = map.get(key);
      item.entries.push(v);
      if (!v.isCancelled) {
        item.totalAmount += Number(v.amount || 0);
      }
    });
    return Array.from(map.values()).sort((a, b) => b.totalAmount - a.totalAmount);
  }, [filteredVouchers, expenseTab, getProjectInfo]);

  const toggleGroup = (key) => {
    setExpandedGroups((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const expandAllGroups = (groups) => {
    const next = {};
    groups.forEach((g) => {
      next[g.key] = true;
    });
    setExpandedGroups(next);
  };

  const collapseAllGroups = () => {
    setExpandedGroups({});
  };

  // Total amount of currently filtered records
  const filteredTotalAmount = useMemo(() => {
    return filteredVouchers.reduce((sum, v) => {
      if (v.isCancelled) return sum;
      return sum + Number(v.amount || 0);
    }, 0);
  }, [filteredVouchers]);

  // -------------------------------------------------------------
  // PROJECT EXPENSE SUMMARY CALCULATOR (Section 6)
  // -------------------------------------------------------------
  const projectSummaryDetails = useMemo(() => {
    if (!selectedProjectForSummary) return null;
    const pId = selectedProjectForSummary.estimateNo || selectedProjectForSummary._id;
    const pName = selectedProjectForSummary.projectName;

    const matchedExpenses = vouchers.filter((v) => {
      if (v.isCancelled) return false;
      if (v.transactionType !== "DEBIT" && v.transactionType !== "EXPENSE") return false;
      return (
        v.referenceType === "project" &&
        (v.referenceId === pId || (pName && v.referenceName?.toLowerCase() === pName.toLowerCase()))
      );
    });

    let totalSpent = 0;
    const catMap = {};
    const dateMap = {};

    matchedExpenses.forEach((item) => {
      const amt = Number(item.amount || 0);
      totalSpent += amt;
      const cat = item.expenseCategory || "Other";
      catMap[cat] = (catMap[cat] || 0) + amt;

      const d = item.date ? item.date.slice(0, 10) : "Unknown Date";
      dateMap[d] = (dateMap[d] || 0) + amt;
    });

    return {
      project: selectedProjectForSummary,
      totalSpent,
      count: matchedExpenses.length,
      categoryBreakdown: catMap,
      dateBreakdown: dateMap,
      expenses: matchedExpenses
    };
  }, [selectedProjectForSummary, vouchers]);

  // -------------------------------------------------------------
  // ENGINEER REPORT CALCULATOR (Section 11)
  // -------------------------------------------------------------
  const engineerReportDetails = useMemo(() => {
    if (!selectedEngineerForReport) return null;
    const target = engineers.find(
      (e) => e._id === selectedEngineerForReport || e.engineerId === selectedEngineerForReport
    );
    const engId = target?.engineerId || target?._id || selectedEngineerForReport;
    const engName = target?.name || "";

    const userTxns = vouchers.filter((v) => {
      if (v.isCancelled) return false;
      const matchesTo = v.toUser?.userId === engId || (engName && v.toUser?.userName?.toLowerCase() === engName.toLowerCase());
      const matchesFrom = v.fromUser?.userId === engId || (engName && v.fromUser?.userName?.toLowerCase() === engName.toLowerCase());
      return matchesTo || matchesFrom;
    });

    let totalCredit = 0;
    let generalExpense = 0;
    let projectExpense = 0;
    let complaintExpense = 0;
    let abbWarrantyExpense = 0;
    const projectBreakdown = {};
    const complaintBreakdown = {};
    const abbWarrantyBreakdown = {};
    const generalBreakdown = {};

    userTxns.forEach((tx) => {
      const amt = Number(tx.amount || 0);
      const isCredit = tx.transactionType === "CREDIT" || tx.transactionType === "ADVANCE";
      if (isCredit) {
        totalCredit += amt;
      } else {
        // Debit
        if (tx.referenceType === "project") {
          projectExpense += amt;
          const pName = tx.referenceName || tx.referenceId || "Project";
          projectBreakdown[pName] = (projectBreakdown[pName] || 0) + amt;
        } else if (tx.referenceType === "abb_warranty") {
          abbWarrantyExpense += amt;
          const aName = tx.referenceName || tx.customerName || "ABB Warranty";
          abbWarrantyBreakdown[aName] = (abbWarrantyBreakdown[aName] || 0) + amt;
        } else if (tx.referenceType === "complaint") {
          complaintExpense += amt;
          const cName = getComplaintCompanyName(tx);
          complaintBreakdown[cName] = (complaintBreakdown[cName] || 0) + amt;
        } else {
          generalExpense += amt;
          const cat = tx.expenseCategory || "Other";
          generalBreakdown[cat] = (generalBreakdown[cat] || 0) + amt;
        }
      }
    });

    const totalExpense = generalExpense + projectExpense + complaintExpense + abbWarrantyExpense;
    const balance = totalCredit - totalExpense;

    return {
      target,
      totalCredit,
      generalExpense,
      projectExpense,
      complaintExpense,
      abbWarrantyExpense,
      totalExpense,
      balance,
      projectBreakdown,
      complaintBreakdown,
      abbWarrantyBreakdown,
      generalBreakdown,
      transactions: userTxns
    };
  }, [selectedEngineerForReport, engineers, vouchers, getComplaintCompanyName]);

  // -------------------------------------------------------------
  // COMPLAINT REPORT CALCULATOR (Section 13)
  // -------------------------------------------------------------
  const complaintReportDetails = useMemo(() => {
    if (!selectedComplaintForReport) return null;
    const target = complaints.find(
      (c) => c._id === selectedComplaintForReport || c.complaintId === selectedComplaintForReport
    );
    const cId = target?.complaintId || target?._id || selectedComplaintForReport;

    const matched = vouchers.filter((v) => {
      if (v.isCancelled) return false;
      return v.referenceType === "complaint" && (v.referenceId === cId || v.referenceName === cId);
    });

    let totalExpense = 0;
    const catBreakdown = {};

    matched.forEach((v) => {
      const amt = Number(v.amount || 0);
      totalExpense += amt;
      const cat = v.expenseCategory || "Other";
      catBreakdown[cat] = (catBreakdown[cat] || 0) + amt;
    });

    return {
      complaint: target,
      totalExpense,
      count: matched.length,
      categoryBreakdown: catBreakdown,
      expenses: matched
    };
  }, [selectedComplaintForReport, complaints, vouchers]);

  // -------------------------------------------------------------
  // ABB WARRANTY REPORT CALCULATOR
  // -------------------------------------------------------------
  const abbWarrantyReportDetails = useMemo(() => {
    if (!selectedAbbWarrantyForReport) return null;
    const target = complaints.find(
      (c) => c._id === selectedAbbWarrantyForReport || c.complaintId === selectedAbbWarrantyForReport
    );
    const cId = target?.complaintId || target?._id || selectedAbbWarrantyForReport;

    const matched = vouchers.filter((v) => {
      if (v.isCancelled) return false;
      return v.referenceType === "abb_warranty" && (v.referenceId === cId || v.referenceName === cId);
    });

    let totalExpense = 0;
    const catBreakdown = {};

    matched.forEach((v) => {
      const amt = Number(v.amount || 0);
      totalExpense += amt;
      const cat = v.expenseCategory || "Other";
      catBreakdown[cat] = (catBreakdown[cat] || 0) + amt;
    });

    return {
      complaint: target,
      totalExpense,
      count: matched.length,
      categoryBreakdown: catBreakdown,
      expenses: matched
    };
  }, [selectedAbbWarrantyForReport, complaints, vouchers]);

  // Mobile Card Component for responsive display (< 768px)
  const renderMobileVoucherCard = (v) => {
    const isCredit = v.transactionType === "CREDIT" || v.transactionType === "ADVANCE";
    const cmp = v.referenceType === "complaint" ? getComplaintInfo(v) : null;
    const prj = v.referenceType === "project" ? getProjectInfo(v) : null;

    return (
      <div
        key={v._id || v.transactionId}
        className={`rounded-2xl border bg-white p-3.5 shadow-xs space-y-2.5 transition ${
          v.isCancelled ? "opacity-50 line-through bg-slate-100/60 border-slate-200" : "border-slate-200 hover:border-slate-300"
        }`}
      >
        {/* Row 1: Date, Type, Amount */}
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 flex-wrap">
            {canManageStatus && !v.isCancelled && (userRole === "admin" || canManageVoucherStatus(v)) && (
              <input
                type="checkbox"
                checked={selectedVoucherIds.includes(v._id || v.transactionId)}
                onChange={() => handleToggleSelect(v._id || v.transactionId)}
                className="h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500 cursor-pointer"
              />
            )}
            <span className="text-xs font-bold text-slate-700">
              {formatDateDisplay(v.date)}
            </span>
            <span
              className={`inline-flex items-center px-2 py-0.5 rounded-full text-[9px] font-black uppercase tracking-wider ${
                isCredit
                  ? "bg-emerald-100 text-emerald-800"
                  : "bg-rose-100 text-rose-800"
              }`}
            >
              {isCredit ? "+ CREDIT" : "- DEBIT"}
            </span>
          </div>

          <div
            className={`text-base font-black whitespace-nowrap ${
              isCredit ? "text-emerald-700" : "text-rose-700"
            }`}
          >
            {isCredit ? "+" : "-"} {formatCurrency(v.amount)}
          </div>
        </div>


        {/* Row 2: Linked Complaint / Project / General */}
        <div>
          {v.referenceType === "abb_warranty" ? (
            <div className="rounded-xl bg-teal-50/80 border border-teal-200/90 p-2 text-xs space-y-1">
              <div className="flex items-center gap-1.5 text-teal-950 font-bold">
                <span className="material-symbols-outlined text-sm text-teal-600">verified</span>
                <span className="truncate">{v.referenceName || v.customerName || cmp?.company || "ABB Warranty"}</span>
                <span className="px-1.5 py-0.5 rounded bg-teal-100 text-teal-800 text-[10px] font-bold">ABB WARRANTY</span>
              </div>
              {(v.referenceId || cmp?.id) && (
                <div className="text-[11px] font-mono font-bold text-teal-800">
                  Complaint ID: #{v.referenceId || cmp?.id}
                </div>
              )}
            </div>
          ) : v.referenceType === "complaint" && cmp ? (
            <div className="rounded-xl bg-amber-50/80 border border-amber-200/90 p-2 text-xs space-y-1">
              <div className="flex items-center gap-1.5 text-amber-950 font-bold">
                <span className="material-symbols-outlined text-sm text-amber-600">build</span>
                <span className="truncate">{cmp.company}</span>
              </div>
              {cmp.id && (
                <div className="text-[11px] font-mono font-bold text-amber-800">
                  Complaint ID: #{cmp.id}
                </div>
              )}
            </div>
          ) : v.referenceType === "project" && prj ? (
            <div className="rounded-xl bg-blue-50/80 border border-blue-200/90 p-2 text-xs space-y-1">
              <div className="flex items-center gap-1.5 text-blue-950 font-bold">
                <span className="material-symbols-outlined text-sm text-blue-600">domain</span>
                <span className="truncate">{prj.title}</span>
              </div>
              {(prj.company || prj.id) && (
                <div className="text-[11px] text-slate-600 flex items-center gap-1.5 flex-wrap">
                  {prj.company && <span className="font-semibold text-slate-800">{prj.company}</span>}
                  {prj.id && (
                    <span className="font-mono text-blue-700 bg-blue-100 px-1 rounded text-[10px] font-bold">
                      {prj.id}
                    </span>
                  )}
                </div>
              )}
            </div>
          ) : (
            <div className="text-xs text-slate-400 italic">General Expense</div>
          )}
        </div>

        {/* Row 3: Category & Created By */}
        <div className="text-xs space-y-1">
          <div className="flex items-center justify-between text-slate-600 flex-wrap gap-1">
            <span className="font-bold text-slate-800">
              {isCredit
                ? `From: ${v.fromUser?.userName || "Admin"}`
                : v.expenseCategory || "General Expense"}
            </span>
            {userRole !== "engineer" && (
              <span className="text-[11px] text-slate-400">
                By: <strong className="text-slate-700 font-semibold">{v.createdBy?.userName || v.fromUser?.userName || "User"}</strong> ({v.createdBy?.userRole || "user"})
              </span>
            )}
          </div>

          {v.description && (
            <p className="text-[11px] text-slate-600 bg-slate-50 rounded-lg p-2 border border-slate-100 break-words">
              {v.description}
            </p>
          )}
        </div>

        {/* Row 4: Status & Actions */}
        <div className="pt-2 border-t border-slate-100 flex items-center justify-between gap-2 flex-wrap">
          {/* Status */}
          <div>
            {v.isCancelled ? (
              <span className="inline-block px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-slate-200 text-slate-600">
                Cancelled
              </span>
            ) : canManageVoucherStatus(v) ? (
              <select
                value={v.status === "pending" ? "pending_approval" : (v.status || "pending_approval")}
                disabled={updatingStatusId === (v._id || v.transactionId)}
                onChange={(e) => handleStatusUpdate(v._id || v.transactionId, e.target.value)}
                className={`text-[10px] font-black uppercase tracking-wider px-2.5 py-1 rounded-full border cursor-pointer outline-none shadow-2xs ${
                  v.status === "approved" || v.status === "settled"
                    ? "bg-emerald-50 text-emerald-800 border-emerald-300"
                    : v.status === "rejected"
                    ? "bg-rose-50 text-rose-700 border-rose-300"
                    : "bg-amber-50 text-amber-800 border-amber-300"
                }`}
              >
                <option value="pending_approval">PENDING</option>
                <option value="approved">APPROVED</option>
                <option value="rejected">REJECTED</option>
              </select>
            ) : (
              <span
                className={`inline-block px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${
                  v.status === "approved" || v.status === "settled"
                    ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                    : v.status === "rejected"
                    ? "bg-rose-50 text-rose-700 border border-rose-200"
                    : "bg-amber-50 text-amber-700 border border-amber-200"
                }`}
              >
                {v.status === "pending_approval" ? "PENDING" : (v.status || "PENDING")}
              </span>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex items-center gap-1.5 ml-auto">
            {/* Quick Approve for Pending */}
            {canManageVoucherStatus(v) && !v.isCancelled && v.status !== "approved" && (
              <button
                type="button"
                disabled={updatingStatusId === (v._id || v.transactionId)}
                onClick={() => handleStatusUpdate(v._id || v.transactionId, "approved")}
                title="Approve this voucher"
                className="px-2 py-1 rounded-lg bg-emerald-50 hover:bg-emerald-100 text-emerald-800 border border-emerald-300 font-bold text-xs flex items-center gap-1 cursor-pointer transition shadow-2xs"
              >
                <span className="material-symbols-outlined text-xs font-bold">check</span>
                Approve
              </button>
            )}



            {!v.isCancelled && userRole !== "admin" && (
              <>
                <button
                  type="button"
                  onClick={() => openEditModal(v)}
                  className="px-3 py-1 rounded-lg bg-blue-50 hover:bg-blue-100 text-blue-700 font-bold text-xs flex items-center gap-1 cursor-pointer"
                >
                  <span className="material-symbols-outlined text-sm">edit</span>
                  Edit
                </button>
                <button
                  type="button"
                  onClick={() => openVoidModal(v)}
                  className="px-3 py-1 rounded-lg bg-rose-50 hover:bg-rose-100 text-rose-700 font-bold text-xs flex items-center gap-1 cursor-pointer"
                >
                  <span className="material-symbols-outlined text-sm">cancel</span>
                  Void
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-slate-50/70 pb-20 pt-2 text-slate-800 w-full">
      <div className="w-full px-1 sm:px-2 lg:px-3 space-y-4 sm:space-y-6">

        {/* FEEDBACK ALERTS */}
        {successMessage && (
          <div className="rounded-2xl bg-emerald-50 border border-emerald-200 p-4 text-emerald-900 flex items-center justify-between shadow-sm animate-fade-in">
            <div className="flex items-center gap-3">
              <span className="material-symbols-outlined text-emerald-600 text-2xl">check_circle</span>
              <p className="text-sm font-bold">{successMessage}</p>
            </div>
            <button
              onClick={() => setSuccessMessage("")}
              className="text-emerald-500 hover:text-emerald-800 text-lg font-bold px-2"
            >
              ✕
            </button>
          </div>
        )}

        {errorMessage && (
          <div className="rounded-2xl bg-rose-50 border border-rose-200 p-4 text-rose-900 flex items-center justify-between shadow-sm animate-fade-in">
            <div className="flex items-center gap-3">
              <span className="material-symbols-outlined text-rose-600 text-2xl">warning</span>
              <p className="text-sm font-bold">{errorMessage}</p>
            </div>
            <button
              onClick={() => setErrorMessage("")}
              className="text-rose-500 hover:text-rose-800 text-lg font-bold px-2"
            >
              ✕
            </button>
          </div>
        )}

        {/* ============================================================ */}
        {/* LIVE FINANCIAL BALANCE BANNER                                */}
        {/* ============================================================ */}
        <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 border-b border-slate-100 pb-5">
            <div>
              <div className="flex items-center gap-2">
                <span className={`inline-block px-2.5 py-0.5 rounded-full text-[11px] font-black uppercase tracking-wider ${
                  userRole === "admin" ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-700"
                }`}>
                  {userRole.toUpperCase()} PORTAL
                </span>
                <span className="text-xs text-slate-400 font-semibold">• {currentUserName}</span>
                {userRole === "admin" && (
                  <span className="inline-block px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-100 text-amber-900 border border-amber-200">
                    VIEW & AUDIT ONLY
                  </span>
                )}
              </div>
              <h1 className="text-2xl sm:text-3xl font-black text-slate-900 mt-1">
                {userRole === "admin" ? "VOUCHER & EXPENSE AUDIT" : "VOUCHER & EXPENSE MANAGER"}
              </h1>
              {userRole === "admin" && (
                <p className="text-xs text-slate-500 mt-0.5">
                  Auditing expenses recorded by Engineers and Accounts. Filter and sort by Engineers, Accounts, Complaints, and Projects below.
                </p>
              )}
            </div>

            {/* Top Action Buttons: Statement PDF & Quick Refresh */}
            <div className="flex items-center gap-2 self-start md:self-auto flex-wrap">
              <button
                type="button"
                onClick={handleOpenExportModal}
                className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-xl border border-emerald-300 bg-emerald-50 hover:bg-emerald-100 text-xs font-bold text-emerald-800 transition cursor-pointer shadow-xs"
                title="Download voucher statement ledger PDF"
              >
                <span className="material-symbols-outlined text-base text-emerald-700">
                  picture_as_pdf
                </span>
                Download Statement PDF
              </button>

              <button
                type="button"
                onClick={() => {
                  fetchSummary();
                  fetchVouchers();
                }}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl border border-slate-200 bg-slate-50 hover:bg-slate-100 text-xs font-bold text-slate-700 transition cursor-pointer"
              >
                <span className={`material-symbols-outlined text-base ${listLoading ? "animate-spin" : ""}`}>
                  refresh
                </span>
                Refresh Live Data
              </button>
            </div>
          </div>

          {/* Metric Cards Grid */}
          {userRole === "admin" ? (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-5">
              {/* Engineers Expenses */}
              <div className="rounded-2xl border border-sky-200 bg-sky-50/60 p-4">
                <div className="flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider text-sky-900">
                  <span className="material-symbols-outlined text-sm text-sky-700">engineering</span>
                  Engineers Total
                </div>
                <p className="text-2xl font-black text-sky-900 mt-1">
                  {formatCurrency(adminExpenseStats.engTotal)}
                </p>
                <p className="text-[10px] text-sky-700 font-semibold mt-0.5">
                  {adminExpenseStats.engCount} voucher entries recorded
                </p>
              </div>

              {/* Accounts Expenses */}
              <div className="rounded-2xl border border-indigo-200 bg-indigo-50/60 p-4">
                <div className="flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider text-indigo-900">
                  <span className="material-symbols-outlined text-sm text-indigo-700">account_balance_wallet</span>
                  Accounts Total
                </div>
                <p className="text-2xl font-black text-indigo-900 mt-1">
                  {formatCurrency(adminExpenseStats.accTotal)}
                </p>
                <p className="text-[10px] text-indigo-700 font-semibold mt-0.5">
                  {adminExpenseStats.accCount} voucher entries recorded
                </p>
              </div>

              {/* Complaint & Project Expenses */}
              <div className="rounded-2xl border border-amber-200 bg-amber-50/60 p-4">
                <div className="flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider text-amber-900">
                  <span className="material-symbols-outlined text-sm text-amber-700">build</span>
                  Complaints & Projects
                </div>
                <p className="text-2xl font-black text-amber-900 mt-1">
                  {formatCurrency(adminExpenseStats.cmpTotal + adminExpenseStats.prjTotal)}
                </p>
                <p className="text-[10px] text-amber-800 font-semibold mt-0.5 truncate">
                  Complaints: {formatCurrency(adminExpenseStats.cmpTotal)} • Projects: {formatCurrency(adminExpenseStats.prjTotal)}
                </p>
              </div>

              {/* Total Company Expenses */}
              <div className="rounded-2xl border border-slate-300 bg-slate-900 text-white p-4">
                <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400">
                  Total Expenses Spent
                </p>
                <p className="text-2xl font-black text-emerald-400 mt-1">
                  {formatCurrency(adminExpenseStats.allTotal)}
                </p>
                <p className="text-[10px] text-slate-300 font-semibold mt-0.5">
                  Across {adminExpenseStats.allCount} approved / logged vouchers
                </p>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-5">
              {/* Total Credit Received */}
              <div className="rounded-2xl border border-emerald-100 bg-emerald-50/50 p-4">
                <p className="text-[11px] font-bold uppercase tracking-wider text-emerald-800">
                  Total Money Received
                </p>
                <p className="text-2xl font-black text-emerald-700 mt-1">
                  {formatCurrency(balanceSummary.totalCredit)}
                </p>
                <p className="text-[10px] text-emerald-600 font-semibold mt-0.5">
                  + Inflow into available balance
                </p>
              </div>

              {/* Total Expenses Spent */}
              <div className="rounded-2xl border border-rose-100 bg-rose-50/50 p-4">
                <p className="text-[11px] font-bold uppercase tracking-wider text-rose-800">
                  Total Expenses Spent
                </p>
                <p className="text-2xl font-black text-rose-700 mt-1">
                  {formatCurrency(balanceSummary.totalExpense)}
                </p>
                <p className="text-[10px] text-rose-600 font-semibold mt-0.5">
                  General + Project + Complaint
                </p>
              </div>

              {/* Available Balance */}
              <div className="rounded-2xl border border-blue-100 bg-blue-50/50 p-4">
                <p className="text-[11px] font-bold uppercase tracking-wider text-blue-800">
                  Available Balance
                </p>
                <p
                  className={`text-2xl font-black mt-1 ${
                    balanceSummary.availableBalance >= 0 ? "text-blue-900" : "text-rose-700"
                  }`}
                >
                  {formatCurrency(balanceSummary.availableBalance)}
                </p>
                <p className="text-[10px] text-blue-600 font-semibold mt-0.5">
                  Credit Received − Total Debits
                </p>
              </div>

              {/* Segregated Breakdown Pill */}
              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 flex flex-col justify-center text-xs space-y-1">
                <div className="flex justify-between">
                  <span className="text-slate-500 font-bold">General:</span>
                  <span className="font-black text-slate-800">{formatCurrency(balanceSummary.totalGeneralExpense)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500 font-bold">Project:</span>
                  <span className="font-black text-slate-800">{formatCurrency(balanceSummary.totalProjectExpense)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500 font-bold">Complaint:</span>
                  <span className="font-black text-slate-800">{formatCurrency(balanceSummary.totalComplaintExpense)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-teal-600 font-bold">ABB Warranty:</span>
                  <span className="font-black text-slate-800">{formatCurrency(balanceSummary.totalAbbWarrantyExpense || 0)}</span>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* ============================================================ */}
        {/* MAIN NAVIGATION TABS                                         */}
        {/* ============================================================ */}
        <div className="flex items-center gap-2 overflow-x-auto pb-1 scrollbar-none">
          {userRole !== "admin" && (
            <button
              type="button"
              onClick={() => setActiveMainTab("entry")}
              className={`px-4 sm:px-5 py-2.5 rounded-2xl font-black text-xs uppercase tracking-wider transition-all flex items-center gap-2 whitespace-nowrap shrink-0 cursor-pointer ${
                activeMainTab === "entry"
                  ? "bg-slate-900 text-white shadow-md"
                  : "bg-white text-slate-600 border border-slate-200 hover:bg-slate-100"
              }`}
            >
              <span className="material-symbols-outlined text-base">edit_note</span>
              Voucher Entry
            </button>
          )}

          <button
            type="button"
            onClick={() => setActiveMainTab("expenses")}
            className={`px-4 sm:px-5 py-2.5 rounded-2xl font-black text-xs uppercase tracking-wider transition-all flex items-center gap-2 whitespace-nowrap shrink-0 cursor-pointer ${
              activeMainTab === "expenses"
                ? "bg-slate-900 text-white shadow-md"
                : "bg-white text-slate-600 border border-slate-200 hover:bg-slate-100"
            }`}
          >
            <span className="material-symbols-outlined text-base">receipt_long</span>
            {userRole === "admin"
              ? `Expenses & Vouchers (${vouchers.filter((v) => !v.isCancelled).length})`
              : `Expense List (${vouchers.filter((v) => !v.isCancelled).length})`}
          </button>

          {/* Accounts users do not manage projects or complaint expenses */}
          {userRole !== "accounts" && (
            <button
              type="button"
              onClick={() => setActiveMainTab("projects")}
              className={`px-4 sm:px-5 py-2.5 rounded-2xl font-black text-xs uppercase tracking-wider transition-all flex items-center gap-2 whitespace-nowrap shrink-0 cursor-pointer ${
                activeMainTab === "projects"
                  ? "bg-slate-900 text-white shadow-md"
                  : "bg-white text-slate-600 border border-slate-200 hover:bg-slate-100"
              }`}
            >
              <span className="material-symbols-outlined text-base">domain</span>
              Project Management
            </button>
          )}

          <button
            type="button"
            onClick={() => setActiveMainTab("reports")}
            className={`px-4 sm:px-5 py-2.5 rounded-2xl font-black text-xs uppercase tracking-wider transition-all flex items-center gap-2 whitespace-nowrap shrink-0 cursor-pointer ${
              activeMainTab === "reports"
                ? "bg-slate-900 text-white shadow-md"
                : "bg-white text-slate-600 border border-slate-200 hover:bg-slate-100"
            }`}
          >
            <span className="material-symbols-outlined text-base">monitoring</span>
            Reports & Dashboard
          </button>
        </div>

        {/* ============================================================ */}
        {/* TAB 1: MAIN VOUCHER ENTRY (Strict 2-Entry Architecture)      */}
        {/* ============================================================ */}
        {activeMainTab === "entry" && userRole !== "admin" && (
          <div className="rounded-3xl border border-slate-200 bg-white p-6 sm:p-8 shadow-sm">
            {/* Header */}
            <div className="text-center max-w-xl mx-auto mb-6">
              <h2 className="text-2xl font-black text-slate-900 tracking-tight">
                NEW VOUCHER ENTRY
              </h2>
              <p className="text-xs text-slate-500 mt-1">
                Choose entry type below: <strong className="text-emerald-700">CREDIT</strong> for money received, or <strong className="text-rose-700">DEBIT</strong> for money spent.
              </p>
            </div>

            {/* ONLY TWO MAIN ENTRIES: [ CREDIT ] [ DEBIT ] */}
            <div className="max-w-md mx-auto grid grid-cols-2 gap-3 p-1.5 bg-slate-100 rounded-2xl border border-slate-200">
              <button
                type="button"
                onClick={() => {
                  setEntryType("CREDIT");
                  setErrorMessage("");
                }}
                className={`flex items-center justify-center gap-2 py-3.5 px-4 rounded-xl font-black text-sm uppercase tracking-wider transition-all duration-200 ${
                  entryType === "CREDIT"
                    ? "bg-emerald-600 text-white shadow-md shadow-emerald-600/25 scale-[1.02]"
                    : "text-slate-600 hover:text-slate-900 hover:bg-slate-200/60"
                }`}
              >
                <span className="material-symbols-outlined text-lg font-bold">arrow_downward</span>
                CREDIT
              </button>

              <button
                type="button"
                onClick={() => {
                  setEntryType("DEBIT");
                  setErrorMessage("");
                }}
                className={`flex items-center justify-center gap-2 py-3.5 px-4 rounded-xl font-black text-sm uppercase tracking-wider transition-all duration-200 ${
                  entryType === "DEBIT"
                    ? "bg-rose-600 text-white shadow-md shadow-rose-600/25 scale-[1.02]"
                    : "text-slate-600 hover:text-slate-900 hover:bg-slate-200/60"
                }`}
              >
                <span className="material-symbols-outlined text-lg font-bold">arrow_upward</span>
                DEBIT
              </button>
            </div>

            {/* -------------------------------------------------------- */}
            {/* CREDIT / TRANSFER ENTRY FORM                             */}
            {/* -------------------------------------------------------- */}
            {entryType === "CREDIT" && (
              <form onSubmit={handleCreditSubmit} className="mt-8 max-w-2xl mx-auto space-y-5 animate-fade-in">
                <div className="rounded-2xl bg-emerald-50/60 border border-emerald-200 p-3.5 text-xs text-emerald-900 flex items-center gap-2.5">
                  <span className="material-symbols-outlined text-emerald-600 text-lg font-bold">
                    {creditForm.creditType === "transfer" ? "send_money" : "payments"}
                  </span>
                  <span>
                    {userRole === "admin"
                      ? creditForm.creditType === "transfer"
                        ? "Admin Give Amount / Transfer section. Recorded funds will automatically appear in the recipient's Credit ledger."
                        : "Record company fund inflows. This will automatically increase your organization available balance."
                      : userRole === "accounts"
                      ? creditForm.creditType === "transfer"
                        ? "Transfer amount to a selected Engineer (site visit / travel advance) or back to Admin. This will automatically credit the recipient's ledger."
                        : "Record money received from Admin. This will automatically increase your Accounts available balance."
                      : "Record money received. This will automatically increase your available balance."}
                  </span>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {/* Date */}
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-slate-700 mb-1.5">
                      Date <span className="text-rose-500">*</span>
                    </label>
                    <input
                      type="date"
                      required
                      value={creditForm.date}
                      onChange={(e) => setCreditForm((prev) => ({ ...prev, date: e.target.value }))}
                      className="w-full rounded-xl border border-slate-300 bg-white px-3.5 py-2.5 text-sm font-semibold text-slate-800 outline-none focus:border-slate-800 focus:ring-2 focus:ring-slate-100"
                    />
                  </div>

                  {/* Amount */}
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-slate-700 mb-1.5">
                      Amount (₹) <span className="text-rose-500">*</span>
                    </label>
                    <div className="relative">
                      <span className="absolute left-3.5 top-1/2 -translate-y-1/2 font-bold text-slate-400">₹</span>
                      <input
                        type="number"
                        step="any"
                        placeholder="0.00"
                        required
                        value={creditForm.amount}
                        onChange={(e) => setCreditForm((prev) => ({ ...prev, amount: e.target.value }))}
                        className="w-full rounded-xl border border-slate-300 bg-white pl-8 pr-3.5 py-2.5 text-sm font-black text-slate-900 outline-none focus:border-slate-800 focus:ring-2 focus:ring-slate-100"
                      />
                    </div>
                  </div>
                </div>

                {/* ADMIN ROLE FLOW: Option 1: Company Inflow, Option 2: Give/Allot to User */}
                {userRole === "admin" ? (
                  <div className="space-y-4">
                    <div>
                      <label className="block text-xs font-bold uppercase tracking-wider text-emerald-900 mb-1.5">
                        Credit Operation Type <span className="text-rose-500">*</span>
                      </label>
                      <div className="grid grid-cols-2 gap-3">
                        <button
                          type="button"
                          onClick={() => setCreditForm((prev) => ({ ...prev, creditType: "self" }))}
                          className={`py-2.5 px-3 rounded-xl border text-xs font-black uppercase tracking-wider transition ${
                            creditForm.creditType !== "transfer"
                              ? "bg-emerald-600 text-white border-emerald-600 shadow-xs"
                              : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"
                          }`}
                        >
                          Company / Admin Inflow
                        </button>
                        <button
                          type="button"
                          onClick={() => setCreditForm((prev) => ({ ...prev, creditType: "transfer" }))}
                          className={`py-2.5 px-3 rounded-xl border text-xs font-black uppercase tracking-wider transition ${
                            creditForm.creditType === "transfer"
                              ? "bg-emerald-600 text-white border-emerald-600 shadow-xs"
                              : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"
                          }`}
                        >
                          Allot / Give to User
                        </button>
                      </div>
                    </div>

                    {creditForm.creditType !== "transfer" ? (
                      <div>
                        <label className="block text-xs font-bold uppercase tracking-wider text-emerald-900 mb-1.5">
                          Received From (Source) <span className="text-rose-500">*</span>
                        </label>
                        <input
                          type="text"
                          required
                          value={creditForm.receivedFrom}
                          onChange={(e) => setCreditForm((prev) => ({ ...prev, receivedFrom: e.target.value }))}
                          placeholder="e.g. Customer, Client Payment, Bank Deposit, Cash..."
                          className="w-full rounded-xl border border-emerald-300 bg-emerald-50/20 px-3.5 py-2.5 text-sm font-semibold text-slate-800 outline-none focus:border-emerald-600"
                        />
                        <div className="flex gap-2 mt-2 flex-wrap">
                          {["Customer Payment", "Bank Deposit", "Director / Self", "Cash"].map((src) => (
                            <button
                              key={src}
                              type="button"
                              onClick={() => setCreditForm((prev) => ({ ...prev, receivedFrom: src }))}
                              className="text-[11px] font-bold text-emerald-800 bg-emerald-100/60 hover:bg-emerald-100 px-2.5 py-1 rounded-lg transition"
                            >
                              + {src}
                            </button>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-xs font-bold uppercase tracking-wider text-emerald-900 mb-1.5">
                            Given To <span className="text-rose-500">*</span>
                          </label>
                          <select
                            value={creditForm.givenTo}
                            onChange={(e) => {
                              const val = e.target.value;
                              setCreditForm((prev) => ({
                                ...prev,
                                givenTo: val,
                                recipientId: "",
                                recipientName: ""
                              }));
                            }}
                            className="w-full rounded-xl border border-emerald-300 bg-emerald-50/20 px-3.5 py-2.5 text-sm font-semibold text-slate-800 outline-none focus:border-emerald-600"
                          >
                            <option value="engineer">Engineer</option>
                            <option value="accounts">Accounts</option>
                          </select>
                        </div>

                        <div>
                          <label className="block text-xs font-bold uppercase tracking-wider text-emerald-900 mb-1.5">
                            Select {creditForm.givenTo === "engineer" ? "Engineer" : "Accounts User"} <span className="text-rose-500">*</span>
                          </label>
                          <select
                            required
                            value={creditForm.recipientId}
                            onChange={(e) => {
                              const selId = e.target.value;
                              let name = "";
                              if (creditForm.givenTo === "engineer") {
                                const eng = engineers.find((x) => x._id === selId || x.engineerId === selId);
                                name = eng?.name || "Engineer";
                              } else {
                                const acc = accountsUsers.find((x) => x._id === selId);
                                name = acc?.name || "Accounts";
                              }
                              setCreditForm((prev) => ({
                                ...prev,
                                recipientId: selId,
                                recipientName: name
                              }));
                            }}
                            className="w-full rounded-xl border border-emerald-300 bg-emerald-50/20 px-3.5 py-2.5 text-sm font-semibold text-slate-800 outline-none focus:border-emerald-600"
                          >
                            <option value="">-- Select Recipient --</option>
                            {creditForm.givenTo === "engineer"
                              ? engineers.map((eng) => (
                                  <option key={eng._id || eng.engineerId} value={eng.engineerId || eng._id}>
                                    {eng.name} ({eng.engineerId || "ENG"})
                                  </option>
                                ))
                              : accountsUsers.map((acc) => (
                                  <option key={acc._id} value={acc._id}>
                                    {acc.name} ({acc.email || "Accounts"})
                                  </option>
                                ))}
                          </select>
                        </div>
                      </div>
                    )}
                  </div>
                ) : userRole === "accounts" ? (
                  /* ACCOUNTS ROLE FLOW: Receive from Admin OR Transfer to Admin / Selected Engineer */
                  <div className="space-y-4">
                    <div>
                      <label className="block text-xs font-bold uppercase tracking-wider text-emerald-900 mb-1.5">
                        Operation Type <span className="text-rose-500">*</span>
                      </label>
                      <div className="grid grid-cols-2 gap-3">
                        <button
                          type="button"
                          onClick={() =>
                            setCreditForm((prev) => ({
                              ...prev,
                              creditType: "self",
                              receivedFrom: "Admin",
                              recipientId: "",
                              recipientName: ""
                            }))
                          }
                          className={`py-2.5 px-3 rounded-xl border text-xs font-black uppercase tracking-wider transition flex items-center justify-center gap-1.5 ${
                            creditForm.creditType !== "transfer"
                              ? "bg-emerald-600 text-white border-emerald-600 shadow-xs"
                              : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"
                          }`}
                        >
                          <span className="material-symbols-outlined text-sm font-bold">arrow_downward</span>
                          Receive from Admin
                        </button>
                        <button
                          type="button"
                          onClick={() =>
                            setCreditForm((prev) => ({
                              ...prev,
                              creditType: "transfer",
                              givenTo: "engineer",
                              recipientId: "",
                              recipientName: ""
                            }))
                          }
                          className={`py-2.5 px-3 rounded-xl border text-xs font-black uppercase tracking-wider transition flex items-center justify-center gap-1.5 ${
                            creditForm.creditType === "transfer"
                              ? "bg-emerald-600 text-white border-emerald-600 shadow-xs"
                              : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"
                          }`}
                        >
                          <span className="material-symbols-outlined text-sm font-bold">send_money</span>
                          Transfer Amount
                        </button>
                      </div>
                    </div>

                    {creditForm.creditType !== "transfer" ? (
                      <div>
                        <label className="block text-xs font-bold uppercase tracking-wider text-emerald-900 mb-1.5">
                          Received From
                        </label>
                        <input
                          type="text"
                          disabled
                          value="Admin"
                          className="w-full rounded-xl border border-slate-200 bg-slate-100 px-3.5 py-2.5 text-sm font-bold text-slate-700 cursor-not-allowed"
                        />
                        <p className="text-[11px] text-slate-400 mt-1">
                          Accounts users receive financial allotments exclusively from Admin.
                        </p>
                      </div>
                    ) : (
                      <div className="space-y-4 p-4 rounded-2xl bg-emerald-50/40 border border-emerald-200/80">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-xs font-bold uppercase tracking-wider text-emerald-900 mb-1.5">
                              Transfer To <span className="text-rose-500">*</span>
                            </label>
                            <select
                              value={creditForm.givenTo}
                              onChange={(e) => {
                                const val = e.target.value;
                                setCreditForm((prev) => ({
                                  ...prev,
                                  givenTo: val,
                                  recipientId: "",
                                  recipientName: val === "admin" ? "Admin" : ""
                                }));
                              }}
                              className="w-full rounded-xl border border-emerald-300 bg-white px-3.5 py-2.5 text-sm font-semibold text-slate-800 outline-none focus:border-emerald-600"
                            >
                              <option value="engineer">Selected Engineer</option>
                              <option value="admin">Admin</option>
                            </select>
                          </div>

                          <div>
                            <label className="block text-xs font-bold uppercase tracking-wider text-emerald-900 mb-1.5">
                              Select {creditForm.givenTo === "engineer" ? "Engineer" : "Recipient"} <span className="text-rose-500">*</span>
                            </label>
                            {creditForm.givenTo === "engineer" ? (
                              <select
                                required
                                value={creditForm.recipientId}
                                onChange={(e) => {
                                  const selId = e.target.value;
                                  const eng = engineers.find((x) => x._id === selId || x.engineerId === selId);
                                  setCreditForm((prev) => ({
                                    ...prev,
                                    recipientId: selId,
                                    recipientName: eng?.name || "Engineer"
                                  }));
                                }}
                                className="w-full rounded-xl border border-emerald-300 bg-white px-3.5 py-2.5 text-sm font-semibold text-slate-800 outline-none focus:border-emerald-600"
                              >
                                <option value="">-- Choose Engineer --</option>
                                {engineers.map((eng) => (
                                  <option key={eng._id || eng.engineerId} value={eng.engineerId || eng._id}>
                                    {eng.name} ({eng.engineerId || "ENG"})
                                  </option>
                                ))}
                              </select>
                            ) : (
                              <input
                                type="text"
                                disabled
                                value="Admin"
                                className="w-full rounded-xl border border-slate-200 bg-slate-100 px-3.5 py-2.5 text-sm font-bold text-slate-700 cursor-not-allowed"
                              />
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  /* ENGINEER ROLE FLOW: Received From Admin or Accounts */
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-emerald-900 mb-1.5">
                      Received From <span className="text-rose-500">*</span>
                    </label>
                    <div className="grid grid-cols-2 gap-3">
                      <button
                        type="button"
                        onClick={() => setCreditForm((prev) => ({ ...prev, receivedFrom: "Admin" }))}
                        className={`py-2.5 px-4 rounded-xl border text-xs font-black uppercase tracking-wider transition ${
                          creditForm.receivedFrom === "Admin"
                            ? "bg-emerald-600 text-white border-emerald-600 shadow-xs"
                            : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"
                        }`}
                      >
                        Admin
                      </button>
                      <button
                        type="button"
                        onClick={() => setCreditForm((prev) => ({ ...prev, receivedFrom: "Accounts" }))}
                        className={`py-2.5 px-4 rounded-xl border text-xs font-black uppercase tracking-wider transition ${
                          creditForm.receivedFrom === "Accounts"
                            ? "bg-emerald-600 text-white border-emerald-600 shadow-xs"
                            : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"
                        }`}
                      >
                        Accounts
                      </button>
                    </div>
                  </div>
                )}

                {/* Description / Purpose */}
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-slate-700 mb-1.5">
                    {userRole === "admin" || creditForm.creditType === "transfer" ? "Purpose / Remarks" : "Description / Remarks"}
                  </label>
                  <textarea
                    rows={3}
                    placeholder={
                      userRole === "admin"
                        ? creditForm.creditType === "transfer"
                          ? "e.g. Advance for project visit, travel allowance allotment..."
                          : "e.g. Received client payment, company inflow..."
                        : userRole === "accounts"
                        ? creditForm.creditType === "transfer"
                          ? "e.g. Advance cash transferred to selected engineer for site visit expenses..."
                          : "e.g. Received advance cash from Admin for operational funds..."
                        : "e.g. Received advance cash from Admin for site visit..."
                    }
                    value={creditForm.description}
                    onChange={(e) => setCreditForm((prev) => ({ ...prev, description: e.target.value }))}
                    className="w-full rounded-xl border border-slate-300 bg-white px-3.5 py-2.5 text-sm text-slate-800 outline-none focus:border-slate-800 focus:ring-2 focus:ring-slate-100 resize-none"
                  />
                </div>

                {/* Attachment / Receipt */}
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-slate-700 mb-1.5">
                    Attachment / Receipt <span className="text-slate-400 font-normal lowercase">(optional cheque, bank transfer receipt, bill)</span>
                  </label>
                  <input
                    id="credit-file-input"
                    type="file"
                    accept="image/*,.pdf"
                    onChange={(e) => setCreditForm((prev) => ({ ...prev, attachment: e.target.files?.[0] || null }))}
                    className="block w-full text-xs text-slate-500 file:mr-4 file:py-2.5 file:px-4 file:rounded-xl file:border-0 file:text-xs file:font-bold file:bg-slate-100 file:text-slate-700 hover:file:bg-slate-200 cursor-pointer"
                  />
                </div>

                {/* Submit Button */}
                <div className="pt-2">
                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full py-3.5 px-6 rounded-2xl font-black text-sm uppercase tracking-wider text-white shadow-lg shadow-emerald-600/25 bg-emerald-600 hover:bg-emerald-700 transition disabled:opacity-60 flex items-center justify-center gap-2"
                  >
                    {submitting ? (
                      <>
                        <span className="h-4 w-4 rounded-full border-2 border-white border-t-transparent animate-spin" />
                        {creditForm.creditType === "transfer" ? "Processing Transfer..." : "Recording Credit..."}
                      </>
                    ) : (
                      <>
                        <span className="material-symbols-outlined text-lg font-bold">
                          {creditForm.creditType === "transfer" ? "send_money" : "check"}
                        </span>
                        {userRole === "admin"
                          ? creditForm.creditType === "transfer"
                            ? "Record Admin Payment / Give Amount"
                            : "Record Credit Inflow"
                          : userRole === "accounts"
                          ? creditForm.creditType === "transfer"
                            ? creditForm.recipientName
                              ? `Transfer Amount to ${creditForm.recipientName}`
                              : "Transfer Amount to Selected Recipient"
                            : "Save Credit Entry (Received from Admin)"
                          : "Save Credit Entry"}
                      </>
                    )}
                  </button>
                </div>
              </form>
            )}

            {/* -------------------------------------------------------- */}
            {/* DEBIT ENTRY FORM (General, Project, Complaint)            */}
            {/* -------------------------------------------------------- */}
            {entryType === "DEBIT" && (
              <form onSubmit={handleDebitSubmit} className="mt-8 max-w-2xl mx-auto space-y-6 animate-fade-in">

                {/* THREE DEBIT CATEGORIES PILLS (Section 4 & 8) */}
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-slate-700 mb-2 text-center">
                    Select Expense Category
                  </label>
                  <div
                    className={`grid gap-2.5 p-1.5 bg-slate-100 rounded-2xl border border-slate-200 ${
                      userRole === "accounts" ? "grid-cols-1" : "grid-cols-2 md:grid-cols-4"
                    }`}
                  >
                    <button
                      type="button"
                      onClick={() => {
                        setDebitCategory("general");
                        // Also update category for rows if needed or keep default
                        setDebitForm((prev) => ({
                          ...prev,
                          entries: prev.entries.map((ent) => ({
                            ...ent,
                            expenseCategory: GENERAL_CATEGORIES.includes(ent.expenseCategory)
                              ? ent.expenseCategory
                              : GENERAL_CATEGORIES[0]
                          }))
                        }));
                      }}
                      className={`py-2.5 px-3 rounded-xl font-black text-xs uppercase tracking-wider transition ${
                        debitCategory === "general"
                          ? "bg-rose-600 text-white shadow-xs"
                          : "text-slate-600 hover:text-slate-900"
                      }`}
                    >
                      General Expense
                    </button>

                    {/* Section 8: Accounts does NOT have Project or Complaint expense */}
                    {userRole !== "accounts" && (
                      <>
                        <button
                          type="button"
                          onClick={() => {
                            setDebitCategory("project");
                            setDebitForm((prev) => ({
                              ...prev,
                              entries: prev.entries.map((ent) => ({
                                ...ent,
                                expenseCategory: PROJECT_CATEGORIES.includes(ent.expenseCategory)
                                  ? ent.expenseCategory
                                  : PROJECT_CATEGORIES[0]
                              }))
                            }));
                          }}
                          className={`py-2.5 px-3 rounded-xl font-black text-xs uppercase tracking-wider transition ${
                            debitCategory === "project"
                              ? "bg-rose-600 text-white shadow-xs"
                              : "text-slate-600 hover:text-slate-900"
                          }`}
                        >
                          Project Expense
                        </button>

                        <button
                          type="button"
                          onClick={() => {
                            setDebitCategory("complaint");
                            setDebitForm((prev) => ({
                              ...prev,
                              entries: prev.entries.map((ent) => ({
                                ...ent,
                                expenseCategory: COMPLAINT_CATEGORIES.includes(ent.expenseCategory)
                                  ? ent.expenseCategory
                                  : COMPLAINT_CATEGORIES[0]
                              }))
                            }));
                          }}
                          className={`py-2.5 px-3 rounded-xl font-black text-xs uppercase tracking-wider transition ${
                            debitCategory === "complaint"
                              ? "bg-rose-600 text-white shadow-xs"
                              : "text-slate-600 hover:text-slate-900"
                          }`}
                        >
                          Complaint Expense
                        </button>

                        <button
                          type="button"
                          onClick={() => {
                            setDebitCategory("abb_warranty");
                            setDebitForm((prev) => ({
                              ...prev,
                              entries: prev.entries.map((ent) => ({
                                ...ent,
                                expenseCategory: ABB_WARRANTY_CATEGORIES.includes(ent.expenseCategory)
                                  ? ent.expenseCategory
                                  : ABB_WARRANTY_CATEGORIES[0]
                              }))
                            }));
                          }}
                          className={`py-2.5 px-3 rounded-xl font-black text-xs uppercase tracking-wider transition ${
                            debitCategory === "abb_warranty"
                              ? "bg-rose-600 text-white shadow-xs"
                              : "text-slate-600 hover:text-slate-900"
                          }`}
                        >
                          ABB WARRENTY COMPLAINT
                        </button>
                      </>
                    )}
                  </div>
                </div>

                {/* Shared Date Input */}
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-slate-700 mb-1.5">
                    Date <span className="text-rose-500">*</span>
                  </label>
                  <input
                    type="date"
                    required
                    value={debitForm.date}
                    onChange={(e) => setDebitForm((prev) => ({ ...prev, date: e.target.value }))}
                    className="w-full rounded-xl border border-slate-300 bg-white px-3.5 py-2.5 text-sm font-semibold text-slate-800 outline-none focus:border-slate-800"
                  />
                </div>

                {/* ---------------------------------------------------- */}
                {/* SUB-FLOW B: PROJECT SELECTION (When in Project Mode) */}
                {/* ---------------------------------------------------- */}
                {debitCategory === "project" && (
                  <div className="space-y-2 p-4 rounded-2xl bg-blue-50/50 border border-blue-200">
                    <div className="flex items-center justify-between">
                      <label className="block text-xs font-bold uppercase tracking-wider text-blue-900">
                        Select Project <span className="text-rose-500">*</span>
                      </label>
                      <button
                        type="button"
                        onClick={() => setShowProjectModal(true)}
                        className="text-xs font-black text-blue-700 hover:text-blue-900 inline-flex items-center gap-1 underline"
                      >
                        <span className="material-symbols-outlined text-sm font-bold">add</span>
                        [ + Create Project ]
                      </button>
                    </div>

                    <select
                      required
                      value={debitForm.projectId}
                      onChange={(e) => {
                        const selId = e.target.value;
                        const proj = projects.find((p) => p.estimateNo === selId || p._id === selId);
                        setDebitForm((prev) => ({
                          ...prev,
                          projectId: selId,
                          projectName: proj?.projectName || ""
                        }));
                      }}
                      className="w-full rounded-xl border border-blue-300 bg-white px-3.5 py-2.5 text-sm font-semibold text-slate-800 outline-none focus:border-blue-600"
                    >
                      <option value="">-- Choose Existing Project --</option>
                      {projects.map((proj) => (
                        <option key={proj._id || proj.estimateNo} value={proj.estimateNo || proj._id}>
                          {proj.projectName} ({proj.estimateNo || "Project"})
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {/* ---------------------------------------------------- */}
                {/* SUB-FLOW C: COMPLAINT SELECTION (When in Complaint) */}
                {/* ---------------------------------------------------- */}
                {debitCategory === "complaint" && (
                  <div className="space-y-2 p-4 rounded-2xl bg-amber-50/50 border border-amber-200">
                    <label className="block text-xs font-bold uppercase tracking-wider text-amber-950">
                      Select Complaint (Regular Complaint) <span className="text-rose-500">*</span>
                    </label>
                    <select
                      required
                      value={debitForm.complaintRefId}
                      onChange={(e) => {
                        const selId = e.target.value;
                        const cmp = regularComplaints.find((c) => c.complaintId === selId || c._id === selId);
                        const companyName = cmp?.company || cmp?.customerName || "";
                        setDebitForm((prev) => ({
                          ...prev,
                          complaintRefId: selId,
                          complaintId: companyName || selId,
                          customerName: companyName
                        }));
                      }}
                      className="w-full rounded-xl border border-amber-300 bg-white px-3.5 py-2.5 text-sm font-semibold text-slate-800 outline-none focus:border-amber-600"
                    >
                      <option value="">
                        {regularComplaints.length === 0
                          ? "-- No regular complaints found --"
                          : `-- Choose Existing Complaint (${regularComplaints.length} available) --`}
                      </option>
                      {regularComplaints.map((cmp) => (
                        <option key={cmp._id || cmp.complaintId} value={cmp.complaintId || cmp._id}>
                          #{cmp.complaintId} – {cmp.company}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {/* -------------------------------------------------------- */}
                {/* SUB-FLOW D: ABB WARRANTY COMPLAINT SELECTION / CREATION */}
                {/* -------------------------------------------------------- */}
                {debitCategory === "abb_warranty" && (
                  <div className="space-y-3 p-4 rounded-2xl bg-gradient-to-br from-teal-50/80 to-emerald-50/60 border border-teal-200 shadow-xs">
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <div>
                        <div className="flex items-center gap-1.5">
                          <span className="material-symbols-outlined text-teal-700 text-sm font-bold">verified</span>
                          <label className="block text-xs font-bold uppercase tracking-wider text-teal-950">
                            ABB Warranty Complaint <span className="text-rose-500">*</span>
                          </label>
                        </div>
                        <p className="text-[11px] text-teal-800 mt-0.5">
                          Select an existing ABB warranty complaint or click Create Complaint to enter details.
                        </p>
                      </div>

                      <button
                        type="button"
                        onClick={() => setShowWarrantyModal(true)}
                        className="px-3.5 py-2 rounded-xl bg-teal-600 hover:bg-teal-700 text-white text-xs font-black uppercase tracking-wider shadow-xs transition inline-flex items-center gap-1 cursor-pointer"
                      >
                        <span className="material-symbols-outlined text-sm font-bold">add</span>
                        [ + Create Complaint ]
                      </button>
                    </div>

                    <select
                      required
                      value={debitForm.abbWarrantyRefId}
                      onChange={(e) => {
                        const selId = e.target.value;
                        const cmp = abbWarrantyComplaints.find((c) => c.complaintId === selId || c._id === selId);
                        const companyName = cmp?.company || cmp?.customerName || "";
                        setDebitForm((prev) => ({
                          ...prev,
                          abbWarrantyRefId: selId,
                          abbWarrantyComplaintId: cmp?.complaintId || selId,
                          customerName: companyName,
                          place: cmp?.address || "",
                          details: cmp?.issue || ""
                        }));
                      }}
                      className="w-full rounded-xl border border-teal-300 bg-white px-3.5 py-2.5 text-sm font-semibold text-slate-800 outline-none focus:border-teal-600"
                    >
                      <option value="">
                        {abbWarrantyComplaints.length === 0
                          ? "-- No existing ABB Warranty complaints (Click [+ Create Complaint] above) --"
                          : `-- Choose Existing ABB Warranty Complaint (${abbWarrantyComplaints.length} available) --`}
                      </option>
                      {abbWarrantyComplaints.map((cmp) => (
                        <option key={cmp._id || cmp.complaintId} value={cmp.complaintId || cmp._id}>
                          #{cmp.complaintId} – {cmp.company} {cmp.address ? `• ${cmp.address}` : ""}
                        </option>
                      ))}
                    </select>

                    {/* Active Selected Card Preview */}
                    {debitForm.abbWarrantyRefId && (
                      <div className="p-3.5 rounded-xl bg-white/95 border border-teal-200 text-xs space-y-2 shadow-xs">
                        <div className="flex items-center justify-between">
                          <span className="font-black text-teal-900 uppercase tracking-wider text-[11px] flex items-center gap-1">
                            <span className="material-symbols-outlined text-sm text-teal-600">assignment_turned_in</span>
                            Complaint: #{debitForm.abbWarrantyComplaintId || debitForm.abbWarrantyRefId}
                          </span>
                          <span className="px-2 py-0.5 rounded-full bg-teal-100 text-teal-800 font-black text-[10px] tracking-wider">
                            ABB WARRANTY
                          </span>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 pt-2 border-t border-teal-100 text-slate-700">
                          <div>
                            <span className="text-[10px] uppercase font-bold text-slate-400 block">Customer Name</span>
                            <span className="font-bold text-slate-900">{debitForm.customerName || "-"}</span>
                          </div>
                          <div>
                            <span className="text-[10px] uppercase font-bold text-slate-400 block">Place / Location</span>
                            <span className="font-bold text-slate-900">{debitForm.place || "-"}</span>
                          </div>
                          {debitForm.details && (
                            <div className="sm:col-span-2">
                              <span className="text-[10px] uppercase font-bold text-slate-400 block">Complaint Details</span>
                              <span className="text-slate-800 font-medium">{debitForm.details}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* ---------------------------------------------------- */}
                {/* ONE SINGLE UNIFIED BOX FOR DEBIT EXPENSE ROWS        */}
                {/* ---------------------------------------------------- */}
                <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden shadow-xs">
                  {/* Single Box Header */}
                  <div className="bg-slate-50/80 px-4 py-3 border-b border-slate-200 flex items-center justify-between flex-wrap gap-2">
                    <div className="flex items-center gap-2">
                      <span className="material-symbols-outlined text-rose-600 text-lg">receipt_long</span>
                      <span className="text-xs font-black uppercase tracking-wider text-slate-800">
                        Expense Entries ({debitForm.entries.length})
                      </span>
                    </div>

                    <button
                      type="button"
                      onClick={addDebitEntry}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-rose-600 hover:bg-rose-700 text-white font-black text-xs uppercase tracking-wider transition shadow-2xs cursor-pointer"
                    >
                      <span className="material-symbols-outlined text-sm font-bold">add</span>
                      + Add Row
                    </button>
                  </div>

                  {/* Desktop / Tablet Table Header */}
                  <div className="hidden md:grid md:grid-cols-12 gap-3 px-4 py-2.5 bg-slate-100/70 border-b border-slate-200 text-[10px] font-black uppercase tracking-wider text-slate-500">
                    <div className="col-span-1 text-center">#</div>
                    <div className="col-span-3">Category *</div>
                    <div className="col-span-3">Amount (₹) *</div>
                    <div className="col-span-4">Details / Remarks</div>
                    <div className="col-span-1 text-center">Action</div>
                  </div>

                  {/* Rows inside the Single Box */}
                  <div className="divide-y divide-slate-100">
                    {debitForm.entries.map((entry, idx) => {
                      const categoryOptions =
                        debitCategory === "general"
                          ? GENERAL_CATEGORIES
                          : debitCategory === "project"
                          ? PROJECT_CATEGORIES
                          : debitCategory === "abb_warranty"
                          ? ABB_WARRANTY_CATEGORIES
                          : COMPLAINT_CATEGORIES;

                      return (
                        <div
                          key={entry.id || idx}
                          className="p-3.5 sm:px-4 sm:py-3 hover:bg-slate-50/60 transition"
                        >
                          {/* Desktop Grid Layout */}
                          <div className="hidden md:grid md:grid-cols-12 gap-3 items-center">
                            {/* Row Number */}
                            <div className="col-span-1 flex justify-center">
                              <span className="h-6 w-6 rounded-full bg-slate-100 text-slate-600 text-xs font-black flex items-center justify-center border border-slate-200">
                                {idx + 1}
                              </span>
                            </div>

                            {/* Category Dropdown */}
                            <div className="col-span-3">
                              <select
                                required
                                value={entry.expenseCategory}
                                onChange={(e) => updateDebitEntry(idx, "expenseCategory", e.target.value)}
                                className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-xs font-bold text-slate-800 outline-none focus:border-rose-600"
                              >
                                {categoryOptions.map((cat) => (
                                  <option key={cat} value={cat}>
                                    {cat}
                                  </option>
                                ))}
                              </select>
                            </div>

                            {/* Amount Input */}
                            <div className="col-span-3">
                              <div className="relative">
                                <span className="absolute left-3 top-1/2 -translate-y-1/2 font-bold text-xs text-slate-400">₹</span>
                                <input
                                  type="number"
                                  step="any"
                                  placeholder="0.00"
                                  required
                                  value={entry.amount}
                                  onChange={(e) => updateDebitEntry(idx, "amount", e.target.value)}
                                  className="w-full rounded-xl border border-slate-300 bg-white pl-7 pr-3 py-2 text-xs font-black text-slate-900 outline-none focus:border-rose-600"
                                />
                              </div>
                            </div>

                            {/* Remarks Input */}
                            <div className="col-span-4">
                              <input
                                type="text"
                                placeholder="Purpose, vendor, location..."
                                value={entry.description}
                                onChange={(e) => updateDebitEntry(idx, "description", e.target.value)}
                                className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-xs text-slate-800 outline-none focus:border-rose-600"
                              />
                            </div>

                            {/* Remove Action */}
                            <div className="col-span-1 flex justify-center">
                              {debitForm.entries.length > 1 ? (
                                <button
                                  type="button"
                                  onClick={() => removeDebitEntry(idx)}
                                  className="h-8 w-8 rounded-lg text-slate-400 hover:text-rose-600 hover:bg-rose-50 flex items-center justify-center transition cursor-pointer"
                                  title={`Remove row #${idx + 1}`}
                                >
                                  <span className="material-symbols-outlined text-base">delete</span>
                                </button>
                              ) : (
                                <span className="h-8 w-8 flex items-center justify-center text-slate-300 text-xs">-</span>
                              )}
                            </div>
                          </div>

                          {/* Mobile Stacked Layout (Small Screens) */}
                          <div className="block md:hidden space-y-2.5">
                            <div className="flex items-center justify-between pb-1 border-b border-slate-100">
                              <span className="text-xs font-black uppercase tracking-wider text-slate-700 flex items-center gap-1.5">
                                <span className="h-5 w-5 rounded-full bg-slate-200 text-slate-700 text-[11px] font-black flex items-center justify-center">
                                  {idx + 1}
                                </span>
                                Row #{idx + 1}
                              </span>

                              {debitForm.entries.length > 1 && (
                                <button
                                  type="button"
                                  onClick={() => removeDebitEntry(idx)}
                                  className="text-xs font-bold text-rose-600 hover:text-rose-800 inline-flex items-center gap-0.5 px-2 py-0.5 rounded-lg hover:bg-rose-50 transition cursor-pointer"
                                >
                                  <span className="material-symbols-outlined text-sm">delete</span>
                                  Remove
                                </button>
                              )}
                            </div>

                            <div className="grid grid-cols-2 gap-2">
                              <div>
                                <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">
                                  Category *
                                </label>
                                <select
                                  required
                                  value={entry.expenseCategory}
                                  onChange={(e) => updateDebitEntry(idx, "expenseCategory", e.target.value)}
                                  className="w-full rounded-xl border border-slate-300 bg-white px-2.5 py-1.5 text-xs font-bold text-slate-800 outline-none focus:border-rose-600"
                                >
                                  {categoryOptions.map((cat) => (
                                    <option key={cat} value={cat}>
                                      {cat}
                                    </option>
                                  ))}
                                </select>
                              </div>

                              <div>
                                <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">
                                  Amount (₹) *
                                </label>
                                <div className="relative">
                                  <span className="absolute left-2.5 top-1/2 -translate-y-1/2 font-bold text-xs text-slate-400">₹</span>
                                  <input
                                    type="number"
                                    step="any"
                                    placeholder="0.00"
                                    required
                                    value={entry.amount}
                                    onChange={(e) => updateDebitEntry(idx, "amount", e.target.value)}
                                    className="w-full rounded-xl border border-slate-300 bg-white pl-6 pr-2.5 py-1.5 text-xs font-black text-slate-900 outline-none focus:border-rose-600"
                                  />
                                </div>
                              </div>
                            </div>

                            <div>
                              <input
                                type="text"
                                placeholder="Details / Remarks (optional)..."
                                value={entry.description}
                                onChange={(e) => updateDebitEntry(idx, "description", e.target.value)}
                                className="w-full rounded-xl border border-slate-300 bg-white px-3 py-1.5 text-xs text-slate-800 outline-none focus:border-rose-600"
                              />
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Single Box Footer with Add Row Button and Total */}
                  <div className="bg-slate-50 px-4 py-3 border-t border-slate-200 flex items-center justify-between flex-wrap gap-3">
                    <button
                      type="button"
                      onClick={addDebitEntry}
                      className="inline-flex items-center gap-1 text-xs font-black uppercase tracking-wider text-rose-700 hover:text-rose-900 py-1.5 px-3 rounded-xl border border-rose-200 bg-white hover:bg-rose-50/50 shadow-2xs transition cursor-pointer"
                    >
                      <span className="material-symbols-outlined text-sm font-bold">add</span>
                      + Add Row
                    </button>

                    <div className="flex items-center gap-2">
                      <span className="text-xs font-bold uppercase tracking-wider text-slate-500">
                        Total ({debitForm.entries.length} {debitForm.entries.length === 1 ? "row" : "rows"}):
                      </span>
                      <span className="text-base font-black text-rose-700">
                        {formatCurrency(debitTotalAmount)}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Submit Button */}
                <div className="pt-2">
                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full py-3.5 px-6 rounded-2xl font-black text-sm uppercase tracking-wider text-white shadow-lg shadow-rose-600/25 bg-rose-600 hover:bg-rose-700 transition disabled:opacity-60 flex items-center justify-center gap-2"
                  >
                    {submitting ? (
                      <>
                        <span className="h-4 w-4 rounded-full border-2 border-white border-t-transparent animate-spin" />
                        Saving Debit Entries...
                      </>
                    ) : (
                      <>
                        <span className="material-symbols-outlined text-lg font-bold">check</span>
                        Save All Debit Entries ({formatCurrency(debitTotalAmount)})
                      </>
                    )}
                  </button>
                </div>
              </form>
            )}
          </div>
        )}

        {/* ============================================================ */}
        {/* TAB 2: EXPENSE LIST (Segregated Tabs + Date Filter)          */}
        {/* ============================================================ */}
        {activeMainTab === "expenses" && (
          <div className="rounded-2xl sm:rounded-3xl border border-slate-200 bg-white p-3.5 sm:p-6 md:p-8 shadow-sm space-y-5">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 border-b border-slate-100 pb-5">
              <div>
                <h2 className="text-xl sm:text-2xl font-black text-slate-900 tracking-tight">
                  EXPENSE & VOUCHER RECORDS
                </h2>
                <p className="text-xs text-slate-500 mt-0.5">
                  Showing segregated records with period calculation.
                </p>
              </div>

              {/* Live Period Total Card */}
              <div className="rounded-2xl bg-slate-900 text-white px-4 py-2.5 sm:px-5 sm:py-3 flex items-center justify-between sm:justify-start gap-4 shadow-sm w-full md:w-auto">
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                    Total for Filtered Period
                  </p>
                  <p className="text-lg sm:text-xl font-black text-emerald-400">
                    {formatCurrency(filteredTotalAmount)}
                  </p>
                </div>
                <div className="h-8 w-px bg-slate-700" />
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Records</p>
                  <p className="text-base font-black text-white">{filteredVouchers.length}</p>
                </div>
              </div>
            </div>

            {/* SEGREGATED EXPENSE TABS: ALL | ENGINEERS | ACCOUNTS | COMPLAINTS | PROJECT | GENERAL */}
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3">
              <div className="flex items-center gap-1 overflow-x-auto p-1 bg-slate-100 rounded-2xl border border-slate-200 text-xs font-bold scrollbar-none w-full lg:w-auto">
                {/* ALL */}
                <button
                  type="button"
                  onClick={() => {
                    setExpenseTab("ALL");
                    setSelectedEngineerFilter("ALL");
                    setSelectedAccountFilter("ALL");
                    setSelectedComplaintFilter("ALL");
                    setSelectedAbbWarrantyFilter("ALL");
                    setSelectedProjectFilter("ALL");
                  }}
                  className={`px-3.5 sm:px-4 py-2 rounded-xl transition whitespace-nowrap shrink-0 cursor-pointer flex items-center gap-1.5 ${
                    expenseTab === "ALL" ? "bg-slate-900 text-white shadow-xs" : "text-slate-600 hover:text-slate-900"
                  }`}
                >
                  <span>ALL</span>
                  <span className={`px-1.5 py-0.2 rounded-md text-[10px] ${
                    expenseTab === "ALL" ? "bg-slate-800 text-slate-200" : "bg-slate-200 text-slate-600"
                  }`}>
                    {adminExpenseStats.allCount}
                  </span>
                </button>

                {/* ENGINEERS (Admin / Accounts only) */}
                {userRole !== "engineer" && (
                  <button
                    type="button"
                    onClick={() => {
                      setExpenseTab("ENGINEERS");
                      setSelectedAccountFilter("ALL");
                      setSelectedComplaintFilter("ALL");
                      setSelectedAbbWarrantyFilter("ALL");
                      setSelectedProjectFilter("ALL");
                    }}
                    className={`px-3.5 sm:px-4 py-2 rounded-xl transition whitespace-nowrap shrink-0 cursor-pointer flex items-center gap-1.5 ${
                      expenseTab === "ENGINEERS" ? "bg-sky-600 text-white shadow-xs" : "text-slate-600 hover:text-slate-900"
                    }`}
                  >
                    <span className="material-symbols-outlined text-sm">engineering</span>
                    <span>ENGINEERS</span>
                    <span className={`px-1.5 py-0.2 rounded-md text-[10px] ${
                      expenseTab === "ENGINEERS" ? "bg-sky-700 text-sky-100" : "bg-sky-100 text-sky-800"
                    }`}>
                      {adminExpenseStats.engCount}
                    </span>
                  </button>
                )}

                {/* ACCOUNTS (Admin / Accounts only) */}
                {userRole !== "engineer" && (
                  <button
                    type="button"
                    onClick={() => {
                      setExpenseTab("ACCOUNTS");
                      setSelectedEngineerFilter("ALL");
                      setSelectedComplaintFilter("ALL");
                      setSelectedAbbWarrantyFilter("ALL");
                      setSelectedProjectFilter("ALL");
                    }}
                    className={`px-3.5 sm:px-4 py-2 rounded-xl transition whitespace-nowrap shrink-0 cursor-pointer flex items-center gap-1.5 ${
                      expenseTab === "ACCOUNTS" ? "bg-indigo-600 text-white shadow-xs" : "text-slate-600 hover:text-slate-900"
                    }`}
                  >
                    <span className="material-symbols-outlined text-sm">account_balance_wallet</span>
                    <span>ACCOUNTS</span>
                    <span className={`px-1.5 py-0.2 rounded-md text-[10px] ${
                      expenseTab === "ACCOUNTS" ? "bg-indigo-700 text-indigo-100" : "bg-indigo-100 text-indigo-800"
                    }`}>
                      {adminExpenseStats.accCount}
                    </span>
                  </button>
                )}

                {/* COMPLAINTS */}
                <button
                  type="button"
                  onClick={() => {
                    setExpenseTab("COMPLAINTS");
                    setSelectedEngineerFilter("ALL");
                    setSelectedAccountFilter("ALL");
                    setSelectedProjectFilter("ALL");
                    setSelectedAbbWarrantyFilter("ALL");
                  }}
                  className={`px-3.5 sm:px-4 py-2 rounded-xl transition whitespace-nowrap shrink-0 cursor-pointer flex items-center gap-1.5 ${
                    expenseTab === "COMPLAINTS" || expenseTab === "COMPLAINT" ? "bg-amber-600 text-white shadow-xs" : "text-slate-600 hover:text-slate-900"
                  }`}
                >
                  <span className="material-symbols-outlined text-sm">build</span>
                  <span>COMPLAINTS</span>
                  <span className={`px-1.5 py-0.2 rounded-md text-[10px] ${
                    expenseTab === "COMPLAINTS" || expenseTab === "COMPLAINT" ? "bg-amber-700 text-amber-100" : "bg-amber-100 text-amber-800"
                  }`}>
                    {adminExpenseStats.cmpCount}
                  </span>
                </button>

                {/* ABB WARRANTY */}
                <button
                  type="button"
                  onClick={() => {
                    setExpenseTab("ABB_WARRANTY");
                    setSelectedEngineerFilter("ALL");
                    setSelectedAccountFilter("ALL");
                    setSelectedProjectFilter("ALL");
                    setSelectedComplaintFilter("ALL");
                    setSelectedAbbWarrantyFilter("ALL");
                  }}
                  className={`px-3.5 sm:px-4 py-2 rounded-xl transition whitespace-nowrap shrink-0 cursor-pointer flex items-center gap-1.5 ${
                    expenseTab === "ABB_WARRANTY" || expenseTab === "ABB_WARRANTY_COMPLAINT" ? "bg-teal-600 text-white shadow-xs" : "text-slate-600 hover:text-slate-900"
                  }`}
                >
                  <span className="material-symbols-outlined text-sm">verified</span>
                  <span>ABB WARRANTY</span>
                  <span className={`px-1.5 py-0.2 rounded-md text-[10px] ${
                    expenseTab === "ABB_WARRANTY" || expenseTab === "ABB_WARRANTY_COMPLAINT" ? "bg-teal-700 text-teal-100" : "bg-teal-100 text-teal-800"
                  }`}>
                    {adminExpenseStats.abbCount}
                  </span>
                </button>

                {/* PROJECT */}
                <button
                  type="button"
                  onClick={() => {
                    setExpenseTab("PROJECT");
                    setSelectedEngineerFilter("ALL");
                    setSelectedAccountFilter("ALL");
                    setSelectedComplaintFilter("ALL");
                    setSelectedAbbWarrantyFilter("ALL");
                  }}
                  className={`px-3.5 sm:px-4 py-2 rounded-xl transition whitespace-nowrap shrink-0 cursor-pointer flex items-center gap-1.5 ${
                    expenseTab === "PROJECT" ? "bg-blue-600 text-white shadow-xs" : "text-slate-600 hover:text-slate-900"
                  }`}
                >
                  <span className="material-symbols-outlined text-sm">domain</span>
                  <span>PROJECT</span>
                  <span className={`px-1.5 py-0.2 rounded-md text-[10px] ${
                    expenseTab === "PROJECT" ? "bg-blue-700 text-blue-100" : "bg-blue-100 text-blue-800"
                  }`}>
                    {adminExpenseStats.prjCount}
                  </span>
                </button>

                {/* GENERAL */}
                <button
                  type="button"
                  onClick={() => {
                    setExpenseTab("GENERAL");
                    setSelectedEngineerFilter("ALL");
                    setSelectedAccountFilter("ALL");
                    setSelectedComplaintFilter("ALL");
                    setSelectedAbbWarrantyFilter("ALL");
                    setSelectedProjectFilter("ALL");
                  }}
                  className={`px-3.5 sm:px-4 py-2 rounded-xl transition whitespace-nowrap shrink-0 cursor-pointer flex items-center gap-1.5 ${
                    expenseTab === "GENERAL" ? "bg-rose-600 text-white shadow-xs" : "text-slate-600 hover:text-slate-900"
                  }`}
                >
                  <span>GENERAL</span>
                  <span className={`px-1.5 py-0.2 rounded-md text-[10px] ${
                    expenseTab === "GENERAL" ? "bg-rose-700 text-rose-100" : "bg-rose-100 text-rose-800"
                  }`}>
                    {adminExpenseStats.genCount}
                  </span>
                </button>
              </div>

              {/* Sorting & Search Controls Bar */}
              <div className="flex items-center gap-2 flex-wrap sm:flex-nowrap w-full lg:w-auto">
                {/* SORT BY DROPDOWN */}
                <div className="relative flex items-center shrink-0">
                  <span className="material-symbols-outlined absolute left-2.5 text-slate-400 text-sm pointer-events-none">
                    sort
                  </span>
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    className="rounded-xl border border-slate-300 bg-white pl-8 pr-3 py-2 text-xs font-bold text-slate-800 outline-none focus:border-slate-800 transition cursor-pointer shadow-2xs"
                    title="Sort vouchers"
                  >
                    <option value="date_desc">📅 Date: Newest First</option>
                    <option value="date_asc">📅 Date: Oldest First</option>
                    {userRole !== "engineer" && (
                      <>
                        <option value="engineers">👷 Sort: Engineers (A-Z)</option>
                        <option value="accounts">💼 Sort: Accounts (A-Z)</option>
                      </>
                    )}
                    <option value="complaints">🛠️ Sort: Complaints (A-Z)</option>
                    <option value="project">🏗️ Sort: Projects (A-Z)</option>
                    <option value="amount_desc">💰 Amount: High to Low</option>
                    <option value="amount_asc">💰 Amount: Low to High</option>
                  </select>
                </div>

                {/* Search Bar */}
                <div className="relative w-full sm:w-60">
                  <input
                    type="text"
                    placeholder={userRole === "engineer" ? "Search category, project, complaint..." : "Search employee, category, project..."}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full rounded-xl border border-slate-300 bg-slate-50/70 pl-8 pr-7 py-2 text-xs text-slate-800 outline-none focus:border-slate-800 focus:bg-white transition"
                  />
                  <span className="material-symbols-outlined absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 text-sm">
                    search
                  </span>
                  {searchTerm && (
                    <button
                      onClick={() => setSearchTerm("")}
                      className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 text-xs font-bold cursor-pointer"
                    >
                      ✕
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* ENGINEERS SUB-FILTER & VIEW TOGGLE */}
            {expenseTab === "ENGINEERS" && userRole !== "engineer" && (
              <div className="p-3 sm:p-3.5 rounded-2xl bg-sky-50/80 border border-sky-200 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 animate-fade-in">
                <div className="flex items-center gap-2.5 flex-1 min-w-0">
                  <div className="w-8 h-8 rounded-xl bg-sky-100 text-sky-900 flex items-center justify-center font-bold shrink-0">
                    <span className="material-symbols-outlined text-base">engineering</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <label className="block text-[10px] font-black uppercase tracking-wider text-sky-900 mb-1">
                      Filter by Specific Engineer:
                    </label>
                    <select
                      value={selectedEngineerFilter}
                      onChange={(e) => setSelectedEngineerFilter(e.target.value)}
                      className="w-full rounded-xl border border-sky-300 bg-white px-3 py-2 text-xs font-bold text-slate-800 outline-none focus:border-sky-600 shadow-2xs truncate"
                    >
                      <option value="ALL">
                        -- All Engineers ({engineerOptions.length} engineers • {adminExpenseStats.engCount} entries • {formatCurrency(adminExpenseStats.engTotal)}) --
                      </option>
                      {engineerOptions.map((eng) => (
                        <option key={eng.key} value={eng.key}>
                          {eng.name} ({eng.count} entries • {formatCurrency(eng.totalAmount)})
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* View Mode Toggle */}
                <div className="flex items-center gap-2 self-start sm:self-end shrink-0">
                  <span className="text-[11px] font-bold text-sky-900 uppercase">View:</span>
                  <div className="inline-flex rounded-xl bg-white p-1 border border-sky-200 text-xs font-bold">
                    <button
                      type="button"
                      onClick={() => setExpenseViewMode("table")}
                      className={`px-3 py-1 rounded-lg transition flex items-center gap-1 cursor-pointer ${
                        expenseViewMode === "table"
                          ? "bg-sky-600 text-white shadow-xs"
                          : "text-slate-600 hover:text-slate-900"
                      }`}
                    >
                      <span className="material-symbols-outlined text-sm">table_rows</span>
                      Table View
                    </button>
                    <button
                      type="button"
                      onClick={() => setExpenseViewMode("grouped")}
                      className={`px-3 py-1 rounded-lg transition flex items-center gap-1 cursor-pointer ${
                        expenseViewMode === "grouped"
                          ? "bg-sky-600 text-white shadow-xs"
                          : "text-slate-600 hover:text-slate-900"
                      }`}
                    >
                      <span className="material-symbols-outlined text-sm">unfold_more</span>
                      Grouped View
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* ACCOUNTS SUB-FILTER & VIEW TOGGLE */}
            {expenseTab === "ACCOUNTS" && userRole !== "engineer" && (
              <div className="p-3 sm:p-3.5 rounded-2xl bg-indigo-50/80 border border-indigo-200 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 animate-fade-in">
                <div className="flex items-center gap-2.5 flex-1 min-w-0">
                  <div className="w-8 h-8 rounded-xl bg-indigo-100 text-indigo-900 flex items-center justify-center font-bold shrink-0">
                    <span className="material-symbols-outlined text-base">account_balance_wallet</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <label className="block text-[10px] font-black uppercase tracking-wider text-indigo-900 mb-1">
                      Filter by Specific Accounts Staff:
                    </label>
                    <select
                      value={selectedAccountFilter}
                      onChange={(e) => setSelectedAccountFilter(e.target.value)}
                      className="w-full rounded-xl border border-indigo-300 bg-white px-3 py-2 text-xs font-bold text-slate-800 outline-none focus:border-indigo-600 shadow-2xs truncate"
                    >
                      <option value="ALL">
                        -- All Accounts Staff ({accountOptions.length} users • {adminExpenseStats.accCount} entries • {formatCurrency(adminExpenseStats.accTotal)}) --
                      </option>
                      {accountOptions.map((acc) => (
                        <option key={acc.key} value={acc.key}>
                          {acc.name} ({acc.count} entries • {formatCurrency(acc.totalAmount)})
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* View Mode Toggle */}
                <div className="flex items-center gap-2 self-start sm:self-end shrink-0">
                  <span className="text-[11px] font-bold text-indigo-900 uppercase">View:</span>
                  <div className="inline-flex rounded-xl bg-white p-1 border border-indigo-200 text-xs font-bold">
                    <button
                      type="button"
                      onClick={() => setExpenseViewMode("table")}
                      className={`px-3 py-1 rounded-lg transition flex items-center gap-1 cursor-pointer ${
                        expenseViewMode === "table"
                          ? "bg-indigo-600 text-white shadow-xs"
                          : "text-slate-600 hover:text-slate-900"
                      }`}
                    >
                      <span className="material-symbols-outlined text-sm">table_rows</span>
                      Table View
                    </button>
                    <button
                      type="button"
                      onClick={() => setExpenseViewMode("grouped")}
                      className={`px-3 py-1 rounded-lg transition flex items-center gap-1 cursor-pointer ${
                        expenseViewMode === "grouped"
                          ? "bg-indigo-600 text-white shadow-xs"
                          : "text-slate-600 hover:text-slate-900"
                      }`}
                    >
                      <span className="material-symbols-outlined text-sm">unfold_more</span>
                      Grouped View
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* COMPLAINT SUB-FILTER & VIEW TOGGLE (Company Name & Complaint ID) */}
            {(expenseTab === "COMPLAINTS" || expenseTab === "COMPLAINT") && (
              <div className="p-3 sm:p-3.5 rounded-2xl bg-amber-50/80 border border-amber-200 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 animate-fade-in">
                <div className="flex items-center gap-2.5 flex-1 min-w-0">
                  <div className="w-8 h-8 rounded-xl bg-amber-100 text-amber-900 flex items-center justify-center font-bold shrink-0">
                    <span className="material-symbols-outlined text-base">build</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <label className="block text-[10px] font-black uppercase tracking-wider text-amber-900 mb-1">
                      Filter by Complaint (Company Name & Complaint ID):
                    </label>
                    <select
                      value={selectedComplaintFilter}
                      onChange={(e) => setSelectedComplaintFilter(e.target.value)}
                      className="w-full rounded-xl border border-amber-300 bg-white px-3 py-2 text-xs font-bold text-slate-800 outline-none focus:border-amber-600 shadow-2xs truncate"
                    >
                      <option value="ALL">
                        -- All Complaints ({complaintOptions.length} companies • {vouchers.filter(v => !v.isCancelled && v.referenceType === "complaint").length} entries) --
                      </option>
                      {complaintOptions.map((c) => (
                        <option key={c.key} value={c.key}>
                          {c.company} {c.id ? `— #${c.id}` : ""} ({c.count} entries • {formatCurrency(c.totalAmount)})
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* View Mode Toggle */}
                <div className="flex items-center gap-2 self-start sm:self-end shrink-0">
                  <span className="text-[11px] font-bold text-amber-900 uppercase">View:</span>
                  <div className="inline-flex rounded-xl bg-white p-1 border border-amber-200 text-xs font-bold">
                    <button
                      type="button"
                      onClick={() => setExpenseViewMode("table")}
                      className={`px-3 py-1 rounded-lg transition flex items-center gap-1 cursor-pointer ${
                        expenseViewMode === "table"
                          ? "bg-amber-600 text-white shadow-xs"
                          : "text-slate-600 hover:text-slate-900"
                      }`}
                    >
                      <span className="material-symbols-outlined text-sm">table_rows</span>
                      Table View
                    </button>
                    <button
                      type="button"
                      onClick={() => setExpenseViewMode("grouped")}
                      className={`px-3 py-1 rounded-lg transition flex items-center gap-1 cursor-pointer ${
                        expenseViewMode === "grouped"
                          ? "bg-amber-600 text-white shadow-xs"
                          : "text-slate-600 hover:text-slate-900"
                      }`}
                    >
                      <span className="material-symbols-outlined text-sm">unfold_more</span>
                      Grouped View
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* ABB WARRANTY SUB-FILTER & VIEW TOGGLE (Company Name & Complaint ID) */}
            {(expenseTab === "ABB_WARRANTY" || expenseTab === "ABB_WARRANTY_COMPLAINT") && (
              <div className="p-3 sm:p-3.5 rounded-2xl bg-teal-50/80 border border-teal-200 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 animate-fade-in">
                <div className="flex items-center gap-2.5 flex-1 min-w-0">
                  <div className="w-8 h-8 rounded-xl bg-teal-100 text-teal-900 flex items-center justify-center font-bold shrink-0">
                    <span className="material-symbols-outlined text-base">verified</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <label className="block text-[10px] font-black uppercase tracking-wider text-teal-900 mb-1">
                      Filter by ABB Warranty Complaint:
                    </label>
                    <select
                      value={selectedAbbWarrantyFilter}
                      onChange={(e) => setSelectedAbbWarrantyFilter(e.target.value)}
                      className="w-full rounded-xl border border-teal-300 bg-white px-3 py-2 text-xs font-bold text-slate-800 outline-none focus:border-teal-600 shadow-2xs truncate"
                    >
                      <option value="ALL">
                        -- All ABB Warranty Complaints ({abbWarrantyOptions.length} complaints • {vouchers.filter(v => !v.isCancelled && v.referenceType === "abb_warranty").length} entries) --
                      </option>
                      {abbWarrantyOptions.map((c) => (
                        <option key={c.key} value={c.key}>
                          {c.company} {c.id ? `— #${c.id}` : ""} ({c.count} entries • {formatCurrency(c.totalAmount)})
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* View Mode Toggle */}
                <div className="flex items-center gap-2 self-start sm:self-end shrink-0">
                  <span className="text-[11px] font-bold text-teal-900 uppercase">View:</span>
                  <div className="inline-flex rounded-xl bg-white p-1 border border-teal-200 text-xs font-bold">
                    <button
                      type="button"
                      onClick={() => setExpenseViewMode("table")}
                      className={`px-3 py-1 rounded-lg transition flex items-center gap-1 cursor-pointer ${
                        expenseViewMode === "table"
                          ? "bg-teal-600 text-white shadow-xs"
                          : "text-slate-600 hover:text-slate-900"
                      }`}
                    >
                      <span className="material-symbols-outlined text-sm">table_rows</span>
                      Table View
                    </button>
                    <button
                      type="button"
                      onClick={() => setExpenseViewMode("grouped")}
                      className={`px-3 py-1 rounded-lg transition flex items-center gap-1 cursor-pointer ${
                        expenseViewMode === "grouped"
                          ? "bg-teal-600 text-white shadow-xs"
                          : "text-slate-600 hover:text-slate-900"
                      }`}
                    >
                      <span className="material-symbols-outlined text-sm">unfold_more</span>
                      Grouped View
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* PROJECT SUB-FILTER & VIEW TOGGLE (Company Name & Project Title) */}
            {expenseTab === "PROJECT" && (
              <div className="p-3 sm:p-3.5 rounded-2xl bg-blue-50/80 border border-blue-200 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 animate-fade-in">
                <div className="flex items-center gap-2.5 flex-1 min-w-0">
                  <div className="w-8 h-8 rounded-xl bg-blue-100 text-blue-900 flex items-center justify-center font-bold shrink-0">
                    <span className="material-symbols-outlined text-base">domain</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <label className="block text-[10px] font-black uppercase tracking-wider text-blue-900 mb-1">
                      Filter by Project (Company Name & Project Title):
                    </label>
                    <select
                      value={selectedProjectFilter}
                      onChange={(e) => setSelectedProjectFilter(e.target.value)}
                      className="w-full rounded-xl border border-blue-300 bg-white px-3 py-2 text-xs font-bold text-slate-800 outline-none focus:border-blue-600 shadow-2xs truncate"
                    >
                      <option value="ALL">
                        -- All Projects ({projectOptions.length} projects • {vouchers.filter(v => !v.isCancelled && v.referenceType === "project").length} entries) --
                      </option>
                      {projectOptions.map((p) => (
                        <option key={p.key} value={p.key}>
                          {p.company ? `${p.company} — ` : ""}{p.title} {p.id ? `(${p.id})` : ""} ({p.count} entries • {formatCurrency(p.totalAmount)})
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* View Mode Toggle */}
                <div className="flex items-center gap-2 self-start sm:self-end shrink-0">
                  <span className="text-[11px] font-bold text-blue-900 uppercase">View:</span>
                  <div className="inline-flex rounded-xl bg-white p-1 border border-blue-200 text-xs font-bold">
                    <button
                      type="button"
                      onClick={() => setExpenseViewMode("table")}
                      className={`px-3 py-1 rounded-lg transition flex items-center gap-1 cursor-pointer ${
                        expenseViewMode === "table"
                          ? "bg-blue-600 text-white shadow-xs"
                          : "text-slate-600 hover:text-slate-900"
                      }`}
                    >
                      <span className="material-symbols-outlined text-sm">table_rows</span>
                      Table View
                    </button>
                    <button
                      type="button"
                      onClick={() => setExpenseViewMode("grouped")}
                      className={`px-3 py-1 rounded-lg transition flex items-center gap-1 cursor-pointer ${
                        expenseViewMode === "grouped"
                          ? "bg-blue-600 text-white shadow-xs"
                          : "text-slate-600 hover:text-slate-900"
                      }`}
                    >
                      <span className="material-symbols-outlined text-sm">unfold_more</span>
                      Grouped View
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* DATE FILTERS (Section 10) */}
            <div className="p-3 sm:p-4 rounded-2xl bg-slate-50 border border-slate-200 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3">
              {/* Quick Presets */}
              <div className="flex items-center gap-1.5 flex-wrap text-xs font-bold">
                <span className="text-slate-500 mr-1 text-[11px] uppercase">Filter:</span>
                {[
                  { id: "all", label: "All Time" },
                  { id: "today", label: "Today" },
                  { id: "week", label: "This Week" },
                  { id: "month", label: "This Month" },
                  { id: "year", label: "This Year" }
                ].map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => handleQuickDate(item.id)}
                    className={`px-3 py-1.5 rounded-lg border transition cursor-pointer ${
                      quickDatePreset === item.id
                        ? "bg-slate-900 text-white border-slate-900 shadow-xs"
                        : "bg-white text-slate-700 border-slate-200 hover:bg-slate-100"
                    }`}
                  >
                    {item.label}
                  </button>
                ))}
              </div>

              {/* Custom Date Range */}
              <div className="flex items-center gap-2 text-xs font-bold flex-wrap">
                <span className="text-slate-500 text-[11px] uppercase">From:</span>
                <input
                  type="date"
                  value={dateFilter.from}
                  onChange={(e) => {
                    setQuickDatePreset("custom");
                    setDateFilter((prev) => ({ ...prev, from: e.target.value }));
                  }}
                  className="rounded-lg border border-slate-300 bg-white px-2.5 py-1 text-xs text-slate-800"
                />
                <span className="text-slate-500 text-[11px] uppercase">To:</span>
                <input
                  type="date"
                  value={dateFilter.to}
                  onChange={(e) => {
                    setQuickDatePreset("custom");
                    setDateFilter((prev) => ({ ...prev, to: e.target.value }));
                  }}
                  className="rounded-lg border border-slate-300 bg-white px-2.5 py-1 text-xs text-slate-800"
                />
                {(dateFilter.from || dateFilter.to) && (
                  <button
                    type="button"
                    onClick={() => handleQuickDate("all")}
                    className="text-xs text-rose-600 hover:underline font-bold ml-1 cursor-pointer"
                  >
                    Clear
                  </button>
                )}
              </div>
            </div>

            {/* AUDIT & APPROVAL QUICK SELECT BAR (Admin & Accounts) */}
            {canManageStatus && (
              <div className="p-3 sm:p-3.5 rounded-2xl bg-slate-900 text-white flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 shadow-md animate-fade-in">
                <div className="flex items-center gap-2.5 flex-wrap">
                  <div className="flex items-center gap-1.5 font-black text-xs uppercase tracking-wider text-emerald-400">
                    <span className="material-symbols-outlined text-base">checklist</span>
                    <span>Audit Selection:</span>
                  </div>

                  <div className="flex items-center gap-1.5 flex-wrap text-xs font-bold">
                    <button
                      type="button"
                      onClick={handleToggleSelectAllVisible}
                      className={`px-3 py-1.5 rounded-xl border transition cursor-pointer flex items-center gap-1.5 ${
                        isAllVisibleSelected
                          ? "bg-emerald-600 text-white border-emerald-500 shadow-xs"
                          : "bg-slate-800 text-slate-200 border-slate-700 hover:bg-slate-700"
                      }`}
                    >
                      <span className="material-symbols-outlined text-sm font-bold">
                        {isAllVisibleSelected ? "check_box" : "check_box_outline_blank"}
                      </span>
                      <span>{isAllVisibleSelected ? "Deselect All" : `Select All Visible (${visibleVoucherIds.length})`}</span>
                    </button>

                    {pendingVisibleVoucherIds.length > 0 && (
                      <button
                        type="button"
                        onClick={handleSelectPendingOnly}
                        className="px-3 py-1.5 rounded-xl border bg-amber-500/20 text-amber-300 border-amber-500/40 hover:bg-amber-500/30 transition cursor-pointer flex items-center gap-1.5"
                      >
                        <span className="h-2 w-2 rounded-full bg-amber-400 animate-pulse" />
                        <span>Select Pending ({pendingVisibleVoucherIds.length})</span>
                      </button>
                    )}

                    {selectedVoucherIds.length > 0 && (
                      <button
                        type="button"
                        onClick={handleClearSelection}
                        className="px-2.5 py-1.5 rounded-xl text-slate-400 hover:text-white hover:bg-slate-800 text-xs transition cursor-pointer"
                      >
                        Clear ({selectedVoucherIds.length})
                      </button>
                    )}
                  </div>
                </div>

                {/* Right side: Selected Summary & Direct Approve Button */}
                <div className="flex items-center justify-between sm:justify-end gap-3 pt-2 sm:pt-0 border-t sm:border-t-0 border-slate-800">
                  <div className="text-left sm:text-right">
                    <span className="text-[10px] uppercase font-bold text-slate-400 block">
                      Selected Amount
                    </span>
                    <span className="text-base font-black text-emerald-400">
                      {formatCurrency(selectedTotalAmount)}
                    </span>
                  </div>

                  <button
                    type="button"
                    onClick={() => handleBulkApprove()}
                    disabled={bulkApproving || selectedVoucherIds.length === 0}
                    className="px-4 sm:px-5 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-500 disabled:opacity-40 disabled:hover:bg-emerald-600 text-white text-xs font-black uppercase tracking-wider shadow-md shadow-emerald-950/50 flex items-center gap-2 transition cursor-pointer disabled:cursor-not-allowed"
                  >
                    {bulkApproving ? (
                      <>
                        <span className="h-3.5 w-3.5 rounded-full border-2 border-white border-t-transparent animate-spin" />
                        Approving...
                      </>
                    ) : (
                      <>
                        <span className="material-symbols-outlined text-base font-bold">verified</span>
                        Approve Selected ({selectedVoucherIds.length})
                      </>
                    )}
                  </button>

                  {selectedVoucherIds.length > 0 && (
                    <button
                      type="button"
                      onClick={handleDownloadSelectedPdf}
                      className="px-3.5 py-2 rounded-xl bg-slate-800 hover:bg-slate-700 border border-slate-700 text-emerald-400 text-xs font-black uppercase tracking-wider flex items-center gap-1.5 transition cursor-pointer shadow-xs"
                      title="Download PDF report for all selected vouchers"
                    >
                      <span className="material-symbols-outlined text-base text-emerald-400">picture_as_pdf</span>
                      Download Selected PDF ({selectedVoucherIds.length})
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* EXPENSE RECORDS VIEW (Grouped Dropdown Accordion OR Flat Table) */}

            {(expenseTab === "COMPLAINTS" || expenseTab === "COMPLAINT" || expenseTab === "ABB_WARRANTY" || expenseTab === "ABB_WARRANTY_COMPLAINT" || expenseTab === "PROJECT" || expenseTab === "ENGINEERS" || expenseTab === "ACCOUNTS") && expenseViewMode === "grouped" ? (
              /* ============================================================ */
              /* GROUPED DROPDOWN ACCORDION VIEW                              */
              /* ============================================================ */
              <div className="space-y-4">
                {/* Header with Expand/Collapse All and Counts */}
                {(() => {
                  const isAbbWarrantyTab = expenseTab === "ABB_WARRANTY" || expenseTab === "ABB_WARRANTY_COMPLAINT";
                  const activeGroups =
                    expenseTab === "ENGINEERS"
                      ? groupedFilteredEngineers
                      : expenseTab === "ACCOUNTS"
                      ? groupedFilteredAccounts
                      : (expenseTab === "COMPLAINTS" || expenseTab === "COMPLAINT")
                      ? groupedFilteredComplaints
                      : isAbbWarrantyTab
                      ? groupedFilteredAbbWarranties
                      : groupedFilteredProjects;

                  const groupLabel =
                    expenseTab === "ENGINEERS"
                      ? "engineers"
                      : expenseTab === "ACCOUNTS"
                      ? "accounts staff"
                      : (expenseTab === "COMPLAINTS" || expenseTab === "COMPLAINT")
                      ? "complaints"
                      : isAbbWarrantyTab
                      ? "ABB warranty complaints"
                      : "projects";

                  return (
                    <>
                      <div className="flex items-center justify-between pb-1 text-xs">
                        <span className="text-slate-500 font-semibold">
                          Showing {activeGroups.length} {groupLabel} with recorded expenses
                        </span>
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => expandAllGroups(activeGroups)}
                            className="px-2.5 py-1 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold transition text-[11px] cursor-pointer"
                          >
                            Expand All
                          </button>
                          <button
                            type="button"
                            onClick={collapseAllGroups}
                            className="px-2.5 py-1 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold transition text-[11px] cursor-pointer"
                          >
                            Collapse All
                          </button>
                        </div>
                      </div>

                      {activeGroups.length === 0 ? (
                        <div className="rounded-2xl border border-slate-200 p-12 text-center text-slate-400 bg-slate-50/50">
                          <span className="material-symbols-outlined text-3xl text-slate-300">receipt_long</span>
                          <p className="font-bold text-slate-700 mt-2">No matching expense records found</p>
                          <p className="text-xs text-slate-400 mt-1">
                            Try adjusting the filter dropdown or date criteria above.
                          </p>
                        </div>
                      ) : (
                        activeGroups.map((group) => {
                          const isExpanded = Boolean(expandedGroups[group.key]);
                          const isComplaint = expenseTab === "COMPLAINTS" || expenseTab === "COMPLAINT";
                          const isAbbWarranty = expenseTab === "ABB_WARRANTY" || expenseTab === "ABB_WARRANTY_COMPLAINT";
                          const isEngineer = expenseTab === "ENGINEERS";
                          const isAccount = expenseTab === "ACCOUNTS";

                          const borderColor = isEngineer
                            ? "border-sky-200"
                            : isAccount
                            ? "border-indigo-200"
                            : isAbbWarranty
                            ? "border-teal-200"
                            : isComplaint
                            ? "border-amber-200"
                            : "border-blue-200";

                          const iconBg = isEngineer
                            ? "bg-sky-100 text-sky-800"
                            : isAccount
                            ? "bg-indigo-100 text-indigo-800"
                            : isAbbWarranty
                            ? "bg-teal-100 text-teal-800"
                            : isComplaint
                            ? "bg-amber-100 text-amber-800"
                            : "bg-blue-100 text-blue-800";

                          const headerExpandedBg = isEngineer
                            ? "bg-sky-50/70 border-b border-sky-200"
                            : isAccount
                            ? "bg-indigo-50/70 border-b border-indigo-200"
                            : isAbbWarranty
                            ? "bg-teal-50/70 border-b border-teal-200"
                            : isComplaint
                            ? "bg-amber-50/70 border-b border-amber-200"
                            : "bg-blue-50/70 border-b border-blue-200";

                          const btnColor = isExpanded
                            ? isEngineer
                              ? "bg-sky-600 text-white hover:bg-sky-700"
                              : isAccount
                              ? "bg-indigo-600 text-white hover:bg-indigo-700"
                              : isAbbWarranty
                              ? "bg-teal-600 text-white hover:bg-teal-700"
                              : isComplaint
                              ? "bg-amber-600 text-white hover:bg-amber-700"
                              : "bg-blue-600 text-white hover:bg-blue-700"
                            : "bg-slate-900 text-white hover:bg-slate-800";

                          const iconName = isEngineer
                            ? "engineering"
                            : isAccount
                            ? "account_balance_wallet"
                            : isAbbWarranty
                            ? "verified"
                            : isComplaint
                            ? "build"
                            : "domain";

                          const title = isEngineer || isAccount
                            ? group.name
                            : (isComplaint || isAbbWarranty)
                            ? group.company
                            : (group.company ? `${group.company} — ${group.title}` : group.title);

                          return (
                            <div
                              key={group.key}
                              className={`rounded-2xl border transition shadow-xs overflow-hidden bg-white ${borderColor}`}
                            >
                              {/* Header / Dropdown bar */}
                              <div
                                onClick={() => toggleGroup(group.key)}
                                className={`p-3.5 sm:p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3 cursor-pointer transition select-none ${
                                  isExpanded ? headerExpandedBg : "hover:bg-slate-50/50"
                                }`}
                              >
                                <div className="flex items-center gap-3">
                                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${iconBg}`}>
                                    <span className="material-symbols-outlined text-xl">
                                      {iconName}
                                    </span>
                                  </div>

                                  <div className="min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                      <h3 className="text-sm font-black text-slate-900 break-words">
                                        {title}
                                      </h3>
                                      {group.id && (
                                        <span
                                          className={`px-2 py-0.5 rounded-md font-mono text-xs font-bold border shrink-0 ${
                                            isAbbWarranty
                                              ? "bg-teal-100 text-teal-900 border-teal-300"
                                              : isComplaint
                                              ? "bg-amber-100 text-amber-900 border-amber-300"
                                              : "bg-blue-100 text-blue-900 border-blue-300"
                                          }`}
                                        >
                                          {isComplaint || isAbbWarranty ? `#${group.id}` : group.id}
                                        </span>
                                      )}
                                      {isAbbWarranty && (
                                        <span className="px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider bg-teal-100 text-teal-800">
                                          ABB WARRANTY
                                        </span>
                                      )}
                                      {(isEngineer || isAccount) && (
                                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider ${
                                          isEngineer ? "bg-sky-100 text-sky-800" : "bg-indigo-100 text-indigo-800"
                                        }`}>
                                          {isEngineer ? "ENGINEER" : "ACCOUNTS"}
                                        </span>
                                      )}
                                    </div>
                                    <p className="text-[11px] text-slate-500 mt-0.5 font-medium">
                                      {group.entries.length} expense {group.entries.length === 1 ? "entry" : "entries"} recorded
                                    </p>
                                  </div>
                                </div>

                                <div className="flex items-center justify-between sm:justify-end gap-3 w-full sm:w-auto sm:ml-auto pt-2 sm:pt-0 border-t sm:border-t-0 border-slate-100">
                                  <div className="text-left sm:text-right">
                                    <span className="text-[10px] uppercase font-bold text-slate-400 block">Total Expense</span>
                                    <span className="text-base font-black text-rose-700">
                                      {formatCurrency(group.totalAmount)}
                                    </span>
                                  </div>

                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      toggleGroup(group.key);
                                    }}
                                    className={`px-3 py-1.5 rounded-xl font-bold text-xs flex items-center gap-1.5 transition shadow-xs cursor-pointer shrink-0 ${btnColor}`}
                                  >
                                    <span>{isExpanded ? "Collapse" : "View All Entries"}</span>
                                    <span className="material-symbols-outlined text-sm">
                                      {isExpanded ? "expand_less" : "expand_more"}
                                    </span>
                                  </button>
                                </div>
                              </div>

                              {/* Dropped down entries */}
                              {isExpanded && (
                                <div className="border-t border-slate-100 animate-fade-in">
                                  {/* Mobile Card List (< 768px) */}
                                  <div className="block md:hidden p-3 space-y-2.5 bg-slate-50/60">
                                    {group.entries.map(renderMobileVoucherCard)}
                                  </div>

                                  {/* Desktop Table (>= 768px) */}
                                  <div className="hidden md:block overflow-x-auto bg-white">
                                    <table className="w-full text-left text-xs border-collapse">
                                      <thead>
                                        <tr className="bg-slate-50 text-slate-500 font-bold uppercase tracking-wider text-[10px] border-b border-slate-100">
                                          {canManageStatus && (
                                            <th className="py-3 px-3 w-10 text-center select-none">
                                              <input
                                                type="checkbox"
                                                checked={
                                                  group.entries.filter((e) => !e.isCancelled && (userRole === "admin" || canManageVoucherStatus(e))).length > 0 &&
                                                  group.entries
                                                    .filter((e) => !e.isCancelled && (userRole === "admin" || canManageVoucherStatus(e)))
                                                    .every((e) => selectedVoucherIds.includes(e._id || e.transactionId))
                                                }
                                                onChange={() => {
                                                  const groupIds = group.entries
                                                    .filter((e) => !e.isCancelled && (userRole === "admin" || canManageVoucherStatus(e)))
                                                    .map((e) => e._id || e.transactionId);
                                                  if (groupIds.length === 0) return;
                                                  const allSelected = groupIds.every((id) => selectedVoucherIds.includes(id));
                                                  if (allSelected) {
                                                    setSelectedVoucherIds((prev) => prev.filter((id) => !groupIds.includes(id)));
                                                  } else {
                                                    setSelectedVoucherIds((prev) => Array.from(new Set([...prev, ...groupIds])));
                                                  }
                                                }}
                                                title="Select / Deselect all in this group"
                                                className="h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500 cursor-pointer"
                                              />
                                            </th>
                                          )}
                                          <th className="py-3 px-4">Date</th>
                                          {userRole !== "engineer" && (
                                            <th className="py-3 px-4">Employee / User</th>
                                          )}
                                          <th className="py-3 px-4">Category</th>
                                          <th className="py-3 px-4 text-right">Amount</th>
                                          <th className="py-3 px-4">Description</th>
                                          <th className="py-3 px-4 text-center">Status</th>
                                          <th className="py-3 px-4 text-center">Actions</th>
                                        </tr>
                                      </thead>
                                      <tbody className="divide-y divide-slate-100">
                                        {group.entries.map((v) => (
                                          <tr
                                            key={v._id || v.transactionId}
                                            className={`hover:bg-slate-50/75 transition-colors ${
                                              selectedVoucherIds.includes(v._id || v.transactionId) ? "bg-emerald-50/40" : ""
                                            } ${
                                              v.isCancelled ? "opacity-50 line-through bg-slate-100/50" : ""
                                            }`}
                                          >
                                            {canManageStatus && (
                                              <td className="py-3 px-3 text-center" onClick={(e) => e.stopPropagation()}>
                                                {(userRole === "admin" || canManageVoucherStatus(v)) ? (
                                                  <input
                                                    type="checkbox"
                                                    checked={selectedVoucherIds.includes(v._id || v.transactionId)}
                                                    onChange={() => handleToggleSelect(v._id || v.transactionId)}
                                                    disabled={v.isCancelled}
                                                    className="h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500 cursor-pointer"
                                                  />
                                                ) : (
                                                  <span className="inline-block w-4" />
                                                )}
                                              </td>
                                            )}
                                            <td className="py-3 px-4 font-semibold text-slate-700 whitespace-nowrap">
                                              {formatDateDisplay(v.date)}
                                            </td>
                                            {userRole !== "engineer" && (
                                               <td className="py-3 px-4 whitespace-nowrap">
                                                 <div className="font-bold text-slate-900">
                                                   {v.createdBy?.userName || v.fromUser?.userName || "User"}
                                                 </div>
                                                 <div className="text-[10px] text-slate-400 font-medium">
                                                   {v.createdBy?.userRole || v.fromUser?.role || "user"}
                                                 </div>
                                               </td>
                                             )}
                                            <td className="py-3 px-4 whitespace-nowrap font-semibold text-slate-800">
                                              {v.expenseCategory || "Expense"}
                                            </td>
                                            <td className="py-3 px-4 text-right font-black text-rose-700 whitespace-nowrap">
                                              - {formatCurrency(v.amount)}
                                            </td>
                                            <td className="py-3 px-4 max-w-xs text-slate-600">
                                              <p className="truncate" title={v.description}>
                                                {v.description || "-"}
                                              </p>
                                            </td>
                                            <td className="py-3 px-4 text-center whitespace-nowrap">
                                              {v.isCancelled ? (
                                                <span className="inline-block px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-slate-200 text-slate-600 border border-slate-300">
                                                  Cancelled
                                                </span>
                                              ) : canManageVoucherStatus(v) ? (
                                                <select
                                                  value={v.status === "pending" ? "pending_approval" : (v.status || "pending_approval")}
                                                  disabled={updatingStatusId === (v._id || v.transactionId)}
                                                  onChange={(e) => handleStatusUpdate(v._id || v.transactionId, e.target.value)}
                                                  className={`text-[10px] font-black uppercase tracking-wider px-2 py-1 rounded-full border cursor-pointer outline-none transition shadow-sm font-sans ${
                                                    v.status === "approved" || v.status === "settled"
                                                      ? "bg-emerald-50 text-emerald-800 border-emerald-300 hover:bg-emerald-100"
                                                      : v.status === "rejected"
                                                      ? "bg-rose-50 text-rose-700 border-rose-300 hover:bg-rose-100"
                                                      : "bg-amber-50 text-amber-800 border-amber-300 hover:bg-amber-100"
                                                  }`}
                                                  title="Change voucher status"
                                                >
                                                  <option value="pending_approval" className="bg-white text-amber-800 font-bold">PENDING</option>
                                                  <option value="approved" className="bg-white text-emerald-800 font-bold">APPROVED</option>
                                                  <option value="rejected" className="bg-white text-rose-800 font-bold">REJECTED</option>
                                                </select>
                                              ) : (
                                                <span
                                                  className={`inline-block px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${
                                                    v.status === "approved" || v.status === "settled"
                                                      ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                                                      : v.status === "rejected"
                                                      ? "bg-rose-50 text-rose-700 border border-rose-200"
                                                      : "bg-amber-50 text-amber-700 border border-amber-200"
                                                  }`}
                                                >
                                                  {v.status === "pending_approval" ? "PENDING" : (v.status || "PENDING")}
                                                </span>
                                              )}
                                            </td>
                                            <td className="py-3 px-4 text-center whitespace-nowrap">
                                              <div className="inline-flex items-center gap-1.5">
                                                {/* Quick Approve for Pending */}
                                                {canManageVoucherStatus(v) && !v.isCancelled && v.status !== "approved" && (
                                                  <button
                                                    type="button"
                                                    disabled={updatingStatusId === (v._id || v.transactionId)}
                                                    onClick={() => handleStatusUpdate(v._id || v.transactionId, "approved")}
                                                    title="Approve this voucher"
                                                    className="px-2.5 py-1 rounded-lg bg-emerald-50 hover:bg-emerald-100 text-emerald-800 border border-emerald-300 font-bold transition text-[11px] flex items-center gap-1 cursor-pointer shadow-2xs"
                                                  >
                                                    <span className="material-symbols-outlined text-xs font-bold">check</span>
                                                    Approve
                                                  </button>
                                                )}


                                                {!v.isCancelled && userRole !== "admin" && (
                                                  <>
                                                    <button
                                                      type="button"
                                                      onClick={() => openEditModal(v)}
                                                      title="Edit voucher"
                                                      className="px-2.5 py-1 rounded-lg bg-blue-50 hover:bg-blue-100 text-blue-700 font-bold transition text-[11px] flex items-center gap-0.5 cursor-pointer"
                                                    >
                                                      <span className="material-symbols-outlined text-sm">edit</span>
                                                      Edit
                                                    </button>
                                                    <button
                                                      type="button"
                                                      onClick={() => openVoidModal(v)}
                                                      title="Void / Cancel transaction"
                                                      className="px-2.5 py-1 rounded-lg bg-rose-50 hover:bg-rose-100 text-rose-700 font-bold transition text-[11px] flex items-center gap-0.5 cursor-pointer"
                                                    >
                                                      <span className="material-symbols-outlined text-sm">cancel</span>
                                                      Void
                                                    </button>
                                                  </>
                                                )}
                                              </div>
                                            </td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })
                      )}
                    </>
                  );
                })()}
              </div>
            ) : (
              /* ============================================================ */
              /* STANDARD FLAT VIEW (Mobile Cards + Desktop Table)           */
              /* ============================================================ */
              <div>
                {listLoading && vouchers.length === 0 ? (
                  <div className="rounded-2xl border border-slate-200 p-12 text-center text-slate-400 bg-slate-50/50">
                    <div className="flex flex-col items-center gap-2">
                      <span className="h-6 w-6 rounded-full border-2 border-slate-600 border-t-transparent animate-spin" />
                      <span className="font-semibold text-sm">Loading expenses...</span>
                    </div>
                  </div>
                ) : filteredVouchers.length === 0 ? (
                  <div className="rounded-2xl border border-slate-200 p-12 text-center text-slate-400 bg-slate-50/50">
                    <div className="flex flex-col items-center gap-1.5">
                      <span className="material-symbols-outlined text-3xl text-slate-300">receipt_long</span>
                      <p className="font-bold text-slate-700">No matching expense records found</p>
                      <p className="text-[11px] text-slate-400">
                        Try adjusting the date filters, category tab, or search criteria.
                      </p>
                    </div>
                  </div>
                ) : (
                  <>
                    {/* Mobile Card List (< 768px) */}
                    <div className="block md:hidden space-y-3">
                      {displayedVouchers.map(renderMobileVoucherCard)}
                    </div>

                    {/* Desktop Table (>= 768px) */}
                    <div className="hidden md:block overflow-x-auto rounded-2xl border border-slate-200">
                      <table className="w-full text-left text-xs border-collapse">
                        <thead>
                          <tr className="bg-slate-100/75 text-slate-600 font-bold uppercase tracking-wider text-[11px] border-b border-slate-200">
                            {canManageStatus && (
                              <th className="py-3.5 px-3 w-10 text-center select-none">
                                <input
                                  type="checkbox"
                                  checked={isAllVisibleSelected}
                                  ref={(el) => {
                                    if (el) el.indeterminate = isSomeVisibleSelected;
                                  }}
                                  onChange={handleToggleSelectAllVisible}
                                  title="Select / Deselect all visible vouchers"
                                  className="h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500 cursor-pointer"
                                />
                              </th>
                            )}
                            <th
                              className="py-3.5 px-4 cursor-pointer select-none hover:bg-slate-200/75 transition-colors"
                              onClick={() => setSortBy(sortBy === "date_desc" ? "date_asc" : "date_desc")}
                              title="Sort by Date"
                            >
                              <div className="flex items-center gap-1">
                                Date
                                <span className="material-symbols-outlined text-xs">
                                  {sortBy === "date_desc" ? "arrow_downward" : sortBy === "date_asc" ? "arrow_upward" : "unfold_more"}
                                </span>
                              </div>
                            </th>
                            {userRole !== "engineer" && (
                              <th
                                className="py-3.5 px-4 cursor-pointer select-none hover:bg-slate-200/75 transition-colors"
                                onClick={() => setSortBy(sortBy === "engineers" ? "accounts" : "engineers")}
                                title="Sort by Employee / Role"
                              >
                                <div className="flex items-center gap-1">
                                  Employee / User
                                  <span className="material-symbols-outlined text-xs">
                                    {sortBy === "engineers" || sortBy === "accounts" ? "swap_vert" : "unfold_more"}
                                  </span>
                                </div>
                              </th>
                            )}
                            <th className="py-3.5 px-4">Type</th>
                            <th className="py-3.5 px-4">Category</th>
                            <th
                              className="py-3.5 px-4 cursor-pointer select-none hover:bg-slate-200/75 transition-colors"
                              onClick={() => setSortBy(sortBy === "project" ? "complaints" : "project")}
                              title="Sort by Project / Complaint"
                            >
                              <div className="flex items-center gap-1">
                                Project / Complaint
                                <span className="material-symbols-outlined text-xs">
                                  {sortBy === "project" || sortBy === "complaints" ? "swap_vert" : "unfold_more"}
                                </span>
                              </div>
                            </th>
                            <th
                              className="py-3.5 px-4 text-right cursor-pointer select-none hover:bg-slate-200/75 transition-colors"
                              onClick={() => setSortBy(sortBy === "amount_desc" ? "amount_asc" : "amount_desc")}
                              title="Sort by Amount"
                            >
                              <div className="flex items-center justify-end gap-1">
                                Amount
                                <span className="material-symbols-outlined text-xs">
                                  {sortBy === "amount_desc" ? "arrow_downward" : sortBy === "amount_asc" ? "arrow_upward" : "unfold_more"}
                                </span>
                              </div>
                            </th>
                            <th className="py-3.5 px-4">Description</th>
                            <th className="py-3.5 px-4 text-center">Status</th>
                            <th className="py-3.5 px-4 text-center">Actions</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                          {displayedVouchers.map((v) => {
                            const isCredit = v.transactionType === "CREDIT" || v.transactionType === "ADVANCE";
                            const displayType = isCredit ? "CREDIT" : "DEBIT";

                            return (
                              <tr
                                key={v._id || v.transactionId}
                                className={`hover:bg-slate-50/75 transition-colors ${
                                  selectedVoucherIds.includes(v._id || v.transactionId) ? "bg-emerald-50/40" : ""
                                } ${
                                  v.isCancelled ? "opacity-50 line-through bg-slate-100/50" : ""
                                }`}
                              >
                                {canManageStatus && (
                                  <td className="py-3.5 px-3 text-center" onClick={(e) => e.stopPropagation()}>
                                    {(userRole === "admin" || canManageVoucherStatus(v)) ? (
                                      <input
                                        type="checkbox"
                                        checked={selectedVoucherIds.includes(v._id || v.transactionId)}
                                        onChange={() => handleToggleSelect(v._id || v.transactionId)}
                                        disabled={v.isCancelled}
                                        className="h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500 cursor-pointer"
                                      />
                                    ) : (
                                      <span className="inline-block w-4" />
                                    )}
                                  </td>
                                )}
                                {/* Date */}
                                <td className="py-3.5 px-4 font-semibold text-slate-700 whitespace-nowrap">
                                  {formatDateDisplay(v.date)}
                                </td>


                                {/* Employee / User */}
                                 {userRole !== "engineer" && (
                                   <td className="py-3.5 px-4 whitespace-nowrap">
                                     <div className="font-bold text-slate-900">
                                       {v.createdBy?.userName || v.fromUser?.userName || "User"}
                                     </div>
                                     <div className="text-[10px] text-slate-400 font-medium">
                                       {v.createdBy?.userRole || v.fromUser?.role || "user"}
                                     </div>
                                   </td>
                                 )}

                                {/* Type */}
                                <td className="py-3.5 px-4 whitespace-nowrap">
                                  <span
                                    className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider ${
                                      displayType === "CREDIT"
                                        ? "bg-emerald-100 text-emerald-800 border border-emerald-200"
                                        : "bg-rose-100 text-rose-800 border border-rose-200"
                                    }`}
                                  >
                                    <span className="material-symbols-outlined text-xs">
                                      {displayType === "CREDIT" ? "arrow_downward" : "arrow_upward"}
                                    </span>
                                    {displayType}
                                  </span>
                                </td>

                                {/* Category */}
                                <td className="py-3.5 px-4 whitespace-nowrap">
                                  <span className="font-semibold text-slate-800">
                                    {displayType === "CREDIT"
                                      ? `From: ${v.fromUser?.userName || "Admin"}`
                                      : v.expenseCategory || "General Expense"}
                                  </span>
                                </td>

                                {/* Project / Complaint */}
                                <td className="py-3.5 px-4 whitespace-nowrap">
                                  {v.referenceType === "project" ? (
                                    (() => {
                                      const prj = getProjectInfo(v);
                                      return (
                                        <div className="max-w-xs space-y-0.5">
                                          <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-blue-50 text-blue-800 border border-blue-200 font-bold text-[11px]">
                                            <span className="material-symbols-outlined text-xs">domain</span>
                                            <span className="truncate">{prj.title}</span>
                                          </div>
                                          {(prj.company || prj.id) && (
                                            <div className="text-[10px] text-slate-500 font-medium flex items-center gap-1.5 flex-wrap">
                                              {prj.company && (
                                                <span className="font-semibold text-slate-700">{prj.company}</span>
                                              )}
                                              {prj.id && (
                                                <span className="font-mono text-blue-700 bg-blue-100/60 px-1 rounded font-bold">
                                                  {prj.id}
                                                </span>
                                              )}
                                            </div>
                                          )}
                                        </div>
                                      );
                                    })()
                                  ) : v.referenceType === "abb_warranty" ? (
                                    (() => {
                                      const cmp = getComplaintInfo(v);
                                      return (
                                        <div className="max-w-xs space-y-0.5">
                                          <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-teal-50 text-teal-900 border border-teal-200 font-bold text-[11px]">
                                            <span className="material-symbols-outlined text-xs text-teal-700">verified</span>
                                            <span className="truncate">{v.referenceName || v.customerName || cmp.company || "ABB Warranty"}</span>
                                          </div>
                                          <div className="text-[10px] font-mono font-bold text-teal-800 flex items-center gap-1">
                                            <span>#{v.referenceId || cmp.id}</span>
                                            <span className="bg-teal-100 text-teal-800 px-1 rounded text-[9px] font-bold">ABB WARRANTY</span>
                                          </div>
                                        </div>
                                      );
                                    })()
                                  ) : v.referenceType === "complaint" ? (
                                    (() => {
                                      const cmp = getComplaintInfo(v);
                                      return (
                                        <div className="max-w-xs space-y-0.5">
                                          <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-amber-50 text-amber-900 border border-amber-200 font-bold text-[11px]">
                                            <span className="material-symbols-outlined text-xs">build</span>
                                            <span className="truncate">{cmp.company}</span>
                                          </div>
                                          {cmp.id && (
                                            <div className="text-[10px] font-mono font-bold text-amber-800">
                                              #{cmp.id}
                                            </div>
                                          )}
                                        </div>
                                      );
                                    })()
                                  ) : (
                                    <span className="text-slate-400 italic">General</span>
                                  )}
                                </td>

                                {/* Amount */}
                                <td
                                  className={`py-3.5 px-4 text-right font-black whitespace-nowrap ${
                                    displayType === "CREDIT" ? "text-emerald-700" : "text-rose-700"
                                  }`}
                                >
                                  {displayType === "CREDIT" ? "+" : "-"} {formatCurrency(v.amount)}
                                </td>

                                {/* Description */}
                                <td className="py-3.5 px-4 max-w-xs text-slate-600">
                                  <p className="truncate" title={v.description}>
                                    {v.description || "-"}
                                  </p>
                                </td>

                                {/* Status */}
                                <td className="py-3.5 px-4 text-center whitespace-nowrap">
                                  {v.isCancelled ? (
                                    <span className="inline-block px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-slate-200 text-slate-600 border border-slate-300">
                                      Cancelled
                                    </span>
                                  ) : canManageVoucherStatus(v) ? (
                                    <select
                                      value={v.status === "pending" ? "pending_approval" : (v.status || "pending_approval")}
                                      disabled={updatingStatusId === (v._id || v.transactionId)}
                                      onChange={(e) => handleStatusUpdate(v._id || v.transactionId, e.target.value)}
                                      className={`text-[10px] font-black uppercase tracking-wider px-2 py-1 rounded-full border cursor-pointer outline-none transition shadow-sm font-sans ${
                                        v.status === "approved" || v.status === "settled"
                                          ? "bg-emerald-50 text-emerald-700 border-emerald-300 hover:bg-emerald-100"
                                          : v.status === "rejected"
                                          ? "bg-rose-50 text-rose-700 border-rose-300 hover:bg-rose-100"
                                          : "bg-amber-50 text-amber-800 border-amber-300 hover:bg-amber-100"
                                      }`}
                                      title="Change voucher status"
                                    >
                                      <option value="pending_approval" className="bg-white text-amber-800 font-bold">
                                        PENDING
                                      </option>
                                      <option value="approved" className="bg-white text-emerald-800 font-bold">
                                        APPROVED
                                      </option>
                                      <option value="rejected" className="bg-white text-rose-800 font-bold">
                                        REJECTED
                                      </option>
                                    </select>
                                  ) : (
                                    <span
                                      className={`inline-block px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${
                                        v.status === "approved" || v.status === "settled"
                                          ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                                          : v.status === "rejected"
                                          ? "bg-rose-50 text-rose-700 border border-rose-200"
                                          : "bg-amber-50 text-amber-700 border border-amber-200"
                                      }`}
                                    >
                                      {v.status === "pending_approval" ? "PENDING" : (v.status || "PENDING")}
                                    </span>
                                  )}
                                </td>

                                {/* Actions */}
                                 <td className="py-3.5 px-4 text-center whitespace-nowrap">
                                   <div className="inline-flex items-center gap-1.5">
                                     {/* Quick Approve for Pending */}
                                     {canManageVoucherStatus(v) && !v.isCancelled && v.status !== "approved" && (
                                       <button
                                         type="button"
                                         disabled={updatingStatusId === (v._id || v.transactionId)}
                                         onClick={() => handleStatusUpdate(v._id || v.transactionId, "approved")}
                                         title="Quick Approve this voucher"
                                         className="px-2.5 py-1 rounded-lg bg-emerald-50 hover:bg-emerald-100 text-emerald-800 border border-emerald-300 font-bold transition text-[11px] flex items-center gap-1 cursor-pointer shadow-2xs"
                                       >
                                         <span className="material-symbols-outlined text-xs font-bold">check</span>
                                         Approve
                                       </button>
                                     )}


                                     {!v.isCancelled && userRole !== "admin" && (
                                       <>
                                         {/* Edit */}
                                         <button
                                           type="button"
                                           onClick={() => openEditModal(v)}
                                           title="Edit voucher"
                                           className="px-2.5 py-1 rounded-lg bg-blue-50 hover:bg-blue-100 text-blue-700 font-bold transition text-[11px] flex items-center gap-0.5 cursor-pointer"
                                         >
                                           <span className="material-symbols-outlined text-sm">edit</span>
                                           Edit
                                         </button>

                                         {/* Void / Cancel */}
                                         <button
                                           type="button"
                                           onClick={() => openVoidModal(v)}
                                           title="Void / Cancel transaction"
                                           className="px-2.5 py-1 rounded-lg bg-rose-50 hover:bg-rose-100 text-rose-700 font-bold transition text-[11px] flex items-center gap-0.5 cursor-pointer"
                                         >
                                           <span className="material-symbols-outlined text-sm">cancel</span>
                                           Void
                                         </button>
                                       </>
                                     )}
                                   </div>
                                 </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>

                    {/* Pagination / View More for Vouchers */}
                    {filteredVouchers.length > voucherLimit && (
                      <div className="mt-4 flex flex-col sm:flex-row items-center justify-between gap-3 p-4 bg-slate-50 rounded-2xl border border-slate-200">
                        <div className="text-xs font-semibold text-slate-500 flex items-center gap-1.5">
                          <span className="material-symbols-outlined text-base text-slate-400">format_list_numbered</span>
                          Showing <span className="font-bold text-slate-800">{displayedVouchers.length}</span> of <span className="font-bold text-slate-800">{filteredVouchers.length}</span> entries
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => setVoucherLimit((prev) => prev + 20)}
                            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold shadow-xs transition cursor-pointer"
                          >
                            <span className="material-symbols-outlined text-sm font-bold">expand_more</span>
                            View More (+20)
                          </button>
                          <button
                            type="button"
                            onClick={() => setVoucherLimit(filteredVouchers.length)}
                            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-slate-200 hover:bg-slate-300 text-slate-700 text-xs font-bold transition cursor-pointer"
                          >
                            View All ({filteredVouchers.length})
                          </button>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        )}

        {/* ============================================================ */}
        {/* TAB 3: PROJECT MANAGEMENT SECTION (Section 6 & 12)           */}
        {/* ============================================================ */}
        {activeMainTab === "projects" && userRole !== "accounts" && (
          <div className="rounded-3xl border border-slate-200 bg-white p-6 sm:p-8 shadow-sm space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 border-b border-slate-100 pb-5">
              <div>
                <h2 className="text-2xl font-black text-slate-900 tracking-tight">
                  PROJECT MANAGEMENT & EXPENSES
                </h2>
                <p className="text-xs text-slate-500 mt-0.5">
                  Track project-specific expense histories, category breakdowns, and create new projects.
                </p>
              </div>

              <button
                type="button"
                onClick={() => setShowProjectModal(true)}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-2xl bg-blue-600 hover:bg-blue-700 text-white font-black text-xs uppercase tracking-wider shadow-md shadow-blue-600/25 transition"
              >
                <span className="material-symbols-outlined text-lg font-bold">add</span>
                [ + Create Project ]
              </button>
            </div>

            {/* Projects Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {projects.length === 0 ? (
                <div className="col-span-full py-12 text-center text-slate-400">
                  <span className="material-symbols-outlined text-4xl text-slate-300">domain_disabled</span>
                  <p className="font-bold text-slate-700 mt-2">No projects found</p>
                  <p className="text-xs text-slate-400">
                    Click [+ Create Project] above to start tracking project expenses.
                  </p>
                </div>
              ) : (
                projects.map((p) => {
                  const pId = p.estimateNo || p._id;
                  const pExpenses = vouchers.filter(
                    (v) =>
                      !v.isCancelled &&
                      v.referenceType === "project" &&
                      (v.referenceId === pId || (p.projectName && v.referenceName === p.projectName))
                  );
                  const totalSpent = pExpenses.reduce((sum, x) => sum + Number(x.amount || 0), 0);

                  return (
                    <div
                      key={p._id || p.estimateNo}
                      className="rounded-2xl border border-slate-200 bg-slate-50/50 p-5 hover:border-blue-300 hover:shadow-md transition space-y-3"
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <span className="text-[10px] font-mono font-bold text-blue-700 bg-blue-50 px-2 py-0.5 rounded-md">
                            {p.estimateNo || "PROJ"}
                          </span>
                          <h3 className="text-base font-black text-slate-900 mt-1">
                            {p.projectName}
                          </h3>
                        </div>
                        <span className="text-[10px] uppercase font-bold px-2 py-0.5 rounded-full bg-slate-200 text-slate-700">
                          {p.status || "active"}
                        </span>
                      </div>

                      <div className="text-xs text-slate-600 space-y-1">
                        {p.customerName && (
                          <p className="truncate">
                            <strong className="text-slate-500">Client:</strong> {p.customerName}
                          </p>
                        )}
                        {p.location && (
                          <p className="truncate">
                            <strong className="text-slate-500">Location:</strong> {p.location}
                          </p>
                        )}
                        <p className="text-slate-400 text-[11px]">
                          Start: {formatDateDisplay(p.estimateDate || p.createdAt)}
                        </p>
                      </div>

                      {/* Financial summary for this project */}
                      <div className="pt-3 border-t border-slate-200 flex items-center justify-between">
                        <div>
                          <span className="text-[10px] uppercase font-bold text-slate-400 block">Total Spent</span>
                          <span className="text-base font-black text-rose-700">{formatCurrency(totalSpent)}</span>
                        </div>
                        <button
                          type="button"
                          onClick={() => setSelectedProjectForSummary(p)}
                          className="px-3 py-1.5 rounded-xl bg-slate-900 hover:bg-slate-800 text-white font-bold text-xs transition"
                        >
                          View Summary
                        </button>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        )}

        {/* ============================================================ */}
        {/* TAB 4: REPORTS & DASHBOARD (Sections 11–15)                  */}
        {/* ============================================================ */}
        {activeMainTab === "reports" && (
          <div className="rounded-3xl border border-slate-200 bg-white p-6 sm:p-8 shadow-sm space-y-6">
            <div className="border-b border-slate-100 pb-4">
              <h2 className="text-2xl font-black text-slate-900 tracking-tight">
                FINANCIAL REPORTS & ANALYTICS
              </h2>
              <p className="text-xs text-slate-500 mt-0.5">
                Segregated insights across General, Project, Complaint, Engineer, and Accounts expenses.
              </p>
            </div>

            {/* Sub-Tabs for Reports */}
            <div className="flex flex-wrap items-center gap-2 border-b border-slate-100 pb-3">
              {[
                { id: "dashboard", label: "Overall Dashboard" },
                { id: "engineer", label: "Engineer-wise Expense" },
                { id: "project", label: "Project-wise Report" },
                { id: "complaint", label: "Complaint-wise Report" },
                { id: "abb_warranty", label: "ABB Warranty Report" },
                { id: "general", label: "General Expense Report" }
              ].map((sub) => (
                <button
                  key={sub.id}
                  type="button"
                  onClick={() => setReportSubTab(sub.id)}
                  className={`px-3.5 py-1.5 rounded-xl font-bold text-xs transition ${
                    reportSubTab === sub.id
                      ? "bg-slate-900 text-white shadow-xs"
                      : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                  }`}
                >
                  {sub.label}
                </button>
              ))}
            </div>

            {/* 1. OVERALL EXPENSE DASHBOARD (Section 15) */}
            {reportSubTab === "dashboard" && (
              <div className="space-y-6 animate-fade-in">
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-3">
                  <div className="rounded-2xl border border-emerald-200 bg-emerald-50/60 p-4">
                    <span className="text-[10px] font-bold uppercase text-emerald-800 block">Total Money Received</span>
                    <span className="text-xl font-black text-emerald-700 mt-1 block">
                      {formatCurrency(balanceSummary.totalCredit)}
                    </span>
                  </div>

                  <div className="rounded-2xl border border-rose-200 bg-rose-50/60 p-4">
                    <span className="text-[10px] font-bold uppercase text-rose-800 block">General Expense</span>
                    <span className="text-xl font-black text-rose-700 mt-1 block">
                      {formatCurrency(balanceSummary.totalGeneralExpense)}
                    </span>
                  </div>

                  <div className="rounded-2xl border border-blue-200 bg-blue-50/60 p-4">
                    <span className="text-[10px] font-bold uppercase text-blue-800 block">Project Expense</span>
                    <span className="text-xl font-black text-blue-700 mt-1 block">
                      {formatCurrency(balanceSummary.totalProjectExpense)}
                    </span>
                  </div>

                  <div className="rounded-2xl border border-amber-200 bg-amber-50/60 p-4">
                    <span className="text-[10px] font-bold uppercase text-amber-800 block">Complaint Expense</span>
                    <span className="text-xl font-black text-amber-700 mt-1 block">
                      {formatCurrency(balanceSummary.totalComplaintExpense)}
                    </span>
                  </div>

                  <div className="rounded-2xl border border-teal-200 bg-teal-50/60 p-4">
                    <span className="text-[10px] font-bold uppercase text-teal-800 block">ABB Warranty Expense</span>
                    <span className="text-xl font-black text-teal-700 mt-1 block">
                      {formatCurrency(balanceSummary.totalAbbWarrantyExpense || 0)}
                    </span>
                  </div>

                  <div className="rounded-2xl border border-slate-300 bg-slate-100 p-4">
                    <span className="text-[10px] font-bold uppercase text-slate-700 block">Total Expense</span>
                    <span className="text-xl font-black text-slate-900 mt-1 block">
                      {formatCurrency(balanceSummary.totalExpense)}
                    </span>
                  </div>

                  <div className="rounded-2xl border border-indigo-200 bg-indigo-50/60 p-4">
                    <span className="text-[10px] font-bold uppercase text-indigo-800 block">Total Balance</span>
                    <span className="text-xl font-black text-indigo-700 mt-1 block">
                      {formatCurrency(balanceSummary.availableBalance)}
                    </span>
                  </div>
                </div>

                {/* Quick breakdown visual */}
                <div className="p-5 rounded-2xl border border-slate-200 bg-slate-50">
                  <h4 className="text-xs font-black uppercase tracking-wider text-slate-700 mb-3">
                    Expense Segregation Overview
                  </h4>
                  <div className="h-4 rounded-full bg-slate-200 flex overflow-hidden">
                    {balanceSummary.totalExpense > 0 && (
                      <>
                        <div
                          style={{
                            width: `${(balanceSummary.totalGeneralExpense / balanceSummary.totalExpense) * 100}%`
                          }}
                          className="bg-rose-500 hover:opacity-90 transition-all"
                          title={`General: ${formatCurrency(balanceSummary.totalGeneralExpense)}`}
                        />
                        <div
                          style={{
                            width: `${(balanceSummary.totalProjectExpense / balanceSummary.totalExpense) * 100}%`
                          }}
                          className="bg-blue-500 hover:opacity-90 transition-all"
                          title={`Project: ${formatCurrency(balanceSummary.totalProjectExpense)}`}
                        />
                        <div
                          style={{
                            width: `${(balanceSummary.totalComplaintExpense / balanceSummary.totalExpense) * 100}%`
                          }}
                          className="bg-amber-500 hover:opacity-90 transition-all"
                          title={`Complaint: ${formatCurrency(balanceSummary.totalComplaintExpense)}`}
                        />
                        <div
                          style={{
                            width: `${((balanceSummary.totalAbbWarrantyExpense || 0) / balanceSummary.totalExpense) * 100}%`
                          }}
                          className="bg-teal-500 hover:opacity-90 transition-all"
                          title={`ABB Warranty: ${formatCurrency(balanceSummary.totalAbbWarrantyExpense || 0)}`}
                        />
                      </>
                    )}
                  </div>
                  <div className="flex flex-wrap items-center gap-6 mt-3 text-xs">
                    <span className="inline-flex items-center gap-1.5 font-bold text-slate-700">
                      <span className="h-3 w-3 rounded-full bg-rose-500" />
                      General ({formatCurrency(balanceSummary.totalGeneralExpense)})
                    </span>
                    <span className="inline-flex items-center gap-1.5 font-bold text-slate-700">
                      <span className="h-3 w-3 rounded-full bg-blue-500" />
                      Project ({formatCurrency(balanceSummary.totalProjectExpense)})
                    </span>
                    <span className="inline-flex items-center gap-1.5 font-bold text-slate-700">
                      <span className="h-3 w-3 rounded-full bg-amber-500" />
                      Complaint ({formatCurrency(balanceSummary.totalComplaintExpense)})
                    </span>
                    <span className="inline-flex items-center gap-1.5 font-bold text-slate-700">
                      <span className="h-3 w-3 rounded-full bg-teal-500" />
                      ABB Warranty ({formatCurrency(balanceSummary.totalAbbWarrantyExpense || 0)})
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* 2. ENGINEER-WISE EXPENSE REPORT (Section 11) */}
            {reportSubTab === "engineer" && (
              <div className="space-y-5 animate-fade-in">
                <div className="max-w-md">
                  <label className="block text-xs font-bold uppercase tracking-wider text-slate-700 mb-1.5">
                    Select Engineer
                  </label>
                  <select
                    value={selectedEngineerForReport}
                    onChange={(e) => setSelectedEngineerForReport(e.target.value)}
                    className="w-full rounded-xl border border-slate-300 bg-white px-3.5 py-2.5 text-sm font-semibold text-slate-800 outline-none focus:border-slate-800"
                  >
                    <option value="">-- Choose an Engineer --</option>
                    {engineers.map((eng) => (
                      <option key={eng._id || eng.engineerId} value={eng.engineerId || eng._id}>
                        {eng.name} ({eng.engineerId || "ENG"})
                      </option>
                    ))}
                  </select>
                </div>

                {engineerReportDetails ? (
                  <div className="space-y-5">
                    {/* Engineer KPI Cards */}
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-3">
                      <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-3.5">
                        <span className="text-[10px] font-bold text-emerald-800 uppercase block">Total Credit</span>
                        <span className="text-lg font-black text-emerald-700 mt-1 block">
                          {formatCurrency(engineerReportDetails.totalCredit)}
                        </span>
                      </div>

                      <div className="rounded-xl border border-rose-200 bg-rose-50 p-3.5">
                        <span className="text-[10px] font-bold text-rose-800 uppercase block">General Expense</span>
                        <span className="text-lg font-black text-rose-700 mt-1 block">
                          {formatCurrency(engineerReportDetails.generalExpense)}
                        </span>
                      </div>

                      <div className="rounded-xl border border-blue-200 bg-blue-50 p-3.5">
                        <span className="text-[10px] font-bold text-blue-800 uppercase block">Project Expense</span>
                        <span className="text-lg font-black text-blue-700 mt-1 block">
                          {formatCurrency(engineerReportDetails.projectExpense)}
                        </span>
                      </div>

                      <div className="rounded-xl border border-amber-200 bg-amber-50 p-3.5">
                        <span className="text-[10px] font-bold text-amber-800 uppercase block">Complaint Expense</span>
                        <span className="text-lg font-black text-amber-700 mt-1 block">
                          {formatCurrency(engineerReportDetails.complaintExpense)}
                        </span>
                      </div>

                      <div className="rounded-xl border border-teal-200 bg-teal-50 p-3.5">
                        <span className="text-[10px] font-bold text-teal-800 uppercase block">ABB Warranty</span>
                        <span className="text-lg font-black text-teal-700 mt-1 block">
                          {formatCurrency(engineerReportDetails.abbWarrantyExpense || 0)}
                        </span>
                      </div>

                      <div className="rounded-xl border border-slate-300 bg-slate-100 p-3.5">
                        <span className="text-[10px] font-bold text-slate-700 uppercase block">Total Expense</span>
                        <span className="text-lg font-black text-slate-900 mt-1 block">
                          {formatCurrency(engineerReportDetails.totalExpense)}
                        </span>
                      </div>

                      <div className="rounded-xl border border-indigo-200 bg-indigo-50 p-3.5">
                        <span className="text-[10px] font-bold text-indigo-800 uppercase block">Balance</span>
                        <span
                          className={`text-lg font-black mt-1 block ${
                            engineerReportDetails.balance >= 0 ? "text-indigo-700" : "text-rose-700"
                          }`}
                        >
                          {formatCurrency(engineerReportDetails.balance)}
                        </span>
                      </div>
                    </div>

                    {/* Breakdown tables */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 text-xs">
                      {/* General Expense Breakdown */}
                      <div className="rounded-2xl border border-slate-200 p-4 bg-slate-50">
                        <h4 className="font-bold text-slate-800 uppercase mb-2">General Breakdown</h4>
                        {Object.keys(engineerReportDetails.generalBreakdown).length === 0 ? (
                          <p className="text-slate-400 italic">No general expenses</p>
                        ) : (
                          <div className="space-y-1.5">
                            {Object.entries(engineerReportDetails.generalBreakdown).map(([cat, amt]) => (
                              <div key={cat} className="flex justify-between">
                                <span className="text-slate-600">{cat}:</span>
                                <span className="font-bold">{formatCurrency(amt)}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* Project Breakdown */}
                      <div className="rounded-2xl border border-slate-200 p-4 bg-slate-50">
                        <h4 className="font-bold text-slate-800 uppercase mb-2">Project Breakdown</h4>
                        {Object.keys(engineerReportDetails.projectBreakdown).length === 0 ? (
                          <p className="text-slate-400 italic">No project expenses</p>
                        ) : (
                          <div className="space-y-1.5">
                            {Object.entries(engineerReportDetails.projectBreakdown).map(([proj, amt]) => (
                              <div key={proj} className="flex justify-between">
                                <span className="text-slate-600 truncate max-w-[120px]">{proj}:</span>
                                <span className="font-bold">{formatCurrency(amt)}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* Complaint Breakdown */}
                      <div className="rounded-2xl border border-slate-200 p-4 bg-slate-50">
                        <h4 className="font-bold text-slate-800 uppercase mb-2">Complaint Breakdown</h4>
                        {Object.keys(engineerReportDetails.complaintBreakdown).length === 0 ? (
                          <p className="text-slate-400 italic">No complaint expenses</p>
                        ) : (
                          <div className="space-y-1.5">
                            {Object.entries(engineerReportDetails.complaintBreakdown).map(([cmp, amt]) => (
                              <div key={cmp} className="flex justify-between">
                                <span className="text-slate-600 truncate max-w-[120px]">#{cmp}:</span>
                                <span className="font-bold">{formatCurrency(amt)}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* ABB Warranty Breakdown */}
                      <div className="rounded-2xl border border-teal-200 p-4 bg-teal-50/40">
                        <h4 className="font-bold text-teal-800 uppercase mb-2">ABB Warranty Breakdown</h4>
                        {Object.keys(engineerReportDetails.abbWarrantyBreakdown || {}).length === 0 ? (
                          <p className="text-slate-400 italic">No ABB warranty expenses</p>
                        ) : (
                          <div className="space-y-1.5">
                            {Object.entries(engineerReportDetails.abbWarrantyBreakdown).map(([abb, amt]) => (
                              <div key={abb} className="flex justify-between">
                                <span className="text-slate-600 truncate max-w-[120px]">#{abb}:</span>
                                <span className="font-bold text-teal-900">{formatCurrency(amt)}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ) : (
                  <p className="text-xs text-slate-400 italic">Select an engineer above to view the summary.</p>
                )}
              </div>
            )}

            {/* 3. PROJECT-WISE REPORT (Section 12) */}
            {reportSubTab === "project" && (
              <div className="space-y-5 animate-fade-in">
                <div className="max-w-md">
                  <label className="block text-xs font-bold uppercase tracking-wider text-slate-700 mb-1.5">
                    Select Project
                  </label>
                  <select
                    value={selectedProjectForReport}
                    onChange={(e) => setSelectedProjectForReport(e.target.value)}
                    className="w-full rounded-xl border border-slate-300 bg-white px-3.5 py-2.5 text-sm font-semibold text-slate-800 outline-none focus:border-slate-800"
                  >
                    <option value="">-- Choose a Project --</option>
                    {projects.map((proj) => (
                      <option key={proj._id || proj.estimateNo} value={proj.estimateNo || proj._id}>
                        {proj.projectName} ({proj.estimateNo || "Project"})
                      </option>
                    ))}
                  </select>
                </div>

                {selectedProjectForReport ? (
                  (() => {
                    const prj = projects.find(
                      (p) => p.estimateNo === selectedProjectForReport || p._id === selectedProjectForReport
                    );
                    const prjId = prj?.estimateNo || prj?._id || selectedProjectForReport;
                    const prjExpenses = vouchers.filter(
                      (v) =>
                        !v.isCancelled &&
                        v.referenceType === "project" &&
                        (v.referenceId === prjId || (prj?.projectName && v.referenceName === prj.projectName))
                    );
                    const totalSpent = prjExpenses.reduce((sum, x) => sum + Number(x.amount || 0), 0);
                    const catBreakdown = {};
                    prjExpenses.forEach((x) => {
                      const c = x.expenseCategory || "Other";
                      catBreakdown[c] = (catBreakdown[c] || 0) + Number(x.amount || 0);
                    });

                    return (
                      <div className="space-y-5">
                        {/* Project Header */}
                        <div className="p-4 rounded-2xl bg-blue-50/60 border border-blue-200 flex flex-wrap items-center justify-between gap-4 text-xs">
                          <div>
                            <span className="font-mono text-blue-700 font-bold">{prj?.estimateNo}</span>
                            <h3 className="text-base font-black text-slate-900">{prj?.projectName}</h3>
                            <p className="text-slate-500">Client: {prj?.customerName || "N/A"}</p>
                          </div>
                          <div>
                            <span className="text-slate-500 uppercase block font-bold text-[10px]">Total Project Expense</span>
                            <span className="text-2xl font-black text-blue-900">{formatCurrency(totalSpent)}</span>
                          </div>
                        </div>

                        {/* Category breakdown */}
                        <div className="p-4 rounded-2xl border border-slate-200 bg-slate-50">
                          <h4 className="text-xs font-bold text-slate-800 uppercase mb-2">Category Breakdown</h4>
                          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
                            {Object.entries(catBreakdown).map(([cat, amt]) => (
                              <div key={cat} className="p-2.5 rounded-xl bg-white border border-slate-200">
                                <span className="text-slate-500 block text-[11px] font-semibold">{cat}</span>
                                <span className="text-sm font-black text-slate-900">{formatCurrency(amt)}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    );
                  })()
                ) : (
                  <p className="text-xs text-slate-400 italic">Select a project above to inspect expense details.</p>
                )}
              </div>
            )}

            {/* 4. COMPLAINT-WISE REPORT (Section 13) */}
            {reportSubTab === "complaint" && (
              <div className="space-y-5 animate-fade-in">
                <div className="max-w-md">
                  <label className="block text-xs font-bold uppercase tracking-wider text-slate-700 mb-1.5">
                    Select Complaint
                  </label>
                  <select
                    value={selectedComplaintForReport}
                    onChange={(e) => setSelectedComplaintForReport(e.target.value)}
                    className="w-full rounded-xl border border-slate-300 bg-white px-3.5 py-2.5 text-sm font-semibold text-slate-800 outline-none focus:border-slate-800"
                  >
                    <option value="">-- Choose a Complaint --</option>
                    {regularComplaints.map((c) => (
                      <option key={c._id || c.complaintId} value={c.complaintId || c._id}>
                        #{c.complaintId} – {c.company}
                      </option>
                    ))}
                  </select>
                </div>

                {complaintReportDetails ? (
                  <div className="space-y-5">
                    <div className="p-4 rounded-2xl bg-amber-50/60 border border-amber-200 flex flex-wrap items-center justify-between gap-4 text-xs">
                      <div>
                        <span className="font-mono text-amber-800 font-bold">
                          #{complaintReportDetails.complaint?.complaintId}
                        </span>
                        <h3 className="text-base font-black text-slate-900">
                          {complaintReportDetails.complaint?.company}
                        </h3>
                        <p className="text-slate-500">Issue: {complaintReportDetails.complaint?.issue}</p>
                      </div>
                      <div>
                        <span className="text-slate-500 uppercase block font-bold text-[10px]">Total Complaint Expense</span>
                        <span className="text-2xl font-black text-amber-900">
                          {formatCurrency(complaintReportDetails.totalExpense)}
                        </span>
                      </div>
                    </div>

                    <div className="p-4 rounded-2xl border border-slate-200 bg-slate-50">
                      <h4 className="text-xs font-bold text-slate-800 uppercase mb-2">Category Breakdown</h4>
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
                        {Object.entries(complaintReportDetails.categoryBreakdown).map(([cat, amt]) => (
                          <div key={cat} className="p-2.5 rounded-xl bg-white border border-slate-200">
                            <span className="text-slate-500 block text-[11px] font-semibold">{cat}</span>
                            <span className="text-sm font-black text-slate-900">{formatCurrency(amt)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                ) : (
                  <p className="text-xs text-slate-400 italic">Select a complaint above to inspect service expense.</p>
                )}
              </div>
            )}

            {/* 4b. ABB WARRANTY COMPLAINT REPORT */}
            {reportSubTab === "abb_warranty" && (
              <div className="space-y-5 animate-fade-in">
                <div className="max-w-md">
                  <label className="block text-xs font-bold uppercase tracking-wider text-teal-800 mb-1.5">
                    Select ABB Warranty Complaint
                  </label>
                  <select
                    value={selectedAbbWarrantyForReport}
                    onChange={(e) => setSelectedAbbWarrantyForReport(e.target.value)}
                    className="w-full rounded-xl border border-teal-300 bg-white px-3.5 py-2.5 text-sm font-semibold text-slate-800 outline-none focus:border-teal-600"
                  >
                    <option value="">-- Choose an ABB Warranty Complaint --</option>
                    {abbWarrantyComplaints.map((c) => (
                      <option key={c._id || c.complaintId} value={c.complaintId || c._id}>
                        #{c.complaintId} – {c.company}
                      </option>
                    ))}
                  </select>
                </div>

                {abbWarrantyReportDetails ? (
                  <div className="space-y-5">
                    <div className="p-4 rounded-2xl bg-teal-50/60 border border-teal-200 flex flex-wrap items-center justify-between gap-4 text-xs">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-mono text-teal-800 font-bold">
                            #{abbWarrantyReportDetails.complaint?.complaintId}
                          </span>
                          <span className="px-2 py-0.5 rounded-md bg-teal-100 text-teal-800 font-bold text-[10px] uppercase">
                            ABB Warranty
                          </span>
                        </div>
                        <h3 className="text-base font-black text-slate-900">
                          {abbWarrantyReportDetails.complaint?.company}
                        </h3>
                        <p className="text-slate-500">Issue: {abbWarrantyReportDetails.complaint?.issue}</p>
                      </div>
                      <div>
                        <span className="text-slate-500 uppercase block font-bold text-[10px]">Total ABB Warranty Expense</span>
                        <span className="text-2xl font-black text-teal-900">
                          {formatCurrency(abbWarrantyReportDetails.totalExpense)}
                        </span>
                      </div>
                    </div>

                    <div className="p-4 rounded-2xl border border-slate-200 bg-slate-50">
                      <h4 className="text-xs font-bold text-teal-900 uppercase mb-2">Category Breakdown</h4>
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
                        {Object.entries(abbWarrantyReportDetails.categoryBreakdown).map(([cat, amt]) => (
                          <div key={cat} className="p-2.5 rounded-xl bg-white border border-teal-100 shadow-xs">
                            <span className="text-slate-500 block text-[11px] font-semibold">{cat}</span>
                            <span className="text-sm font-black text-slate-900">{formatCurrency(amt)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                ) : (
                  <p className="text-xs text-slate-400 italic">Select an ABB warranty complaint above to inspect service expense.</p>
                )}
              </div>
            )}

            {/* 5. GENERAL EXPENSE REPORT (Section 14) */}
            {reportSubTab === "general" && (
              <div className="space-y-5 animate-fade-in">
                {/* Filters (Engineer/Accounts, Date, Category) */}
                <div className="p-4 rounded-2xl bg-slate-50 border border-slate-200 grid grid-cols-1 sm:grid-cols-4 gap-3 text-xs">
                  <div>
                    <label className="block text-[11px] font-bold text-slate-600 uppercase mb-1">User / Role</label>
                    <select
                      value={generalReportFilter.user}
                      onChange={(e) => setGeneralReportFilter((prev) => ({ ...prev, user: e.target.value }))}
                      className="w-full rounded-xl border border-slate-300 bg-white p-2 text-slate-800 outline-none"
                    >
                      <option value="">All Users</option>
                      <optgroup label="Engineers">
                        {engineers.map((eng) => (
                          <option key={eng._id || eng.engineerId} value={eng.name}>
                            {eng.name}
                          </option>
                        ))}
                      </optgroup>
                      <optgroup label="Accounts">
                        {accountsUsers.map((acc) => (
                          <option key={acc._id} value={acc.name}>
                            {acc.name}
                          </option>
                        ))}
                      </optgroup>
                    </select>
                  </div>

                  <div>
                    <label className="block text-[11px] font-bold text-slate-600 uppercase mb-1">Category</label>
                    <select
                      value={generalReportFilter.category}
                      onChange={(e) => setGeneralReportFilter((prev) => ({ ...prev, category: e.target.value }))}
                      className="w-full rounded-xl border border-slate-300 bg-white p-2 text-slate-800 outline-none"
                    >
                      <option value="">All Categories</option>
                      {GENERAL_CATEGORIES.map((cat) => (
                        <option key={cat} value={cat}>
                          {cat}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-[11px] font-bold text-slate-600 uppercase mb-1">From Date</label>
                    <input
                      type="date"
                      value={generalReportFilter.from}
                      onChange={(e) => setGeneralReportFilter((prev) => ({ ...prev, from: e.target.value }))}
                      className="w-full rounded-xl border border-slate-300 bg-white p-1.5 text-slate-800 outline-none"
                    />
                  </div>

                  <div>
                    <label className="block text-[11px] font-bold text-slate-600 uppercase mb-1">To Date</label>
                    <input
                      type="date"
                      value={generalReportFilter.to}
                      onChange={(e) => setGeneralReportFilter((prev) => ({ ...prev, to: e.target.value }))}
                      className="w-full rounded-xl border border-slate-300 bg-white p-1.5 text-slate-800 outline-none"
                    />
                  </div>
                </div>

                {(() => {
                  const filteredGeneral = vouchers.filter((v) => {
                    if (v.isCancelled) return false;
                    if (v.transactionType !== "DEBIT" && v.transactionType !== "EXPENSE") return false;
                    if (v.referenceType === "project" || v.referenceType === "complaint" || v.referenceType === "abb_warranty") return false;

                    if (generalReportFilter.category && v.expenseCategory !== generalReportFilter.category) return false;
                    if (generalReportFilter.user) {
                      const u = generalReportFilter.user.toLowerCase();
                      const matchUser =
                        v.createdBy?.userName?.toLowerCase().includes(u) ||
                        v.fromUser?.userName?.toLowerCase().includes(u);
                      if (!matchUser) return false;
                    }
                    const vDate = v.date ? v.date.slice(0, 10) : "";
                    if (generalReportFilter.from && vDate < generalReportFilter.from) return false;
                    if (generalReportFilter.to && vDate > generalReportFilter.to) return false;
                    return true;
                  });

                  const totalFilteredGeneral = filteredGeneral.reduce((s, x) => s + Number(x.amount || 0), 0);

                  return (
                    <div className="space-y-4">
                      <div className="p-4 rounded-2xl bg-rose-50/60 border border-rose-200 flex items-center justify-between">
                        <div>
                          <span className="text-[10px] uppercase font-bold text-rose-800">Total Filtered General Expense</span>
                          <h3 className="text-2xl font-black text-rose-900">
                            {formatCurrency(totalFilteredGeneral)}
                          </h3>
                        </div>
                        <span className="text-xs font-bold text-slate-500">{filteredGeneral.length} records</span>
                      </div>

                      {/* Category breakdown grid */}
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
                        {GENERAL_CATEGORIES.map((cat) => {
                          const totalForCat = filteredGeneral
                            .filter((v) => v.expenseCategory === cat)
                            .reduce((s, x) => s + Number(x.amount || 0), 0);

                          return (
                            <div key={cat} className="p-3.5 rounded-2xl border border-slate-200 bg-slate-50">
                              <span className="text-slate-500 block font-bold text-[11px] uppercase truncate">{cat}</span>
                              <span className="text-lg font-black text-slate-900 mt-1 block">{formatCurrency(totalForCat)}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })()}
              </div>
            )}
          </div>
        )}

      </div>

      {/* ============================================================ */}
      {/* MODAL 1: VIEW VOUCHER DETAILS (Section 18)                   */}
      {/* ============================================================ */}
      {viewVoucher && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-xs p-4 overflow-y-auto">
          <div className="bg-white rounded-3xl p-6 sm:p-8 max-w-lg w-full shadow-2xl border border-slate-100 my-auto animate-scale-up space-y-5">
            <div className="flex items-center justify-between border-b border-slate-100 pb-4">
              <div>
                <span
                  className={`inline-block px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider mb-1 ${
                    viewVoucher.transactionType === "CREDIT" || viewVoucher.transactionType === "ADVANCE"
                      ? "bg-emerald-100 text-emerald-800"
                      : "bg-rose-100 text-rose-800"
                  }`}
                >
                  {viewVoucher.transactionType === "CREDIT" || viewVoucher.transactionType === "ADVANCE"
                    ? "CREDIT VOUCHER (RECEIVED)"
                    : "DEBIT VOUCHER (SPENT)"}
                </span>
                <h3 className="text-xl font-black text-slate-900 font-mono">
                  {viewVoucher.transactionId}
                </h3>
              </div>
              <button
                type="button"
                onClick={() => setViewVoucher(null)}
                className="p-2 text-slate-400 hover:text-slate-700 rounded-xl hover:bg-slate-100 font-bold"
              >
                ✕
              </button>
            </div>

            <div className="space-y-3 text-xs text-slate-700">
              <div className="flex justify-between py-1.5 border-b border-slate-100">
                <span className="font-bold text-slate-500 uppercase">Amount</span>
                <span className="font-black text-lg text-slate-900">
                  {formatCurrency(viewVoucher.amount)}
                </span>
              </div>

              <div className="flex justify-between py-1.5 border-b border-slate-100">
                <span className="font-bold text-slate-500 uppercase">Date</span>
                <span className="font-semibold text-slate-800">{formatDateDisplay(viewVoucher.date)}</span>
              </div>

              {viewVoucher.transactionType === "CREDIT" ? (
                <div className="flex justify-between py-1.5 border-b border-slate-100">
                  <span className="font-bold text-slate-500 uppercase">Received From</span>
                  <span className="font-bold text-emerald-800">{viewVoucher.fromUser?.userName || "Admin"}</span>
                </div>
              ) : (
                <>
                  <div className="flex justify-between py-1.5 border-b border-slate-100">
                    <span className="font-bold text-slate-500 uppercase">Expense Category</span>
                    <span className="font-bold text-rose-800">{viewVoucher.expenseCategory || "General"}</span>
                  </div>

                  {viewVoucher.referenceType && viewVoucher.referenceType !== "general" && (
                    <div className="flex justify-between py-1.5 border-b border-slate-100">
                      <span className="font-bold text-slate-500 uppercase">Linked Reference</span>
                      <span className="font-bold text-blue-800">
                        {viewVoucher.referenceType === "abb_warranty"
                          ? `ABB WARRANTY: ${viewVoucher.referenceName || viewVoucher.customerName || viewVoucher.referenceId}`
                          : viewVoucher.referenceType === "complaint"
                          ? `COMPLAINT: ${getComplaintCompanyName(viewVoucher)}`
                          : `${viewVoucher.referenceType.toUpperCase()}: ${viewVoucher.referenceName || viewVoucher.referenceId}`}
                      </span>
                    </div>
                  )}
                </>
              )}

              <div className="flex justify-between py-1.5 border-b border-slate-100">
                <span className="font-bold text-slate-500 uppercase">Created By</span>
                <span className="font-semibold text-slate-800">
                  {viewVoucher.createdBy?.userName || viewVoucher.fromUser?.userName || "System"} ({viewVoucher.createdBy?.userRole || "User"})
                </span>
              </div>

              <div className="flex justify-between py-1.5 border-b border-slate-100">
                <span className="font-bold text-slate-500 uppercase">Created At</span>
                <span className="font-semibold text-slate-800">{formatDateTimeDisplay(viewVoucher.createdAt)}</span>
              </div>

              <div className="flex justify-between py-1.5 border-b border-slate-100 items-center">
                <span className="font-bold text-slate-500 uppercase">Status</span>
                {viewVoucher.isCancelled ? (
                  <span className="font-bold uppercase text-rose-600">Cancelled (Voided)</span>
                ) : canManageVoucherStatus(viewVoucher) ? (
                  <div className="flex items-center gap-1.5">
                    <button
                      type="button"
                      disabled={viewVoucher.status === "approved" || updatingStatusId === (viewVoucher._id || viewVoucher.transactionId)}
                      onClick={async () => {
                        await handleStatusUpdate(viewVoucher._id || viewVoucher.transactionId, "approved");
                        setViewVoucher((prev) => (prev ? { ...prev, status: "approved" } : null));
                      }}
                      className={`px-2.5 py-1 rounded-lg text-xs font-bold transition ${
                        viewVoucher.status === "approved"
                          ? "bg-emerald-600 text-white"
                          : "bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100"
                      }`}
                    >
                      Approve
                    </button>
                    <button
                      type="button"
                      disabled={viewVoucher.status === "pending_approval" || updatingStatusId === (viewVoucher._id || viewVoucher.transactionId)}
                      onClick={async () => {
                        await handleStatusUpdate(viewVoucher._id || viewVoucher.transactionId, "pending_approval");
                        setViewVoucher((prev) => (prev ? { ...prev, status: "pending_approval" } : null));
                      }}
                      className={`px-2.5 py-1 rounded-lg text-xs font-bold transition ${
                        viewVoucher.status === "pending_approval"
                          ? "bg-amber-500 text-white"
                          : "bg-amber-50 text-amber-700 border border-amber-200 hover:bg-amber-100"
                      }`}
                    >
                      Pending
                    </button>
                    <button
                      type="button"
                      disabled={viewVoucher.status === "rejected" || updatingStatusId === (viewVoucher._id || viewVoucher.transactionId)}
                      onClick={async () => {
                        await handleStatusUpdate(viewVoucher._id || viewVoucher.transactionId, "rejected");
                        setViewVoucher((prev) => (prev ? { ...prev, status: "rejected" } : null));
                      }}
                      className={`px-2.5 py-1 rounded-lg text-xs font-bold transition ${
                        viewVoucher.status === "rejected"
                          ? "bg-rose-600 text-white"
                          : "bg-rose-50 text-rose-700 border border-rose-200 hover:bg-rose-100"
                      }`}
                    >
                      Reject
                    </button>
                  </div>
                ) : (
                  <span className="font-bold uppercase text-slate-800">
                    <span
                      className={
                        viewVoucher.status === "approved"
                          ? "text-emerald-700"
                          : viewVoucher.status === "rejected"
                          ? "text-rose-700"
                          : "text-amber-700"
                      }
                    >
                      {viewVoucher.status === "pending_approval" ? "Pending Approval" : (viewVoucher.status || "Recorded")}
                    </span>
                  </span>
                )}
              </div>

              {viewVoucher.isCancelled && (
                <div className="p-3 rounded-xl bg-rose-50 border border-rose-200 text-rose-900">
                  <p className="font-bold">Cancellation Audit Trail:</p>
                  <p className="text-[11px] mt-0.5">Reason: {viewVoucher.cancellationReason || "No reason given"}</p>
                  <p className="text-[10px] text-rose-700 mt-1">
                    By: {viewVoucher.cancelledBy?.userName || "Admin"} on {formatDateTimeDisplay(viewVoucher.cancelledBy?.cancelledAt)}
                  </p>
                </div>
              )}

              {viewVoucher.description && (
                <div className="py-1">
                  <span className="font-bold text-slate-500 uppercase block mb-1">Description</span>
                  <p className="rounded-xl bg-slate-50 p-3 text-slate-700 leading-relaxed">
                    {viewVoucher.description}
                  </p>
                </div>
              )}

              {/* Attachments */}
              {Array.isArray(viewVoucher.attachments) && viewVoucher.attachments.length > 0 && (
                <div className="py-1">
                  <span className="font-bold text-slate-500 uppercase block mb-1.5">
                    Attachments ({viewVoucher.attachments.length})
                  </span>
                  <div className="space-y-2">
                    {viewVoucher.attachments.map((att, idx) => {
                      const fullUrl = att.fileUrl?.startsWith("http")
                        ? att.fileUrl
                        : `${API_BASE_URL}${att.fileUrl}`;
                      return (
                        <div
                          key={idx}
                          className="rounded-xl border border-slate-200 p-2.5 bg-slate-50 flex items-center justify-between gap-3"
                        >
                          <span className="truncate font-semibold text-slate-800">
                            {att.fileName || `Attachment ${idx + 1}`}
                          </span>
                          <a
                            href={fullUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="px-3 py-1 rounded-lg bg-blue-600 text-white font-bold hover:bg-blue-700 transition shrink-0"
                          >
                            Open
                          </a>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            <div className="pt-3 border-t border-slate-100 flex items-center justify-between gap-3">
              <button
                type="button"
                onClick={() => handleDownloadVoucherSlip(viewVoucher)}
                className="px-4 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold transition text-xs flex items-center gap-1.5 shadow-sm cursor-pointer"
                title="Download voucher slip PDF"
              >
                <span className="material-symbols-outlined text-base">picture_as_pdf</span>
                Download Voucher PDF
              </button>

              <button
                type="button"
                onClick={() => setViewVoucher(null)}
                className="px-5 py-2.5 rounded-xl border border-slate-200 font-bold text-slate-700 hover:bg-slate-100 transition text-xs uppercase"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ============================================================ */}
      {/* MODAL 2: EDIT VOUCHER                                        */}
      {/* ============================================================ */}
      {editVoucher && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-xs p-4 overflow-y-auto">
          <div className="bg-white rounded-3xl p-6 sm:p-8 max-w-lg w-full shadow-2xl border border-slate-100 my-auto animate-scale-up space-y-5">
            <div className="flex items-center justify-between border-b border-slate-100 pb-4">
              <div>
                <span className="inline-block px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider mb-1 bg-blue-100 text-blue-800">
                  Edit Voucher
                </span>
                <h3 className="text-base font-black text-slate-900">
                  {editVoucher.referenceType === "complaint"
                    ? `Complaint: ${getComplaintInfo(editVoucher).company}${getComplaintInfo(editVoucher).id ? ` (#${getComplaintInfo(editVoucher).id})` : ""}`
                    : editVoucher.referenceType === "project"
                    ? `Project: ${getProjectInfo(editVoucher).title}`
                    : "General Expense"}
                </h3>
              </div>
              <button
                type="button"
                onClick={() => setEditVoucher(null)}
                className="p-2 text-slate-400 hover:text-slate-700 rounded-xl hover:bg-slate-100 font-bold"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleEditSubmit} className="space-y-4 text-xs">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[11px] font-bold text-slate-700 uppercase mb-1">Date *</label>
                  <input
                    type="date"
                    required
                    value={editFormData.date}
                    onChange={(e) => setEditFormData((prev) => ({ ...prev, date: e.target.value }))}
                    className="w-full rounded-xl border border-slate-300 p-2.5 font-semibold text-slate-800 outline-none"
                  />
                </div>
                <div>
                  <label className="block text-[11px] font-bold text-slate-700 uppercase mb-1">Amount (₹) *</label>
                  <input
                    type="number"
                    step="any"
                    required
                    value={editFormData.amount}
                    onChange={(e) => setEditFormData((prev) => ({ ...prev, amount: e.target.value }))}
                    className="w-full rounded-xl border border-slate-300 p-2.5 font-black text-slate-900 outline-none"
                  />
                </div>
              </div>

              {editVoucher.transactionType === "CREDIT" ? (
                <div>
                  <label className="block text-[11px] font-bold text-emerald-900 uppercase mb-1">Received From *</label>
                  <input
                    type="text"
                    required
                    value={editFormData.receivedFrom}
                    onChange={(e) => setEditFormData((prev) => ({ ...prev, receivedFrom: e.target.value }))}
                    className="w-full rounded-xl border border-emerald-300 p-2.5 font-semibold text-slate-800 outline-none"
                  />
                </div>
              ) : (
                <div>
                  <label className="block text-[11px] font-bold text-rose-900 uppercase mb-1">Expense Category *</label>
                  <input
                    type="text"
                    required
                    value={editFormData.expenseCategory}
                    onChange={(e) => setEditFormData((prev) => ({ ...prev, expenseCategory: e.target.value }))}
                    className="w-full rounded-xl border border-rose-300 p-2.5 font-semibold text-slate-800 outline-none"
                  />
                </div>
              )}

              {canManageVoucherStatus(editVoucher) && (
                <div>
                  <label className="block text-[11px] font-bold text-slate-700 uppercase mb-1">
                    Approval Status (Admin / Accounts for Engineers)
                  </label>
                  <select
                    value={editFormData.status === "pending" ? "pending_approval" : (editFormData.status || "pending_approval")}
                    onChange={(e) => setEditFormData((prev) => ({ ...prev, status: e.target.value }))}
                    className="w-full rounded-xl border border-slate-300 p-2.5 font-bold text-slate-800 outline-none bg-white"
                  >
                    <option value="pending_approval">Pending Approval</option>
                    <option value="approved">Approved</option>
                    <option value="rejected">Rejected</option>
                  </select>
                </div>
              )}

              <div>
                <label className="block text-[11px] font-bold text-slate-700 uppercase mb-1">Description / Remarks</label>
                <textarea
                  rows={3}
                  value={editFormData.description}
                  onChange={(e) => setEditFormData((prev) => ({ ...prev, description: e.target.value }))}
                  className="w-full rounded-xl border border-slate-300 p-2.5 text-slate-800 outline-none resize-none"
                />
              </div>

              <div>
                <label className="block text-[11px] font-bold text-slate-700 uppercase mb-1">Add Attachment (Optional)</label>
                <input
                  type="file"
                  accept="image/*,.pdf"
                  onChange={(e) => setEditFormData((prev) => ({ ...prev, attachment: e.target.files?.[0] || null }))}
                  className="block w-full text-xs text-slate-500 file:mr-3 file:py-2 file:px-3 file:rounded-xl file:border-0 file:text-xs file:font-bold file:bg-slate-100 file:text-slate-700 cursor-pointer"
                />
              </div>

              <div className="pt-3 border-t border-slate-100 flex items-center justify-end gap-2.5">
                <button
                  type="button"
                  onClick={() => setEditVoucher(null)}
                  className="px-4 py-2 rounded-xl border border-slate-200 font-bold text-slate-700 hover:bg-slate-100 transition"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={editSubmitting}
                  className="px-5 py-2 rounded-xl bg-slate-900 hover:bg-slate-800 text-white font-black uppercase tracking-wider transition disabled:opacity-60"
                >
                  {editSubmitting ? "Saving..." : "Save Changes"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ============================================================ */}
      {/* MODAL 3: VOID / CANCEL VOUCHER (Section 19)                  */}
      {/* ============================================================ */}
      {voidVoucher && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-xs p-4 overflow-y-auto">
          <div className="bg-white rounded-3xl p-6 sm:p-8 max-w-md w-full shadow-2xl border border-slate-100 my-auto animate-scale-up space-y-4">
            <div className="flex items-center gap-3 border-b border-slate-100 pb-3">
              <span className="material-symbols-outlined text-rose-600 text-3xl">cancel</span>
              <div>
                <h3 className="text-lg font-black text-slate-900">Cancel & Void Voucher</h3>
                <p className="text-xs text-slate-700 font-bold">
                  {voidVoucher.referenceType === "abb_warranty"
                    ? `ABB Warranty: ${voidVoucher.referenceName || voidVoucher.customerName || "Complaint"} (#${voidVoucher.referenceId})`
                    : voidVoucher.referenceType === "complaint"
                    ? `${getComplaintInfo(voidVoucher).company}${getComplaintInfo(voidVoucher).id ? ` (#${getComplaintInfo(voidVoucher).id})` : ""}`
                    : voidVoucher.referenceType === "project"
                    ? `${getProjectInfo(voidVoucher).title}`
                    : `${voidVoucher.expenseCategory || "General Expense"}`}
                </p>
              </div>
            </div>

            <p className="text-xs text-slate-600 leading-relaxed">
              Financial records are non-destructively cancelled. An audit trail of who cancelled this voucher, timestamp, and reason will be permanently preserved.
            </p>

            <form onSubmit={handleVoidSubmit} className="space-y-4 text-xs">
              <div>
                <label className="block text-[11px] font-bold text-slate-700 uppercase mb-1">
                  Cancellation Reason <span className="text-rose-500">*</span>
                </label>
                <textarea
                  rows={3}
                  required
                  placeholder="Explain why this transaction is being voided/cancelled..."
                  value={voidReason}
                  onChange={(e) => setVoidReason(e.target.value)}
                  className="w-full rounded-xl border border-rose-300 p-2.5 text-slate-800 outline-none focus:border-rose-600 resize-none"
                />
              </div>

              <div className="pt-2 flex items-center justify-end gap-2.5">
                <button
                  type="button"
                  onClick={() => setVoidVoucher(null)}
                  className="px-4 py-2 rounded-xl border border-slate-200 font-bold text-slate-700 hover:bg-slate-100 transition"
                >
                  Close
                </button>
                <button
                  type="submit"
                  disabled={voidSubmitting}
                  className="px-5 py-2 rounded-xl bg-rose-600 hover:bg-rose-700 text-white font-black uppercase tracking-wider transition disabled:opacity-60"
                >
                  {voidSubmitting ? "Cancelling..." : "Confirm Void"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ============================================================ */}
      {/* MODAL 4: CREATE NEW PROJECT (Section 6)                      */}
      {/* ============================================================ */}
      {showProjectModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-xs p-4 overflow-y-auto">
          <div className="bg-white rounded-3xl p-6 sm:p-8 max-w-lg w-full shadow-2xl border border-slate-100 my-auto animate-scale-up space-y-5">
            <div className="flex items-center justify-between border-b border-slate-100 pb-4">
              <div>
                <span className="inline-block px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider mb-1 bg-blue-100 text-blue-800">
                  Project Module
                </span>
                <h3 className="text-xl font-black text-slate-900">
                  Create New Project
                </h3>
              </div>
              <button
                type="button"
                onClick={() => setShowProjectModal(false)}
                className="p-2 text-slate-400 hover:text-slate-700 rounded-xl hover:bg-slate-100 font-bold"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleQuickCreateProject} className="space-y-4 text-xs">
              <div>
                <label className="block text-[11px] font-bold text-slate-700 uppercase mb-1">
                  Project Name <span className="text-rose-500">*</span>
                </label>
                <input
                  type="text"
                  required
                  placeholder="e.g. Substation Panel Upgrade - Line 3"
                  value={newProjectData.projectName}
                  onChange={(e) => setNewProjectData((prev) => ({ ...prev, projectName: e.target.value }))}
                  className="w-full rounded-xl border border-slate-300 p-2.5 font-semibold text-slate-800 outline-none focus:border-slate-800"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[11px] font-bold text-slate-700 uppercase mb-1">Customer Name</label>
                  <input
                    type="text"
                    placeholder="e.g. Tata Steel"
                    value={newProjectData.customerName}
                    onChange={(e) => setNewProjectData((prev) => ({ ...prev, customerName: e.target.value }))}
                    className="w-full rounded-xl border border-slate-300 p-2.5 text-slate-800 outline-none"
                  />
                </div>
                <div>
                  <label className="block text-[11px] font-bold text-slate-700 uppercase mb-1">Location</label>
                  <input
                    type="text"
                    placeholder="e.g. Jamshedpur Plant"
                    value={newProjectData.location}
                    onChange={(e) => setNewProjectData((prev) => ({ ...prev, location: e.target.value }))}
                    className="w-full rounded-xl border border-slate-300 p-2.5 text-slate-800 outline-none"
                  />
                </div>
              </div>

              <div>
                <label className="block text-[11px] font-bold text-slate-700 uppercase mb-1">Notes / Scope</label>
                <textarea
                  rows={2}
                  placeholder="Brief description of project requirements..."
                  value={newProjectData.notes}
                  onChange={(e) => setNewProjectData((prev) => ({ ...prev, notes: e.target.value }))}
                  className="w-full rounded-xl border border-slate-300 p-2.5 text-slate-800 outline-none resize-none"
                />
              </div>

              <div className="pt-3 border-t border-slate-100 flex items-center justify-end gap-2.5">
                <button
                  type="button"
                  onClick={() => setShowProjectModal(false)}
                  className="px-4 py-2 rounded-xl border border-slate-200 font-bold text-slate-700 hover:bg-slate-100 transition"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={projectSubmitting}
                  className="px-5 py-2 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-black uppercase tracking-wider transition disabled:opacity-60"
                >
                  {projectSubmitting ? "Creating..." : "Create Project"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ============================================================ */}
      {/* MODAL 4B: CREATE ABB WARRANTY COMPLAINT                      */}
      {/* ============================================================ */}
      {showWarrantyModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-xs p-4 overflow-y-auto">
          <div className="bg-white rounded-3xl p-6 sm:p-8 max-w-lg w-full shadow-2xl border border-slate-100 my-auto animate-scale-up space-y-5">
            <div className="flex items-center justify-between border-b border-slate-100 pb-4">
              <div>
                <span className="inline-block px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider mb-1 bg-teal-100 text-teal-800">
                  ABB Warranty Module
                </span>
                <h3 className="text-xl font-black text-slate-900">
                  Create ABB Warranty Complaint
                </h3>
              </div>
              <button
                type="button"
                onClick={() => setShowWarrantyModal(false)}
                className="p-2 text-slate-400 hover:text-slate-700 rounded-xl hover:bg-slate-100 font-bold cursor-pointer"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleQuickCreateWarrantyComplaint} className="space-y-4 text-xs">
              <div>
                <label className="block text-[11px] font-bold text-slate-700 uppercase mb-1">
                  Customer Name <span className="text-rose-500">*</span>
                </label>
                <input
                  type="text"
                  required
                  placeholder="e.g. Tata Steel / Jindal Power"
                  value={newWarrantyData.customerName}
                  onChange={(e) => setNewWarrantyData((prev) => ({ ...prev, customerName: e.target.value }))}
                  className="w-full rounded-xl border border-slate-300 p-2.5 font-semibold text-slate-800 outline-none focus:border-teal-600"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[11px] font-bold text-slate-700 uppercase mb-1">
                    Place / Location <span className="text-rose-500">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    placeholder="e.g. Pune Site / Jamshedpur"
                    value={newWarrantyData.place}
                    onChange={(e) => setNewWarrantyData((prev) => ({ ...prev, place: e.target.value }))}
                    className="w-full rounded-xl border border-slate-300 p-2.5 text-slate-800 outline-none focus:border-teal-600"
                  />
                </div>
                <div>
                  <label className="block text-[11px] font-bold text-slate-700 uppercase mb-1">Visit Date</label>
                  <input
                    type="date"
                    value={newWarrantyData.visitDate}
                    onChange={(e) => setNewWarrantyData((prev) => ({ ...prev, visitDate: e.target.value }))}
                    className="w-full rounded-xl border border-slate-300 p-2.5 text-slate-800 outline-none focus:border-teal-600"
                  />
                </div>
              </div>

              <div>
                <label className="block text-[11px] font-bold text-slate-700 uppercase mb-1">
                  Complaint Details / Issue <span className="text-rose-500">*</span>
                </label>
                <textarea
                  rows={3}
                  required
                  placeholder="Describe the complaint details, issue, or scope of warranty work..."
                  value={newWarrantyData.details}
                  onChange={(e) => setNewWarrantyData((prev) => ({ ...prev, details: e.target.value }))}
                  className="w-full rounded-xl border border-slate-300 p-2.5 text-slate-800 outline-none resize-none focus:border-teal-600"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[11px] font-bold text-slate-700 uppercase mb-1">Contact Person (Optional)</label>
                  <input
                    type="text"
                    placeholder="e.g. Mr. Sharma"
                    value={newWarrantyData.contactPerson}
                    onChange={(e) => setNewWarrantyData((prev) => ({ ...prev, contactPerson: e.target.value }))}
                    className="w-full rounded-xl border border-slate-300 p-2.5 text-slate-800 outline-none focus:border-teal-600"
                  />
                </div>
                <div>
                  <label className="block text-[11px] font-bold text-slate-700 uppercase mb-1">Contact Phone (Optional)</label>
                  <input
                    type="tel"
                    placeholder="e.g. 9876543210"
                    value={newWarrantyData.contactPhone}
                    onChange={(e) => setNewWarrantyData((prev) => ({ ...prev, contactPhone: e.target.value }))}
                    className="w-full rounded-xl border border-slate-300 p-2.5 text-slate-800 outline-none focus:border-teal-600"
                  />
                </div>
              </div>

              <div className="pt-3 border-t border-slate-100 flex items-center justify-end gap-2.5">
                <button
                  type="button"
                  onClick={() => setShowWarrantyModal(false)}
                  className="px-4 py-2 rounded-xl border border-slate-200 font-bold text-slate-700 hover:bg-slate-100 transition cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={warrantySubmitting}
                  className="px-5 py-2 rounded-xl bg-teal-600 hover:bg-teal-700 text-white font-black uppercase tracking-wider transition disabled:opacity-60 cursor-pointer shadow-xs"
                >
                  {warrantySubmitting ? "Creating..." : "Create & Select Complaint"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ============================================================ */}
      {/* MODAL 5: PROJECT EXPENSE SUMMARY (Section 6)                 */}
      {/* ============================================================ */}
      {projectSummaryDetails && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-xs p-4 overflow-y-auto">
          <div className="bg-white rounded-3xl p-6 sm:p-8 max-w-2xl w-full shadow-2xl border border-slate-100 my-auto animate-scale-up space-y-5 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between border-b border-slate-100 pb-4">
              <div>
                <span className="inline-block px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider mb-1 bg-blue-100 text-blue-800">
                  PROJECT EXPENSE SUMMARY
                </span>
                <h3 className="text-xl font-black text-slate-900">
                  {projectSummaryDetails.project.projectName}
                </h3>
                <p className="text-xs text-slate-500 font-mono">
                  {projectSummaryDetails.project.estimateNo || "Project ID"}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setSelectedProjectForSummary(null)}
                className="p-2 text-slate-400 hover:text-slate-700 rounded-xl hover:bg-slate-100 font-bold"
              >
                ✕
              </button>
            </div>

            {/* Metrics */}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              <div className="p-3 rounded-2xl bg-blue-50 border border-blue-200">
                <span className="text-[10px] uppercase font-bold text-blue-800">Total Project Expense</span>
                <span className="text-xl font-black text-blue-950 mt-0.5 block">
                  {formatCurrency(projectSummaryDetails.totalSpent)}
                </span>
              </div>
              <div className="p-3 rounded-2xl bg-slate-100 border border-slate-200">
                <span className="text-[10px] uppercase font-bold text-slate-700">Number of Expenses</span>
                <span className="text-xl font-black text-slate-900 mt-0.5 block">
                  {projectSummaryDetails.count}
                </span>
              </div>
              <div className="p-3 rounded-2xl bg-slate-100 border border-slate-200">
                <span className="text-[10px] uppercase font-bold text-slate-700">Status</span>
                <span className="text-base font-black text-slate-900 mt-1 block uppercase">
                  {projectSummaryDetails.project.status || "active"}
                </span>
              </div>
            </div>

            {/* Category-wise Breakdown */}
            <div className="p-4 rounded-2xl border border-slate-200 bg-slate-50">
              <h4 className="text-xs font-bold text-slate-800 uppercase mb-2">Category-wise Expenses</h4>
              {Object.keys(projectSummaryDetails.categoryBreakdown).length === 0 ? (
                <p className="text-xs text-slate-400 italic">No expenses recorded yet.</p>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5 text-xs">
                  {Object.entries(projectSummaryDetails.categoryBreakdown).map(([cat, amt]) => (
                    <div key={cat} className="p-2 rounded-xl bg-white border border-slate-200">
                      <span className="text-slate-500 block text-[11px] truncate">{cat}</span>
                      <span className="text-sm font-black text-slate-900">{formatCurrency(amt)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Date-wise Expenses */}
            <div className="p-4 rounded-2xl border border-slate-200 bg-slate-50">
              <h4 className="text-xs font-bold text-slate-800 uppercase mb-2">Date-wise Expenses</h4>
              {Object.keys(projectSummaryDetails.dateBreakdown).length === 0 ? (
                <p className="text-xs text-slate-400 italic">No date records.</p>
              ) : (
                <div className="space-y-1 text-xs">
                  {Object.entries(projectSummaryDetails.dateBreakdown).map(([dt, amt]) => (
                    <div key={dt} className="flex justify-between py-1 border-b border-slate-200/60 last:border-0">
                      <span className="text-slate-600 font-semibold">{formatDateDisplay(dt)}</span>
                      <span className="font-bold text-slate-900">{formatCurrency(amt)}</span>
                    </div>
                  ))}
                </div>
              )}

      {/* ============================================================ */}
      {/* MODAL 6: EXPORT VOUCHER PDF STATEMENT & REPORT               */}
      {/* ============================================================ */}
      {showExportModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-xs p-4 overflow-y-auto animate-fade-in">
          <div className="bg-white rounded-3xl p-6 sm:p-8 max-w-lg w-full shadow-2xl border border-slate-100 my-auto animate-scale-up space-y-5">
            <div className="flex items-center justify-between border-b border-slate-100 pb-4">
              <div className="flex items-center gap-2.5">
                <div className="w-10 h-10 rounded-2xl bg-emerald-50 text-emerald-700 border border-emerald-200 flex items-center justify-center">
                  <span className="material-symbols-outlined text-2xl">picture_as_pdf</span>
                </div>
                <div>
                  <h3 className="text-lg font-black text-slate-900">Export Voucher PDF</h3>
                  <p className="text-xs text-slate-500">Generate branded official statement or audit report</p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setShowExportModal(false)}
                className="p-2 text-slate-400 hover:text-slate-700 rounded-xl hover:bg-slate-100 font-bold"
              >
                ✕
              </button>
            </div>

            <div className="space-y-4 text-xs font-semibold text-slate-700">
              {/* Scope Selection */}
              <div>
                <label className="block text-[11px] font-black uppercase text-slate-500 mb-1.5">
                  Export Scope
                </label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => setExportFilterState((prev) => ({ ...prev, scope: "all" }))}
                    className={`p-3 rounded-2xl border text-left transition ${
                      exportFilterState.scope === "all"
                        ? "bg-slate-900 text-white border-slate-900 shadow-sm"
                        : "bg-slate-50 text-slate-700 border-slate-200 hover:bg-slate-100"
                    }`}
                  >
                    <div className="font-black text-xs">All Filtered Records</div>
                    <div className={`text-[10px] mt-0.5 ${exportFilterState.scope === "all" ? "text-slate-300" : "text-slate-500"}`}>
                      {`${filteredVouchers.length} records matching current view`}
                    </div>
                  </button>

                  <button
                    type="button"
                    disabled={selectedVoucherIds.length === 0}
                    onClick={() => setExportFilterState((prev) => ({ ...prev, scope: "selected" }))}
                    className={`p-3 rounded-2xl border text-left transition ${
                      exportFilterState.scope === "selected"
                        ? "bg-emerald-600 text-white border-emerald-600 shadow-sm"
                        : selectedVoucherIds.length === 0
                        ? "bg-slate-50 text-slate-300 border-slate-100 opacity-50 cursor-not-allowed"
                        : "bg-slate-50 text-slate-700 border-slate-200 hover:bg-slate-100"
                    }`}
                  >
                    <div className="font-black text-xs">Selected Vouchers</div>
                    <div className={`text-[10px] mt-0.5 ${exportFilterState.scope === "selected" ? "text-emerald-100" : "text-slate-500"}`}>
                      {`${selectedVoucherIds.length} checked voucher${selectedVoucherIds.length === 1 ? '' : 's'}`}
                    </div>
                  </button>
                </div>
              </div>

              {exportFilterState.scope === "all" && (
                <>
                  {/* Status Filter */}
                  <div>
                    <label className="block text-[11px] font-black uppercase text-slate-500 mb-1">
                      Status Filter
                    </label>
                    <select
                      value={exportFilterState.status}
                      onChange={(e) => setExportFilterState((prev) => ({ ...prev, status: e.target.value }))}
                      className="w-full rounded-xl border border-slate-200 bg-slate-50 p-2.5 text-xs font-bold text-slate-800 outline-none focus:border-emerald-500"
                    >
                      <option value="all">ALL STATUSES (Pending, Approved, Settled)</option>
                      <option value="pending">ONLY PENDING APPROVAL</option>
                      <option value="approved">ONLY APPROVED / SETTLED</option>
                    </select>
                  </div>

                  {/* Staff / Engineer Filter */}
                  {userRole !== "engineer" && (
                    <div>
                      <label className="block text-[11px] font-black uppercase text-slate-500 mb-1">
                        Employee / Engineer Filter
                      </label>
                      <select
                        value={exportFilterState.engineer}
                        onChange={(e) => setExportFilterState((prev) => ({ ...prev, engineer: e.target.value }))}
                        className="w-full rounded-xl border border-slate-200 bg-slate-50 p-2.5 text-xs font-bold text-slate-800 outline-none focus:border-emerald-500 truncate"
                      >
                        <option value="ALL">-- All Engineers & Accounts Staff --</option>
                        {engineerOptions.map((e) => (
                          <option key={e.key} value={e.key}>
                            {e.name} (Engineer)
                          </option>
                        ))}
                        {accountOptions.map((a) => (
                          <option key={a.key} value={a.key}>
                            {a.name} (Accounts)
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  {/* Date Range */}
                  <div className="grid grid-cols-2 gap-2.5">
                    <div>
                      <label className="block text-[11px] font-black uppercase text-slate-500 mb-1">From Date</label>
                      <input
                        type="date"
                        value={exportFilterState.fromDate}
                        onChange={(e) => setExportFilterState((prev) => ({ ...prev, fromDate: e.target.value }))}
                        className="w-full rounded-xl border border-slate-200 bg-slate-50 p-2 text-xs font-bold text-slate-800 outline-none focus:border-emerald-500"
                      />
                    </div>
                    <div>
                      <label className="block text-[11px] font-black uppercase text-slate-500 mb-1">To Date</label>
                      <input
                        type="date"
                        value={exportFilterState.toDate}
                        onChange={(e) => setExportFilterState((prev) => ({ ...prev, toDate: e.target.value }))}
                        className="w-full rounded-xl border border-slate-200 bg-slate-50 p-2 text-xs font-bold text-slate-800 outline-none focus:border-emerald-500"
                      />
                    </div>
                  </div>
                </>
              )}
            </div>

            <div className="pt-3 border-t border-slate-100 flex items-center justify-between gap-3">
              <button
                type="button"
                onClick={() => setShowExportModal(false)}
                className="px-4 py-2.5 rounded-xl border border-slate-200 font-bold text-slate-600 hover:bg-slate-100 transition text-xs"
              >
                Cancel
              </button>

              <button
                type="button"
                onClick={() => handleExecuteCustomExport()}
                className="px-6 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-black text-xs uppercase tracking-wider flex items-center gap-2 shadow-md shadow-emerald-950/20 transition cursor-pointer"
              >
                <span className="material-symbols-outlined text-base">download</span>
                Download PDF Statement
              </button>
            </div>
          </div>
        </div>
      )}

            </div>

            {/* Complete Expense History */}
            <div>
              <h4 className="text-xs font-bold text-slate-800 uppercase mb-2">Complete Expense History</h4>
              <div className="max-h-48 overflow-y-auto rounded-xl border border-slate-200 text-xs">
                <table className="w-full text-left border-collapse">
                  <thead className="bg-slate-100 text-[10px] uppercase font-bold text-slate-600 sticky top-0">
                    <tr>
                      <th className="p-2">Date</th>
                      <th className="p-2">Category</th>
                      <th className="p-2">By</th>
                      <th className="p-2 text-right">Amount</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {projectSummaryDetails.expenses.map((e) => (
                      <tr key={e._id || e.transactionId}>
                        <td className="p-2 whitespace-nowrap">{formatDateDisplay(e.date)}</td>
                        <td className="p-2 font-semibold">{e.expenseCategory}</td>
                        <td className="p-2 text-slate-500">{e.createdBy?.userName || "-"}</td>
                        <td className="p-2 text-right font-black text-rose-700">{formatCurrency(e.amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="pt-3 border-t border-slate-100 flex justify-end">
              <button
                type="button"
                onClick={() => setSelectedProjectForSummary(null)}
                className="px-5 py-2.5 rounded-xl border border-slate-200 font-bold text-slate-700 hover:bg-slate-100 transition text-xs uppercase"
              >
                Close Summary
              </button>
            </div>
          </div>
        </div>
      )}

      {/* FLOATING BULK APPROVAL ACTION BAR (Admin & Accounts) */}
      {canManageStatus && selectedVoucherIds.length > 0 && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 w-[95%] max-w-xl bg-slate-950/95 backdrop-blur-md text-white rounded-3xl p-3.5 sm:p-4 shadow-2xl border border-slate-700/80 flex flex-col sm:flex-row items-center justify-between gap-3 animate-fade-in ring-1 ring-white/10">
          <div className="flex items-center gap-3 w-full sm:w-auto">
            <div className="h-10 w-10 rounded-2xl bg-emerald-500/20 text-emerald-400 flex items-center justify-center shrink-0 border border-emerald-500/30">
              <span className="material-symbols-outlined text-xl">checklist</span>
            </div>
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-black text-sm text-white">
                  {selectedVoucherIds.length} Voucher{selectedVoucherIds.length === 1 ? '' : 's'} Selected
                </span>
                <span className="text-xs text-slate-500">•</span>
                <span className="font-extrabold text-sm text-emerald-400">
                  {formatCurrency(selectedTotalAmount)}
                </span>
              </div>
              <p className="text-[11px] text-slate-400">
                Ready for administrative audit approval
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 w-full sm:w-auto justify-end">
            <button
              type="button"
              onClick={handleClearSelection}
              className="px-3 py-2 rounded-xl text-xs font-bold text-slate-400 hover:text-white hover:bg-slate-800 transition cursor-pointer"
            >
              Clear
            </button>
            <button
              type="button"
              onClick={() => handleBulkApprove()}
              disabled={bulkApproving}
              className="px-5 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-black uppercase tracking-wider shadow-lg shadow-emerald-950/50 flex items-center gap-2 transition disabled:opacity-50 cursor-pointer"
            >
              {bulkApproving ? (
                <>
                  <span className="h-4 w-4 rounded-full border-2 border-white border-t-transparent animate-spin" />
                  Approving...
                </>
              ) : (
                <>
                  <span className="material-symbols-outlined text-base font-bold">done_all</span>
                  Approve Selected ({selectedVoucherIds.length})
                </>
              )}
            </button>

            <button
              type="button"
              onClick={handleDownloadSelectedPdf}
              className="px-4 py-2.5 rounded-xl bg-slate-800 hover:bg-slate-700 border border-slate-700 text-emerald-400 text-xs font-black uppercase tracking-wider flex items-center gap-1.5 transition cursor-pointer shadow-md"
              title="Download PDF report for all selected vouchers"
            >
              <span className="material-symbols-outlined text-base text-emerald-400">picture_as_pdf</span>
              Download PDF ({selectedVoucherIds.length})
            </button>
          </div>
        </div>
      )}

    </div>
  );
}
